import React, { useState } from 'react';
import { User, LogOut, Heart, Smile, AlertCircle, X, HelpCircle, ArrowLeft, ArrowRight, Save, CheckCircle, RefreshCw } from 'lucide-react';
import Hast2Screening from './Hast2Screening';
import NumeracyScreening from './NumeracyScreening';
import ConfidenceScreening from './ConfidenceScreening';
import WritingScreening from './WritingScreening';
import LearningStyleScreening from './LearningStyleScreening';
import SDQScreening from './SDQScreening';
import NeurodiversityScreening from './NeurodiversityScreening';
import MALSScreening from './MALSScreening';
import WRAT4Screening from './WRAT4Screening';
import TOWRE2Screening from './TOWRE2Screening';
import LearningPlansScreen from './LearningPlansScreen';

const API_URL = 'https://neurowise-screening.onrender.com';
const OWNER_ROLES = new Set(['CEO', 'SYSTEM OWNER / ADMINISTRATOR', 'SYSTEM OWNER', 'SL1']);
const isOwnerRole = (role) => OWNER_ROLES.has(String(role || '').trim().toUpperCase());

// Brand colors
const COLORS = {
  navyBlue: '#003366',
  orange: '#FF6B35',
  mustard: '#FFAA00',
  gold: '#F4C430',
  green: '#16A34A',
  lightGray: '#F9FAFB',
  white: '#FFFFFF',
  borderGray: '#D1D5DB',
  textGray: '#6B7280',
  textDark: '#1F2937'
};

// Error categorization options
const READING_ERROR_TYPES_ENGLISH = [
  'Substitution',
  'Insertion',
  'Omission',
  'Reversal',
  'Refusal',
  'Mispronunciation',
  'Phonetic attempt',
  'Sight word difficulty',
  'Hesitation*',
  'Repetition*',
  'Self-correction*',
  'Accent/dialect*',
  'Other (describe in Additional notes)'
];

const READING_ERROR_TYPES_KANNADA = [
  'Substitution (ಬದಲಾವಣೆ)',
  'Insertion (ಸೇರಿಸುವಿಕೆ)',
  'Omission (ಬಿಟ್ಟುಬಿಡುವಿಕೆ)',
  'Matra confusion (ಮಾತ್ರಾ ತಪ್ಪು)',
  'Gunintham error (ಗುಣಿತಾಂಕ ತಪ್ಪು)',
  'Vattakshara confusion (ಒತ್ತಕ್ಷರ ತಪ್ಪು)',
  'Phonetic attempt (ಧ್ವನಿಯ ಪ್ರಯತ್ನ)',
  'Sight word difficulty (ಸೈಟ್ ವರ್ಡ್ ಕಷ್ಟ)',
  'Hesitation* (ಹಿಂಜರಿಕೆ)',
  'Repetition* (ಪುನರಾವರ್ತನೆ)',
  'Self-correction* (ಸ್ವಯಂ ಸರಿಪಡಿಸುವಿಕೆ)'
];

const READING_FLUENCY_OPTIONS = ['Smooth', 'Choppy', 'Struggling'];

const SPELLING_ERROR_TYPES_ENGLISH = [
  'Phonetic approximation',
  'Silent letter omitted',
  'Vowel digraph error',
  'Consonant blend error',
  'Double letter error',
  'Common/sight word error',
  'Homophones confusion',
  'Letter reversal',
  'Suffix/prefix error',
  'Irregular pattern error',
  'Visual error'
];

const SPELLING_ERROR_TYPES_KANNADA = [
  'Matra error (ಮಾತ್ರಾ ತಪ್ಪು)',
  'Gunintham error (ಗುಣಿತಾಂಕ ತಪ್ಪು)',
  'Anusvara/Visarga error (ಅನುಸ್ವಾರ/ವಿಸರ್ಗ)',
  'Vattakshara error (ಒತ್ತಕ್ಷರ ತಪ್ಪು)',
  'Akshara confusion (ಅಕ್ಷರ ಗೊಂದಲ)',
  'Phonetic approximation (ಧ್ವನಿ ಆಧಾರಿತ)',
  'Matra length confusion (ಮಾತ್ರಾ ಉದ್ದ ತಪ್ಪು)',
  'Halant omission (ಹಲಂತ ಬಿಟ್ಟುಬಿಡುವಿಕೆ)',
  'Letter reversal (ಅಕ್ಷರ ಹಿಮ್ಮುಖ)',
  'Archaic form used (ಹಳೆಯ ರೂಪ)',
  'Visual error'
];

// Display helpers: treat 0 as a valid value (do not show N/A for zero)
const isEmptyValue = (v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
const displayValue = (v) => (isEmptyValue(v) ? 'N/A' : v);
const displayPercent = (v) => (isEmptyValue(v) ? 'N/A' : `${v}%`);

// ---------------------------------------------------------------------------
// LOADING OVERLAY - shown full-screen whenever the app is saving or loading
// data. Prevents facilitators from clicking buttons or re-submitting while
// waiting for a response from the server.
// ---------------------------------------------------------------------------
const LoadingOverlay = ({ message }) => (
  <div style={{
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 51, 102, 0.55)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  }}>
    <div style={{
      width: '48px', height: '48px',
      border: '4px solid rgba(255,255,255,0.3)',
      borderTopColor: '#fff',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <p style={{ color: '#fff', marginTop: '16px', fontSize: '16px', fontWeight: '500' }}>
      {message || 'Please wait...'}
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// FETCH WITH TIMEOUT - if the server doesn't respond within 30 seconds,
// show a clear error instead of hanging forever.
// ---------------------------------------------------------------------------
const fetchWithTimeout = (url, options = {}, timeoutMs = 30000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const token = window.sessionStorage.getItem('nwaAccessToken');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers, signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

// Writing legibility (store & display as descriptive text; map legacy numeric values if present)
const WRITING_LEGIBILITY_OPTIONS = [
  'Excellent - Consistently clear and easy to read',
  'Good - Generally clear with minor issues',
  'Fair - Readable but requires effort',
  'Poor - Difficult to read consistently',
  'Very Poor - Mostly illegible'
];

const displayWritingLegibility = (v) => {
  if (isEmptyValue(v)) return 'N/A';
  const s = String(v).trim();
  // Legacy numeric values (1-5)
  const legacyMap = {
    '1': WRITING_LEGIBILITY_OPTIONS[0],
    '2': WRITING_LEGIBILITY_OPTIONS[1],
    '3': WRITING_LEGIBILITY_OPTIONS[2],
    '4': WRITING_LEGIBILITY_OPTIONS[3],
    '5': WRITING_LEGIBILITY_OPTIONS[4]
  };
  return legacyMap[s] || s;
};

// Error categorization options
const ERROR_CATEGORIES = {
  englishReading: [
    { value: 'substitution', label: 'Substitution - replaces word with another' },
    { value: 'insertion', label: 'Insertion - adds word not present' },
    { value: 'omission', label: 'Omission - skips a word' },
    { value: 'reversal', label: 'Reversal - reverses word order/letters' },
    { value: 'refusal', label: 'Refusal - cannot attempt word' },
    { value: 'hesitation', label: 'Hesitation* (not counted as error)' },
    { value: 'repetition', label: 'Repetition* (not counted as error)' },
    { value: 'self-correction', label: 'Self-correction* (not counted as error)' }
  ],
  kannadaReading: [
    { value: 'substitution', label: 'Substitution (ಬದಲಾವಣೆ) - replaces word/sound' },
    { value: 'insertion', label: 'Insertion (ಸೇರಿಸುವಿಕೆ) - adds aksharas/matras' },
    { value: 'omission', label: 'Omission (ಬಿಟ್ಟುಬಿಡುವಿಕೆ) - skips aksharas/matras' },
    { value: 'matra-confusion', label: 'Matra confusion (ಮಾತ್ರಾ ತಪ್ಪು) - wrong vowel marker' },
    { value: 'gunintham-error', label: 'Gunintham error (ಗುಣಿತಾಂಕ ತಪ್ಪು) - consonant cluster' },
    { value: 'vattakshara-confusion', label: 'Vattakshara confusion (ಒತ್ತಕ್ಷರ ತಪ್ಪು) - subscript consonant' },
    { value: 'hesitation', label: 'Hesitation (ಹಿಂಜರಿಕೆ)* - not counted' },
    { value: 'repetition', label: 'Repetition (ಪುನರಾವರ್ತನೆ)* - not counted' },
    { value: 'self-correction', label: 'Self-correction (ಸ್ವಯಂ ಸರಿಪಡಿಸುವಿಕೆ)* - not counted' }
  ],
  englishSpelling: [
    { value: 'phonetic', label: 'Phonetic approximation - spells how it sounds' },
    { value: 'silent-letter', label: 'Silent letter omitted' },
    { value: 'vowel-digraph', label: 'Vowel digraph error - wrong vowel combination' },
    { value: 'consonant-blend', label: 'Consonant blend error - bl, cr, str, etc.' },
    { value: 'double-letter', label: 'Double letter error - missing/extra doubles' },
    { value: 'sight-word', label: 'Common/sight word error - irregular words' },
    { value: 'homophones', label: 'Homophones confusion - there/their, to/too' },
    { value: 'letter-reversal', label: 'Letter reversal - b/d, p/q confusion' },
    { value: 'affix-error', label: 'Suffix/prefix error - word endings/beginnings' },
    { value: 'irregular-pattern', label: 'Irregular pattern error - breaks phonics rules' },
    { value: 'visual-error', label: 'Visual error - incorrect visual word pattern' }
  ],
  kannadaSpelling: [
    { value: 'matra-error', label: 'Matra error (ಮಾತ್ರಾ ತಪ್ಪು) - wrong/missing vowel marker' },
    { value: 'gunintham-error', label: 'Gunintham error (ಗುಣಿತಾಂಕ ತಪ್ಪು) - consonant cluster' },
    { value: 'anusvara-visarga', label: 'Anusvara/Visarga error (ಂ ಃ) - misplaced/omitted' },
    { value: 'vattakshara-error', label: 'Vattakshara error (ಒತ್ತಕ್ಷರ ತಪ್ಪು) - subscript consonant' },
    { value: 'akshara-confusion', label: 'Akshara confusion (ಅಕ್ಷರ ಗೊಂದಲ) - similar letters' },
    { value: 'phonetic', label: 'Phonetic approximation (ಧ್ವನಿ ಆಧಾರಿತ) - based on sound' },
    { value: 'matra-length', label: 'Matra length confusion - short vs long vowels' },
    { value: 'halant-omission', label: 'Halant omission (ಹಲಂತ) - missing virama (್)' },
    { value: 'letter-reversal', label: 'Letter reversal (ಅಕ್ಷರ ಹಿಮ್ಮುಖ) - reversed order' },
    { value: 'archaic-form', label: 'Archaic form used (ಹಳೆಯ ರೂಪ) - old script' },
    { value: 'visual-error', label: 'Visual error' }
  ]
};


// Timepoints every repeatable screener (HAST-2, Numeracy, Confidence
// Levels) pins a row to on the dashboard. Anything recorded outside these
// four (an "Other" administration, saved under its own chosen date) still
// shows up, just as an extra row rather than a pinned slot.
const PINNED_TIMEPOINTS = ['Baseline', '3 months', '6 months', '9 months'];

const parseScreeningDate = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;
  const iso = new Date(text);
  if (!Number.isNaN(iso.getTime())) return iso;
  const m = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return Number.isNaN(d.getTime()) ? null : d;
};

const addMonths = (dateValue, months) => {
  const d = new Date(dateValue.getTime());
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return d;
};

const monthsForTimepoint = (tp) => ({ '3 months':3, '6 months':6, '9 months':9 }[tp] || 0);

const REVIEW_DATE_KEYS = {
  '3 months': 'review_3_months',
  '6 months': 'review_6_months',
  '9 months': 'review_9_months',
};

const dateToISO = (dateValue) => {
  if (!dateValue) return '';
  const y = dateValue.getFullYear();
  const m = String(dateValue.getMonth() + 1).padStart(2, '0');
  const d = String(dateValue.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const programmeBaselineDate = (sessionData = []) => {
  const dates = sessionData
    .filter((s) => s.timepoint === 'Baseline')
    .map((s) => parseScreeningDate(s.screening_date))
    .filter(Boolean)
    .sort((a, b) => a.getTime() - b.getTime());
  return dates[0] || null;
};

const buildEffectiveReviewSchedule = (sessionData = [], savedSchedule = {}) => {
  const baseline = programmeBaselineDate(sessionData);
  const result = { ...savedSchedule };
  Object.entries(REVIEW_DATE_KEYS).forEach(([tp, key]) => {
    if (!result[key] && baseline) result[key] = dateToISO(addMonths(baseline, monthsForTimepoint(tp)));
  });
  return result;
};

// Each repeatable screener that gets a dashboard card. Add a new entry
// here as each of the remaining screeners migrates over, rather than
// hand-building another card each time.
const TIMELINE_SCREENERS = [
  { key: 'neurodiversity', viewName: 'neurodiversity', screenerName: 'Neurodiversity Checklist', title: 'Neurodiversity', emoji: '🧩', annualOnly: true },
  { key: 'sdq', viewName: 'sdq', screenerName: 'SDQ', title: 'Social-Emotional Profiling', emoji: '📋' },
  { key: 'mals', viewName: 'mals', screenerName: 'Motivation', title: 'Motivation', emoji: '🎯' },
  { key: 'confidence', viewName: 'confidence', screenerName: 'Confidence Levels', title: 'Confidence Levels', emoji: '🙂' },
  { key: 'numeracy', viewName: 'numeracy', screenerName: 'Numeracy Fluency (Chinn)', title: 'Numeracy', emoji: '🔢' },
  { key: 'wrat4-sentence', viewName: 'wrat4-sentence', screenerName: 'WRAT4 Sentence Reading Comprehension', title: 'Reading Comprehension', emoji: '📘' },
  { key: 'writing', viewName: 'writing', screenerName: 'Writing Assessment', title: 'Writing', emoji: '\u270D\uFE0F' },
  { key: 'wrat4-word', viewName: 'wrat4-word', screenerName: 'WRAT4 Single Word Reading', title: 'Single word reading (untimed)', emoji: '📖' },
  { key: 'towre2', viewName: 'towre2', screenerName: 'TOWRE-2', title: 'Single word reading (timed)', emoji: '⏱️' },
  { key: 'hast2', viewName: 'hast2', screenerName: 'HAST-2', title: 'Spellings', emoji: '🔤' },
  { key: 'learning-style', viewName: 'learning-style', screenerName: 'Learning Style', title: 'Learning Style', emoji: '🧠', annualOnly: true },
];

// Converts a <input type="date"> value ("YYYY-MM-DD") to this app's usual
// DD/MM/YYYY display format, used when "Other" is chosen as a timepoint.
const isoToDDMMYYYY = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return `${d}/${m}/${y}`;
};

// Dashboard card for one repeatable screener. It uses the compact
// dashboard card recipe shared with the Student Passport so the profile
// remains visually consistent while four timepoint rows stay readable.
// footprint one status line used to take. Deliberately no red crosses
// here - with several screeners each showing four rows, that reads as
// a wall of failure rather than a to-do list. A plain dot marks "not
// due yet" or "not done yet", not "wrong".
const TimepointScreenerCard = ({
  emoji, title, screenerKey, screenerName, sessionData, reviewSchedule,
  reviewStateByTimepoint, onReviewDateChange, onAdministrationStatusChange,
  onAdd, annualOnly = false, reviewBusy = false
}) => {
  const [editingReviewKey, setEditingReviewKey] = useState('');
  const [reviewDateDraft, setReviewDateDraft] = useState('');
  const [editingChoiceTimepoint, setEditingChoiceTimepoint] = useState('');

  const sessions = sessionData.filter((s) => s.screener_name === screenerName);
  const isNumeracy = screenerName === 'Numeracy Fluency (Chinn)';
  const sessionsForTimepoint = (tp) => sessions.filter((s) => s.timepoint === tp);
  const stateForTimepoint = (tp) => {
    const matches = sessionsForTimepoint(tp);
    const count = matches.length;
    return {
      session: matches[0] || null,
      count,
      complete: isNumeracy ? count >= 4 : count > 0,
      partial: isNumeracy && count > 0 && count < 4,
    };
  };
  const otherTimepoints = [...new Set(sessions.filter((s) => !PINNED_TIMEPOINTS.includes(s.timepoint)).map((s) => s.timepoint))];
  const annualSession = sessionsForTimepoint('Baseline')[0] || sessions[0] || null;
  const baselineState = stateForTimepoint('Baseline');
  const baselineDone = annualOnly ? !!annualSession : baselineState.complete;
  const today = new Date();
  today.setHours(0,0,0,0);

  const rowStyle = {
    display: 'grid', gridTemplateColumns: '86px minmax(0, 1fr) 68px', alignItems: 'center',
    gap: '6px', padding: '5px 0', borderBottom: '1px solid #F3F4F6',
  };
  const btnBase = {
    border: 'none', borderRadius: '5px', padding: '3px 6px', fontSize: '11px',
    fontWeight: '600', width: '100%', textAlign: 'center', whiteSpace: 'nowrap',
  };
  const addBtnStyle = { ...btnBase, backgroundColor: COLORS.orange, color: COLORS.white, cursor: 'pointer' };
  const completedBtnStyle = { ...btnBase, backgroundColor: '#DDE8DF', color: '#4F6B57', cursor: 'default', fontSize: '15px', lineHeight: 1 };
  const disabledBtnStyle = { ...btnBase, backgroundColor: '#E5E7EB', color: '#9CA3AF', cursor: 'not-allowed' };
  const notAdminBtnStyle = { ...btnBase, backgroundColor: '#EEF1F4', color: '#6B7280', cursor: 'default', fontSize: '14px', lineHeight: 1 };
  const dot = (color) => (
    <span style={{
      display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%',
      backgroundColor: color, marginRight: '6px', flexShrink: 0,
    }} />
  );

  const reviewStateFor = (tp) => reviewStateByTimepoint?.[tp] || {};
  const administrationStatusFor = (tp) => reviewStateFor(tp)?.screener_statuses?.[screenerKey] || 'Administer';
  const canEditTimepoint = (tp) => {
    const state = reviewStateFor(tp);
    if (state?.workflow_status === 'Returned for correction') {
      return state?.correction_screener_key === screenerKey;
    }
    return state?.can_edit !== false;
  };

  const choiceDot = (color, tp) => (
    <button
      type="button"
      onClick={() => canEditTimepoint(tp) && !reviewBusy && setEditingChoiceTimepoint(tp)}
      disabled={!canEditTimepoint(tp) || reviewBusy}
      title={canEditTimepoint(tp) ? 'Screening options' : 'Screening is locked for review'}
      style={{ border: 'none', background: 'transparent', padding: 0, margin: 0, cursor: canEditTimepoint(tp) && !reviewBusy ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
    >
      {dot(color)}
    </button>
  );

  const choiceEditor = (tp, canChangeDate, openDateEditor) => (
    <select
      autoFocus
      defaultValue=""
      onBlur={() => setEditingChoiceTimepoint('')}
      onChange={async (e) => {
        const value = e.target.value;
        setEditingChoiceTimepoint('');
        if (value === 'date' && canChangeDate) {
          openDateEditor();
          return;
        }
        if (value === 'not-administered') {
          await onAdministrationStatusChange?.(screenerKey, tp, 'Not administered');
        } else if (value === 'administer') {
          await onAdministrationStatusChange?.(screenerKey, tp, 'Administer');
        }
      }}
      style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '3px 4px', border: `1px solid ${COLORS.borderGray}`, borderRadius: '5px', fontSize: '10px', color: COLORS.textDark, backgroundColor: COLORS.white }}
    >
      <option value="">Choose...</option>
      <option value="administer">Administer</option>
      {canChangeDate && <option value="date">Change date</option>}
      <option value="not-administered">Not administered</option>
    </select>
  );

  return (
    <div style={{ ...stylesCardBase, padding: '12px' }}>
      <h2 style={{ fontSize: '15px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '6px', textAlign: 'left', width: '100%' }}>
        <span>{emoji} {title}</span>
        {annualOnly && <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: '700', color: COLORS.textGray }}>Annual</span>}
      </h2>
      <div>
        {PINNED_TIMEPOINTS.map((tp) => {
          const tpReview = reviewStateFor(tp);
          const adminStatus = administrationStatusFor(tp);
          const notAdministered = adminStatus === 'Not administered';
          const reviewCanEdit = canEditTimepoint(tp);
          const correctionTarget = tpReview?.workflow_status === 'Returned for correction' && tpReview?.correction_screener_key === screenerKey;

          if (annualOnly) {
            const completed = !!annualSession;
            const primary = tp === 'Baseline';
            return (
              <div key={tp} style={rowStyle}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textDark }}>{tp}</span>
                {primary && completed ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', width: '100%', color: COLORS.green, fontWeight: '600', fontSize: '12px', overflow: 'hidden' }}>
                    {dot(COLORS.green)}<span style={{ whiteSpace: 'nowrap' }}>{annualSession.screening_date || 'Complete'}</span>
                  </span>
                ) : primary && notAdministered ? (
                  editingChoiceTimepoint === tp ? choiceEditor(tp, false, () => {}) : (
                    <span style={{ display: 'flex', alignItems: 'center', width: '100%', color: COLORS.textGray, fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {choiceDot('#B8C0C8', tp)}Not administered
                    </span>
                  )
                ) : primary && editingChoiceTimepoint === tp ? (
                  choiceEditor(tp, false, () => {})
                ) : primary ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', width: '100%', color: COLORS.orange, fontSize: '12px', whiteSpace: 'nowrap' }}>
                    {choiceDot(COLORS.orange, tp)}Due
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', width: '100%', color: COLORS.textGray, fontSize: '12px', whiteSpace: 'nowrap' }}>
                    {dot('#D1D5DB')}Not required
                  </span>
                )}
                <button
                  onClick={() => {
                    if (!primary || notAdministered || !reviewCanEdit) return;
                    if (completed && !correctionTarget) return;
                    onAdd('Baseline');
                  }}
                  disabled={!primary || notAdministered || !reviewCanEdit || (completed && !correctionTarget)}
                  style={primary && completed && correctionTarget ? addBtnStyle : primary && completed ? completedBtnStyle : notAdministered ? notAdminBtnStyle : primary && reviewCanEdit ? addBtnStyle : disabledBtnStyle}
                >
                  {primary && completed && correctionTarget ? 'Edit' : primary && completed ? '✓' : notAdministered ? '/' : primary ? '+ Add' : '-'}
                </button>
              </div>
            );
          }

          const tpState = stateForTimepoint(tp);
          const reviewKey = REVIEW_DATE_KEYS[tp];
          const plannedIso = reviewKey ? (reviewSchedule?.[reviewKey] || '') : '';
          const dueDate = plannedIso ? parseScreeningDate(plannedIso) : null;
          if (dueDate) dueDate.setHours(0,0,0,0);
          const isBaseline = tp === 'Baseline';
          const isFuture = !isBaseline && baselineDone && dueDate && today < dueDate;
          const isDueToday = !isBaseline && baselineDone && dueDate && today.getTime() === dueDate.getTime();
          const isPastDue = !isBaseline && baselineDone && dueDate && today > dueDate;
          const noReviewDate = !isBaseline && baselineDone && !dueDate;
          const displaySession = tpState.session;
          const openDateEditor = () => {
            if (!reviewKey) return;
            setEditingReviewKey(reviewKey);
            setReviewDateDraft(plannedIso || '');
          };
          const saveReviewDate = async (value) => {
            setReviewDateDraft(value);
            if (value && reviewKey && onReviewDateChange) {
              await onReviewDateChange(reviewKey, value, reviewSchedule);
              setEditingReviewKey('');
            }
          };
          const blockedBeforeBaseline = !isBaseline && !baselineDone;
          const canAdd = reviewCanEdit && !notAdministered && (isBaseline || isDueToday || tpState.partial);

          return (
            <div key={tp} style={rowStyle}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: blockedBeforeBaseline || isFuture || isPastDue || notAdministered ? COLORS.textGray : COLORS.textDark }}>{tp}</span>
              {tpState.complete ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', width: '100%', color: COLORS.green, fontWeight: '600', fontSize: '12px', overflow: 'hidden' }}>
                  {dot(COLORS.green)}<span style={{ whiteSpace: 'nowrap' }}>{displaySession?.screening_date || 'Complete'}</span>
                </span>
              ) : tpState.partial ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', width: '100%', color: COLORS.orange, fontSize: '12px', whiteSpace: 'nowrap' }}>
                  {dot(COLORS.orange)}{tpState.count} of 4 complete
                </span>
              ) : notAdministered ? (
                editingChoiceTimepoint === tp ? choiceEditor(tp, !!reviewKey, openDateEditor) : (
                  <span style={{ display: 'flex', alignItems: 'center', width: '100%', color: COLORS.textGray, fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    {choiceDot('#B8C0C8', tp)}<span style={{ whiteSpace: 'nowrap' }}>Not administered</span>
                  </span>
                )
              ) : editingReviewKey === reviewKey && reviewKey ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                  <input
                    autoFocus
                    type="date"
                    value={reviewDateDraft}
                    onChange={(e) => saveReviewDate(e.target.value)}
                    onBlur={() => setEditingReviewKey('')}
                    style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '3px 5px', border: `1px solid ${COLORS.borderGray}`, borderRadius: '5px', fontSize: '11px', color: COLORS.textDark, backgroundColor: COLORS.white }}
                  />
                </span>
              ) : editingChoiceTimepoint === tp ? (
                choiceEditor(tp, !!reviewKey, openDateEditor)
              ) : isBaseline ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', width: '100%', color: COLORS.orange, fontSize: '12px', whiteSpace: 'nowrap' }}>
                  {choiceDot(COLORS.orange, tp)}Due
                </span>
              ) : blockedBeforeBaseline ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', width: '100%', color: COLORS.textGray, fontSize: '12px', whiteSpace: 'nowrap' }}>
                  {choiceDot('#D1D5DB', tp)}After baseline
                </span>
              ) : isPastDue ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', width: '100%', color: '#9A6B3F', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  {choiceDot('#D8B38A', tp)}<span style={{ whiteSpace: 'nowrap' }}>Past due</span>
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', width: '100%', color: isDueToday ? COLORS.orange : COLORS.textGray, fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  {choiceDot(isDueToday ? COLORS.orange : '#D1D5DB', tp)}<span style={{ whiteSpace: 'nowrap' }}>{dueDate ? dueDate.toLocaleDateString('en-GB') : 'Set date'}</span>
                </span>
              )}
              <button
                onClick={() => {
                  if (tpState.complete && correctionTarget && isBaseline) { onAdd(tp); return; }
                  if (canAdd && !tpState.complete) onAdd(tp);
                }}
                disabled={notAdministered || (tpState.complete ? !(correctionTarget && isBaseline) : !canAdd)}
                style={tpState.complete && correctionTarget && isBaseline ? addBtnStyle : tpState.complete ? completedBtnStyle : notAdministered ? notAdminBtnStyle : canAdd ? addBtnStyle : disabledBtnStyle}
              >
                {tpState.complete && correctionTarget && isBaseline ? 'Edit' : tpState.complete ? '✓' : notAdministered ? '/' : tpState.partial ? 'Continue' : isPastDue ? '-' : isFuture || noReviewDate ? 'Due' : blockedBeforeBaseline ? '-' : '+ Add'}
              </button>
            </div>
          );
        })}

        {otherTimepoints.map((tp) => {
          const tpState = stateForTimepoint(tp);
          const displaySession = tpState.session;
          return (
            <div key={`other-${tp}`} style={rowStyle}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textDark }}>{tp || 'Other'}</span>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', width: '100%', color: tpState.complete ? COLORS.green : COLORS.orange, fontSize: '12px', whiteSpace: 'nowrap' }}>
                {dot(tpState.complete ? COLORS.green : COLORS.orange)}{tpState.complete ? (displaySession?.screening_date || 'Complete') : `${tpState.count} of 4 complete`}
              </span>
              <button disabled style={tpState.complete ? completedBtnStyle : disabledBtnStyle}>{tpState.complete ? '✓' : '-'}</button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PassportTimelineCard = ({ passportData, passportSaved, onOpen, correctionEditable = false }) => {
  const completed = !!passportSaved;
  const completedDate = passportData.admin_date || 'Completed';
  const rowStyle = {
    display: 'grid', gridTemplateColumns: '86px 1fr 60px', alignItems: 'center',
    gap: '6px', padding: '5px 0', borderBottom: '1px solid #F3F4F6',
  };
  const btnBase = {
    border: 'none', borderRadius: '5px', padding: '3px 6px', fontSize: '11px',
    fontWeight: '600', width: '100%', textAlign: 'center',
  };
  const activeBtn = { ...btnBase, backgroundColor: COLORS.orange, color: COLORS.white, cursor: 'pointer' };
  const disabledBtn = { ...btnBase, backgroundColor: '#E5E7EB', color: '#9CA3AF', cursor: 'not-allowed' };
  const dot = (color) => (
    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color, marginRight: '6px', flexShrink: 0 }} />
  );

  return (
    <div style={{ ...stylesCardBase, padding: '12px', display: 'flex', flexDirection: 'column' }}>
      <h2 style={{ fontSize: '15px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '6px', textAlign: 'left', width: '100%' }}>
        <span>📄 Student Passport</span>
        <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: '700', color: COLORS.textGray }}>Annual</span>
      </h2>
      {PINNED_TIMEPOINTS.map((tp) => {
        const primary = tp === 'Baseline';
        return (
          <div key={tp} style={rowStyle}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textDark }}>{tp}</span>
            {primary ? (completed ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', width: '100%', color: COLORS.green, fontWeight: '600', fontSize: '12px', overflow: 'hidden' }}>
                {dot(COLORS.green)}<span style={{ whiteSpace: 'nowrap' }}>{completedDate}</span>
              </span>
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', width: '100%', color: COLORS.orange, fontSize: '12px' }}>
                {dot(COLORS.orange)}Annual update
              </span>
            )) : (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', width: '100%', color: COLORS.textGray, fontSize: '12px', whiteSpace: 'nowrap' }}>
                {dot('#D1D5DB')}Not required
              </span>
            )}
            <button
              onClick={() => primary && onOpen()}
              disabled={!primary}
              style={primary ? activeBtn : disabledBtn}
            >
              {primary ? (completed ? (correctionEditable ? 'Edit' : 'View') : '+ Add') : '-'}
            </button>
          </div>
        );
      })}
    </div>
  );
};

// Same visual recipe as styles.card (defined inside the main component),
// duplicated here at module scope so this card can be built outside it.
const stylesCardBase = {
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
};


const NeuroWiseScreening = () => {
  // State management
  const [view, setView] = useState('login');
  const [facilitatorId, setFacilitatorId] = useState('');
  const [pin, setPin] = useState('');
  const [students, setStudents] = useState([]);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [passportData, setPassportData] = useState({});
  const [passportSaved, setPassportSaved] = useState(false);
  const [screeningData, setScreeningData] = useState([]);
  const [currentScreening, setCurrentScreening] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [facilitatorName, setFacilitatorName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [schoolIds, setSchoolIds] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [pinResetOpen, setPinResetOpen] = useState(false);
  const [pinResetUserId, setPinResetUserId] = useState('');
  const [pinResetValue, setPinResetValue] = useState('');
  const [pinResetConfirm, setPinResetConfirm] = useState('');
  const [pinResetSaving, setPinResetSaving] = useState(false);
  const [pinResetError, setPinResetError] = useState('');
  const [pinResetMessage, setPinResetMessage] = useState('');
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateUserId, setDeactivateUserId] = useState('');
  const [deactivateConfirmed, setDeactivateConfirmed] = useState(false);
  const [deactivateSaving, setDeactivateSaving] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');
  const [deactivateMessage, setDeactivateMessage] = useState('');
  const [assignmentStudent, setAssignmentStudent] = useState(null);
  const [assignmentUserId, setAssignmentUserId] = useState('');
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [editStudent, setEditStudent] = useState(null);
  const [editStudentForm, setEditStudentForm] = useState({});
  const [editStudentSaving, setEditStudentSaving] = useState(false);
  const [editStudentError, setEditStudentError] = useState('');
  const [studentActionBusy, setStudentActionBusy] = useState('');
  const [deleteStudent, setDeleteStudent] = useState(null);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [addStudentSaving, setAddStudentSaving] = useState(false);
  const [addStudentError, setAddStudentError] = useState('');
  const [reportMenuStudentCode, setReportMenuStudentCode] = useState('');
  const [reportAvailability, setReportAvailability] = useState({});
  const [reportLoadingStudentCode, setReportLoadingStudentCode] = useState('');
  const [newStudentForm, setNewStudentForm] = useState({
    first_name: '', surname: '', dob: '', standard: '', school_id: '', programme_id: '', assigned_user_id: '',
    gender: '', attendance_pct: '', native_language: '', first_gen_learner: '', medium_of_instruction: ''
  });
  // Optional UI-only expansion controls for reading error examples (kept separate from saved data)
  const [l1ReadingExamplesShown, setL1ReadingExamplesShown] = useState(3);
  const [l2ReadingExamplesShown, setL2ReadingExamplesShown] = useState(3);
  const [l3ReadingExamplesShown, setL3ReadingExamplesShown] = useState(3);
  
  // Screening form state
  const [currentSection, setCurrentSection] = useState(1);
  // IMPORTANT: Keep an immutable template for a fresh (empty) screening form.
  // This prevents data from a previous student/screening carrying over.
  const INITIAL_FORM_DATA = {
    gender: '', screeningType: '', attendance: '', nativeLanguage: '', mediumOfInstruction: '', handedness: '', firstGenerationLearner: '',
    sdqEmotional: '', sdqConduct: '', sdqHyperactivity: '', sdqPeer: '', sdqProsocial: '',
    totalWords: '', legibleWords: '', illegibleWords: '', spellingErrors: '', spellingErrorsList: '',
    spellingErrorsDuringFreeWriting: '',
    // Free writing spelling (Section 3)
    freeWritingExample1Correct: '', freeWritingExample1SpeltAs: '', freeWritingExample1ErrorType: '',
    freeWritingExample2Correct: '', freeWritingExample2SpeltAs: '', freeWritingExample2ErrorType: '',
    freeWritingExample3Correct: '', freeWritingExample3SpeltAs: '', freeWritingExample3ErrorType: '',
    freeWritingExample4Correct: '', freeWritingExample4SpeltAs: '', freeWritingExample4ErrorType: '',
    freeWritingExample5Correct: '', freeWritingExample5SpeltAs: '', freeWritingExample5ErrorType: '',
    freeWritingAdditionalNotes: '',
    // Neurodiversity (Section 6)
    dyslexiaScore: '',
    // Reading Level 1 (Section 7)
    readingLanguage: '',
    readingSpeed: '',
    totalWordsRead: '',
    readingErrors: '',
    readingFluencyL1: '',
    readingComprehensionL1: '',
    proseReadingExample1Correct: '', proseReadingExample1ReadAs: '', proseReadingExample1ErrorType: '',
    proseReadingExample2Correct: '', proseReadingExample2ReadAs: '', proseReadingExample2ErrorType: '',
    proseReadingExample3Correct: '', proseReadingExample3ReadAs: '', proseReadingExample3ErrorType: '',
    proseReadingExample4Correct: '', proseReadingExample4ReadAs: '', proseReadingExample4ErrorType: '',
    proseReadingExample5Correct: '', proseReadingExample5ReadAs: '', proseReadingExample5ErrorType: '',
    proseReadingAdditionalNotes: '',
    // Reading Level 2 (Section 8)
    l2ReadingLanguage: '', l2ReadingSpeed: '', l2TotalWordsRead: '', l2ReadingErrors: '',
    l2ReadingFluency: '',
    l2ProseReadingExample1Correct: '', l2ProseReadingExample1ReadAs: '', l2ProseReadingExample1ErrorType: '',
    l2ProseReadingExample2Correct: '', l2ProseReadingExample2ReadAs: '', l2ProseReadingExample2ErrorType: '',
    l2ProseReadingExample3Correct: '', l2ProseReadingExample3ReadAs: '', l2ProseReadingExample3ErrorType: '',
    l2ProseReadingExample4Correct: '', l2ProseReadingExample4ReadAs: '', l2ProseReadingExample4ErrorType: '',
    l2ProseReadingExample5Correct: '', l2ProseReadingExample5ReadAs: '', l2ProseReadingExample5ErrorType: '',
    l2ProseReadingAdditionalNotes: '',
    // Reading Level 3 (Section 9)
    l3ReadingLanguage: '', l3ReadingSpeed: '', l3TotalWordsRead: '', l3ReadingErrors: '',
    l3ReadingFluency: '',
    l3ProseReadingExample1Correct: '', l3ProseReadingExample1ReadAs: '', l3ProseReadingExample1ErrorType: '',
    l3ProseReadingExample2Correct: '', l3ProseReadingExample2ReadAs: '', l3ProseReadingExample2ErrorType: '',
    l3ProseReadingExample3Correct: '', l3ProseReadingExample3ReadAs: '', l3ProseReadingExample3ErrorType: '',
    l3ProseReadingExample4Correct: '', l3ProseReadingExample4ReadAs: '', l3ProseReadingExample4ErrorType: '',
    l3ProseReadingExample5Correct: '', l3ProseReadingExample5ReadAs: '', l3ProseReadingExample5ErrorType: '',
    l3ProseReadingAdditionalNotes: '',
    // Baseline and Ceilling levels (Section 10)
    l1CeilingLevel: '',
    l1WorkingBaseline: '',
    l2CeilingLevel: '',
    l2WorkingBaseline: '',
    l3CeilingLevel: '',
    l3WorkingBaseline: '',
    // Single word spelling examples (Section 4)
    singleWordSpellingExample1Correct: '', singleWordSpellingExample1SpeltAs: '', singleWordSpellingExample1ErrorType: '',
    singleWordSpellingExample2Correct: '', singleWordSpellingExample2SpeltAs: '', singleWordSpellingExample2ErrorType: '',
    singleWordSpellingExample3Correct: '', singleWordSpellingExample3SpeltAs: '', singleWordSpellingExample3ErrorType: '',
    singleWordSpellingAdditionalNotes: '',
    writingLegibility: '',
    alphabetWriting: '', writingComments: '',

    // Free writing: Physical & Behavioural Aspects
    pencilGrip: '', sittingPosture: '', paperPosition: '',
    // Free writing: Writing behaviour
    writingHesitation: '', writingCrossedOut: '', writingLookingUp: '', writingHandFatigue: '', writingFrustration: '', writingFluency: '',
    // After writing
    afterIdeasClearly: '', afterLogicalSequence: '', afterVocabularyRange: '',
    // Language skills
    languagePlanning: '', languageGrammar: '', languageSentenceStructure: '', languageCapitalisation: '',
    // Writing Mechanics
    mechanicsLetterFormation: '', mechanicsSizeConsistency: '', mechanicsSpacingBetweenWords: '', mechanicsLineAlignment: '',
    additionScore: '', subtractionScore: '', multiplicationScore: '', divisionScore: '',
    dyspraxiaScore: '', adhdScore: '', asdScore: '', dyscalculiaScore: '', sliScore: '',
    readingLanguage: '', readingSpeed: '', totalWordsRead: '', readingErrors: '', readingComprehensionL1: '', spellingTestScore: '', wordsReadInaccurately: '',
    l2ReadingComprehensionL2: '', l3ReadingComprehensionL3: '',
    visualScore: '', auditoryScore: '', kinestheticScore: '',
    interventionStartDate: '', interventionEndDate: ''
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const errorPercentageL1 =
  formData.totalWordsRead > 0
    ? ((Number(formData.readingErrors) / Number(formData.totalWordsRead)) * 100).toFixed(1)
    : '';

const errorPercentageL2 =
  formData.l2TotalWordsRead > 0
    ? ((Number(formData.l2ReadingErrors) / Number(formData.l2TotalWordsRead)) * 100).toFixed(1)
    : '';

const errorPercentageL3 =
  formData.l3TotalWordsRead > 0
    ? ((Number(formData.l3ReadingErrors) / Number(formData.l3TotalWordsRead)) * 100).toFixed(1)
    : '';
  const [formLocked, setFormLocked] = useState(false);

// ── AUTO-CALCULATE CEILING & BASELINE ──
// This runs automatically whenever error percentages change.
// Rules:
//   >20% errors on a passage = that passage is the CEILING (too hard)
//   <20% errors on a passage = that passage is the BASELINE (comfortable)
//
// The system records the FIRST ceiling found and the LAST baseline found,
// working through L1 → L2 → L3.
const autoCeiling = (() => {
  // Check each level in order
  const l1Pct = errorPercentageL1 !== '' ? Number(errorPercentageL1) : null;
  const l2Pct = errorPercentageL2 !== '' ? Number(errorPercentageL2) : null;
  const l3Pct = errorPercentageL3 !== '' ? Number(errorPercentageL3) : null;

  // Scenario A: L1 errors >20% (struggling) → L1 is ceiling
  if (l1Pct !== null && l1Pct > 20) {
    if (l2Pct !== null && l2Pct <= 20) {
      return 'Below L1';  // P2 (below L1) is where they can manage
    }
    return 'L1';  // Ceiling is L1, no baseline found yet from P2
  }

  // Scenario B: L1 errors ≤20% → move up
  if (l2Pct !== null && l2Pct > 20) return 'L2';
  if (l3Pct !== null && l3Pct > 20) return 'L3';

  return '';  // No ceiling found (student at or above expected level)
})();

const autoBaseline = (() => {
  const l1Pct = errorPercentageL1 !== '' ? Number(errorPercentageL1) : null;
  const l2Pct = errorPercentageL2 !== '' ? Number(errorPercentageL2) : null;
  const l3Pct = errorPercentageL3 !== '' ? Number(errorPercentageL3) : null;

  // Scenario A: L1 errors >20% → moved down to P2
  if (l1Pct !== null && l1Pct > 20) {
    if (l2Pct !== null && l2Pct <= 20) return 'Below L1';
    return '';  // No baseline found
  }

  // Scenario B: L1 errors ≤20% → L1 is at least baseline, keep moving up
  if (l1Pct !== null && l1Pct <= 20) {
    // Take the highest level they can still read comfortably
    if (l3Pct !== null && l3Pct <= 20) return 'L3';
    if (l2Pct !== null && l2Pct <= 20) return 'L2';
    return 'L1';
  }

  return '';  // Not enough data yet
})();

// Auto-fill the formData ceiling/baseline fields whenever they change
// We use a React effect-like pattern: update formData when auto values differ
if (autoCeiling && formData.l1CeilingLevel !== autoCeiling) {
  // Use setTimeout to avoid updating state during render
  setTimeout(() => setFormData(prev => ({ ...prev, l1CeilingLevel: autoCeiling })), 0);
}
if (autoBaseline && formData.l1WorkingBaseline !== autoBaseline) {
  setTimeout(() => setFormData(prev => ({ ...prev, l1WorkingBaseline: autoBaseline })), 0);
}
const hasAnyL2Data =
  !!formData.l2ReadingSpeed ||
  !!formData.l2TotalWordsRead ||
  !!formData.l2ReadingErrors ||
  !!formData.l2ReadingFluency ||
  !!formData.l2ReadingComprehensionL2 ||
  !!formData.l2CeilingLevel ||
  !!formData.l2WorkingBaseline;

const hasAnyL3Data =
  !!formData.l3ReadingSpeed ||
  !!formData.l3TotalWordsRead ||
  !!formData.l3ReadingErrors ||
  !!formData.l3ReadingFluency ||
  !!formData.l3ReadingComprehensionL3 ||
  !!formData.l3CeilingLevel ||
  !!formData.l3WorkingBaseline;

const canSkipPassage3 =
  // Path B: Child struggled at P1 (>20%), coped at P2 (<20%) → ceiling & baseline found → skip OK
  (
    (errorPercentageL1 !== '' && Number(errorPercentageL1) > 20) &&
    (errorPercentageL2 !== '' && Number(errorPercentageL2) < 20)
  ) ||
  // Path A: Child coped at P1 (<20%), struggled at P2 (>20%) → baseline & ceiling found → skip OK
  (
    (errorPercentageL1 !== '' && Number(errorPercentageL1) < 20) &&
    (errorPercentageL2 !== '' && Number(errorPercentageL2) > 20)
  ) ||
  // Path B: Child struggled at both P1 and P2 (>20%) → ceiling found, no baseline → skip OK
  (
    (errorPercentageL1 !== '' && Number(errorPercentageL1) > 20) &&
    (errorPercentageL2 !== '' && Number(errorPercentageL2) > 20)
  );
  // NOTE: If child coped at BOTH P1 and P2 (<20% errors each), Passage 3 is MANDATORY
  // because no ceiling has been found yet and we need to test at expected level.
  // Styles
  const styles = {

    label: { display: 'block', fontSize: '14px', fontWeight: '600', color: COLORS.textDark, marginBottom: '6px' },
    smallLabel: { display: 'block', fontSize: '12px', fontWeight: '600', color: COLORS.textGray, marginBottom: '6px' },
    formGrid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    exampleBlock: { border: `1px solid ${COLORS.borderGray}`, borderRadius: '14px', padding: '14px', background: '#FFFFFF' },
    exampleTitle: { fontSize: '14px', fontWeight: '700', color: COLORS.textDark, marginBottom: '4px' },
    exampleHint: { fontSize: '12px', color: COLORS.textGray, marginBottom: '12px' },
    exampleRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' },
    container: {
      minHeight: '100vh',
      backgroundColor: COLORS.lightGray
    },
    loginContainer: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, rgba(0, 51, 102, 0.85) 0%, rgba(255, 107, 53, 0.75) 100%), url(/classroom-bg.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      position: 'relative'
    },
    loginBox: {
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderRadius: '12px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      padding: '48px 40px',
      width: '100%',
      maxWidth: '440px',
      border: `1px solid ${COLORS.navyBlue}`,
      backdropFilter: 'blur(10px)',
      position: 'relative',
      zIndex: 1
    },
    iconCircle: {
      display: 'inline-block',
      padding: '12px',
      backgroundColor: COLORS.orange,
      borderRadius: '9999px',
      marginBottom: '16px'
    },
    header: {
      backgroundColor: COLORS.navyBlue,
      color: COLORS.white,
      padding: '8px 16px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      borderBottom: `2px solid ${COLORS.mustard}`,
      position: 'relative'
    },
    headerContent: {
      maxWidth: '1280px',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    button: {
      backgroundColor: COLORS.orange,
      color: COLORS.white,
      padding: '12px 24px',
      borderRadius: '6px',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      fontSize: '16px'
    },
    logoutButton: {
      backgroundColor: COLORS.green,
      color: COLORS.white,
      padding: '8px 16px',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: `2px solid ${COLORS.borderGray}`,
      borderRadius: '6px',
      fontSize: '16px',
      outline: 'none',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '12px 16px',
      border: `2px solid ${COLORS.borderGray}`,
      borderRadius: '6px',
      fontSize: '16px',
      outline: 'none',
      resize: 'vertical',
      fontFamily: 'inherit',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '12px 16px',
      border: `2px solid ${COLORS.borderGray}`,
      borderRadius: '6px',
      fontSize: '16px',
      outline: 'none',
      backgroundColor: COLORS.white,
      boxSizing: 'border-box'
    },
    card: {
      backgroundColor: COLORS.white,
      borderRadius: '8px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      padding: '24px',
      cursor: 'pointer',
      transition: 'box-shadow 0.2s'
    },
    errorBox: {
      backgroundColor: '#FEE2E2',
      border: '1px solid #FCA5A5',
      color: '#991B1B',
      padding: '12px 16px',
      borderRadius: '6px',
      marginTop: '16px'
    },
    successBox: {
      backgroundColor: '#D1FAE5',
      border: '1px solid #6EE7B7',
      color: '#065F46',
      padding: '12px 16px',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }
  };

  const BUILD_VERSION = '2026-09-27.2-team-access-revocation'; // bump this to verify deployments

  // Login handler
  // Look up which school(s) this account can add students under, and that
  // school's active programmes, so the Add Student form can offer a picker.
  // Legacy MJ facilitators simply get an empty list back and won't see the option.
  const fetchSchoolsAndProgrammes = async (id) => {
    try {
      const schoolsResponse = await fetchWithTimeout(`${API_URL}/api/schools/${id}`);
      const schoolsData = await schoolsResponse.json();
      const schools = schoolsData.school_ids || [];
      setSchoolIds(schools);

      if (schools.length > 0) {
        // Load programmes for every school this user can access. This keeps
        // the student dashboard correct for multi-school users instead of
        // assuming the first authorised school is always the student's school.
        const programmeLists = await Promise.all(
          schools.map(async (school) => {
            const programmesResponse = await fetchWithTimeout(`${API_URL}/api/programmes/${school.school_id}`);
            const programmesData = await programmesResponse.json();
            return (programmesData.programmes || []).map((programme) => ({ ...programme, school_id: school.school_id }));
          })
        );
        const byId = new Map();
        programmeLists.flat().forEach((programme) => {
          if (programme.programme_id) byId.set(programme.programme_id, programme);
        });
        setProgrammes(Array.from(byId.values()));
      } else {
        setProgrammes([]);
      }
    } catch (err) {
      // Non-critical: if this fails, the Add Student button just won't appear.
      setSchoolIds([]);
      setProgrammes([]);
    }
  };

  const fetchPractitioners = async () => {
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/practitioners`);
      const data = await response.json();
      if (response.ok) setPractitioners(data.practitioners || []);
      else setPractitioners([]);
    } catch (err) {
      setPractitioners([]);
    }
  };

  const openPinReset = () => {
    setPinResetUserId('');
    setPinResetValue('');
    setPinResetConfirm('');
    setPinResetError('');
    setPinResetMessage('');
    setPinResetOpen(true);
  };

  const closePinReset = () => {
    if (pinResetSaving) return;
    setPinResetOpen(false);
    setPinResetError('');
    setPinResetMessage('');
    setPinResetValue('');
    setPinResetConfirm('');
  };

  const submitPinReset = async () => {
    setPinResetError('');
    setPinResetMessage('');
    if (!pinResetUserId) { setPinResetError('Please select an NWA Practitioner.'); return; }
    if (!/^\d{4}$/.test(pinResetValue)) { setPinResetError('PIN must be exactly 4 digits.'); return; }
    if (pinResetValue !== pinResetConfirm) { setPinResetError('The PIN entries do not match.'); return; }
    setPinResetSaving(true);
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/practitioners/${encodeURIComponent(pinResetUserId)}/reset-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_pin: pinResetValue, confirm_pin: pinResetConfirm })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'PIN reset failed');
      setPinResetValue('');
      setPinResetConfirm('');
      setPinResetMessage(`New PIN saved for ${data.full_name || data.user_id}. The previous PIN is no longer valid.`);
    } catch (err) {
      setPinResetError(err.message || 'PIN reset failed');
    } finally {
      setPinResetSaving(false);
    }
  };

  const openDeactivateTeamMember = () => {
    setDeactivateUserId('');
    setDeactivateConfirmed(false);
    setDeactivateError('');
    setDeactivateMessage('');
    setDeactivateOpen(true);
  };

  const closeDeactivateTeamMember = () => {
    if (deactivateSaving) return;
    setDeactivateOpen(false);
    setDeactivateUserId('');
    setDeactivateConfirmed(false);
    setDeactivateError('');
    setDeactivateMessage('');
  };

  const submitDeactivateTeamMember = async () => {
    setDeactivateError('');
    setDeactivateMessage('');
    if (!deactivateUserId) { setDeactivateError('Please select an NWA Practitioner.'); return; }
    if (!deactivateConfirmed) { setDeactivateError('Please confirm that you want to deactivate this account.'); return; }
    setDeactivateSaving(true);
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/practitioners/${encodeURIComponent(deactivateUserId)}/deactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Account deactivation failed');
      setDeactivateConfirmed(false);
      setDeactivateMessage(`Access deactivated for ${data.full_name || data.user_id}. Historical student, screening and learning-plan records have been retained.`);
      await fetchPractitioners();
      setDeactivateUserId('');
    } catch (err) {
      setDeactivateError(err.message || 'Account deactivation failed');
    } finally {
      setDeactivateSaving(false);
    }
  };

  const practitionerName = (userId) => {
    if (!userId) return 'Unassigned';
    const match = practitioners.find((p) => p.user_id === userId);
    return match?.full_name || userId;
  };

  const openAddStudent = () => {
    setAddStudentError('');
    const defaultSchoolId = schoolIds.length === 1 ? (schoolIds[0]?.school_id || '') : '';
    setNewStudentForm((prev) => ({
      ...prev,
      school_id: prev.school_id || defaultSchoolId,
      programme_id: prev.school_id && prev.school_id !== defaultSchoolId ? '' : prev.programme_id,
      assigned_user_id: isOwnerRole(userRole) ? facilitatorId : ''
    }));
    setShowAddStudent(true);
  };

  const openAssignment = (student) => {
    setAssignmentStudent(student);
    setAssignmentUserId(student.assigned_user_id || '');
    setAssignmentError('');
  };

  const saveAssignment = async () => {
    if (!assignmentStudent || !assignmentUserId) {
      setAssignmentError('Please select an NWA Practitioner.');
      return;
    }
    setAssignmentSaving(true);
    setAssignmentError('');
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/students/${assignmentStudent.student_code}/assignment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: assignmentUserId })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setAssignmentError(data.error || 'Could not update the assignment.');
        return;
      }
      setStudents((prev) => prev.map((s) =>
        s.student_code === assignmentStudent.student_code
          ? { ...s, assigned_user_id: data.assigned_user_id }
          : s
      ));
      setAssignmentStudent(null);
      setAssignmentUserId('');
    } catch (err) {
      setAssignmentError('Connection error. Please try again.');
    } finally {
      setAssignmentSaving(false);
    }
  };

  const toDateInputValue = (value) => {
    if (!value) return '';
    const text = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
    const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) return `${match[3]}-${String(match[2]).padStart(2, '0')}-${String(match[1]).padStart(2, '0')}`;
    return '';
  };

  const openEditStudent = (student) => {
    setEditStudent(student);
    setEditStudentError('');
    setEditStudentForm({
      first_name: student.first_name || String(student.full_name || '').split(' ')[0] || '',
      surname: student.surname || String(student.full_name || '').split(' ').slice(1).join(' '),
      dob: toDateInputValue(student.dob),
      standard: student.standard || '',
      school_id: student.school_id || '',
      programme_id: student.programme_id || '',
      gender: student.gender || '',
      attendance_pct: student.attendance_pct || '',
      native_language: student.native_language || '',
      first_gen_learner: student.first_gen_learner || '',
      medium_of_instruction: student.medium_of_instruction || ''
    });
  };

  const saveStudentProfile = async () => {
    if (!editStudent) return;
    if (!String(editStudentForm.first_name || '').trim()) {
      setEditStudentError('First name is required.');
      return;
    }
    if (!editStudentForm.school_id) {
      setEditStudentError('Please select a school.');
      return;
    }
    if (!editStudentForm.programme_id) {
      setEditStudentError('Please select a programme.');
      return;
    }
    setEditStudentSaving(true);
    setEditStudentError('');
    try {
      let formattedDob = editStudentForm.dob;
      if (formattedDob && formattedDob.includes('-')) {
        const [y, m, d] = formattedDob.split('-');
        formattedDob = `${d}/${m}/${y}`;
      }
      const payload = { ...editStudentForm, dob: formattedDob };
      const response = await fetchWithTimeout(`${API_URL}/api/students/${editStudent.student_code}/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setEditStudentError(data.error || 'Could not update the student.');
        return;
      }
      await refreshStudents();
      setEditStudent(null);
    } catch (err) {
      setEditStudentError('Connection error. Please try again.');
    } finally {
      setEditStudentSaving(false);
    }
  };

  const toggleStudentLock = async (student) => {
    const nextLocked = !student.is_locked;
    setStudentActionBusy(student.student_code);
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/students/${student.student_code}/lock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locked: nextLocked })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        window.alert(data.error || 'Could not update the profile lock.');
        return;
      }
      setStudents((prev) => prev.map((s) => s.student_code === student.student_code ? { ...s, is_locked: nextLocked } : s));
    } catch (err) {
      window.alert('Connection error. Please try again.');
    } finally {
      setStudentActionBusy('');
    }
  };

  const deleteTestStudent = async (student) => {
    setStudentActionBusy(student.student_code);
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/students/${student.student_code}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm_name: student.full_name })
      }, 60000);
      const data = await response.json();
      if (!response.ok || !data.success) {
        window.alert(data.error || 'Could not delete the student.');
        return;
      }
      setStudents((prev) => prev.filter((s) => s.student_code !== student.student_code));
      setDeleteStudent(null);
    } catch (err) {
      window.alert('The delete request did not complete. Please refresh before trying again.');
    } finally {
      setStudentActionBusy('');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoadingMessage('Logging in...');
    setError('');

    try {
      const response = await fetchWithTimeout(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ facilitator_id: facilitatorId, pin })
      });

      const data = await response.json();

      if (response.ok) {
        if (data.access_token) window.sessionStorage.setItem('nwaAccessToken', data.access_token);
        setFacilitatorName(data.facilitator_name);
        setUserRole(data.role || '');
        setStudents(data.students);
        setView('dashboard');
        fetchSchoolsAndProgrammes(facilitatorId);
        if (isOwnerRole(data.role)) fetchPractitioners();
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('The server took too long to respond. Please try again.');
      } else {
        setError('Connection error. Please try again.');
      }
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleAddStudent = async () => {
    setAddStudentError('');

    if (!newStudentForm.first_name.trim()) {
      setAddStudentError('Please enter the student\'s first name.');
      return;
    }

    if (schoolIds.length > 0 && !newStudentForm.school_id) {
      setAddStudentError('Please select a school before adding this student.');
      return;
    }

    const availableProgrammes = programmes.filter((p) => !newStudentForm.school_id || p.school_id === newStudentForm.school_id);
    if (availableProgrammes.length > 0 && !newStudentForm.programme_id) {
      setAddStudentError('Please select a programme before adding this student.');
      return;
    }

    if (isOwnerRole(userRole) && !newStudentForm.assigned_user_id) {
      setAddStudentError('Please select the NWA Practitioner responsible for this student.');
      return;
    }

    // The date input gives YYYY-MM-DD; the rest of the app expects DD/MM/YYYY.
    let formattedDob = newStudentForm.dob;
    if (formattedDob && formattedDob.includes('-')) {
      const [y, m, d] = formattedDob.split('-');
      formattedDob = `${d}/${m}/${y}`;
    }

    setAddStudentSaving(true);
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/students/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          facilitator_id: facilitatorId,
          first_name: newStudentForm.first_name,
          surname: newStudentForm.surname,
          dob: formattedDob,
          standard: newStudentForm.standard,
          school_id: newStudentForm.school_id,
          programme_id: newStudentForm.programme_id,
          assigned_user_id: newStudentForm.assigned_user_id,
          gender: newStudentForm.gender,
          attendance_pct: newStudentForm.attendance_pct,
          native_language: newStudentForm.native_language,
          first_gen_learner: newStudentForm.first_gen_learner,
          medium_of_instruction: newStudentForm.medium_of_instruction,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStudents([...students, data.student]);
        setShowAddStudent(false);
        setNewStudentForm({
          first_name: '', surname: '', dob: '', standard: '', school_id: '', programme_id: '', assigned_user_id: '',
          gender: '', attendance_pct: '', native_language: '', first_gen_learner: '', medium_of_instruction: ''
        });
        // Stay on the main student-card dashboard after creating a student.
        // The practitioner can choose Screening, Learning Plans or Reports from the card.
        setCurrentStudent(null);
        setView('dashboard');
      } else {
        setAddStudentError(data.error || 'Could not add this student, please try again.');
      }
    } catch (err) {
      setAddStudentError('Connection error. Please try again.');
    } finally {
      setAddStudentSaving(false);
    }
  };

  // Logout handler
  const handleLogout = () => {
    window.sessionStorage.removeItem('nwaAccessToken');
    // Remove any temporary offline lesson data from this device on sign-out.
    Object.keys(window.localStorage).forEach((key) => {
      if (key.startsWith('nwaLessonDraft:') || key.startsWith('nwaActiveLessonCache:')) {
        window.localStorage.removeItem(key);
      }
    });
    setView('login');
    setFacilitatorId('');
    setPin('');
    setStudents([]);
    setCurrentStudent(null);
    setPassportData({});
    setPassportSaved(false);
    setScreeningData([]);
    setFacilitatorName('');
    setUserRole('');
    setCurrentSection(1);
    setFormData(INITIAL_FORM_DATA);
    setFormLocked(false);
    setPassportSaved(false);
    setL1ReadingExamplesShown(3);
    setL2ReadingExamplesShown(3);
    setL3ReadingExamplesShown(3);
    setSchoolIds([]);
    setProgrammes([]);
    setPractitioners([]);
    setAssignmentStudent(null);
    setAssignmentUserId('');
    setSchoolFilter('all');
    setStatusFilter('active');
    setEditStudent(null);
    setEditStudentForm({});
    setStudentActionBusy('');
    setShowAddStudent(false);
  };

  // Refresh student data
  const refreshStudents = async () => {
    setLoading(true);
    setLoadingMessage('Refreshing students...');
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/students/${facilitatorId}`);
      const data = await response.json();
      if (response.ok) {
        setStudents(data.students || []);
        setError('');
      } else {
        setError(data.error || 'Failed to refresh student data');
      }
    } catch (err) {
      if (err.name === 'AbortError') setError('The server took too long to respond. Please try again.');
      else setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // View student profile
  const [sessionData, setSessionData] = useState([]);
  const [reviewSchedule, setReviewSchedule] = useState({});
  const [reviewScheduleSaving, setReviewScheduleSaving] = useState(false);
  const [reviewScheduleStatus, setReviewScheduleStatus] = useState('');
  const [screeningReviewByTimepoint, setScreeningReviewByTimepoint] = useState({});
  const [screeningReviewBusy, setScreeningReviewBusy] = useState(false);
  const [screeningReviewError, setScreeningReviewError] = useState('');
  const [showReturnForCorrection, setShowReturnForCorrection] = useState(false);
  const [returnForCorrectionNote, setReturnForCorrectionNote] = useState('');
  const [returnForCorrectionScreenerKey, setReturnForCorrectionScreenerKey] = useState('');
  const [reviewCorrectionMode, setReviewCorrectionMode] = useState('return');
  const [showScreeningReviewEvidence, setShowScreeningReviewEvidence] = useState(false);
  const [screeningReviewEvidence, setScreeningReviewEvidence] = useState(null);
  const [screeningReviewEvidenceLoading, setScreeningReviewEvidenceLoading] = useState(false);
  const [pendingTimepoint, setPendingTimepoint] = useState('Baseline');
  const startTimepointScreener = (viewName, tp) => {
    setPendingTimepoint(tp || 'Baseline');
    setView(viewName);
  };
  const viewStudentProfile = async (student) => {
    setCurrentStudent(student);
    // Reset any in-progress screening state so the next student never inherits previous answers.
    setFormData(INITIAL_FORM_DATA);
    setCurrentSection(1);
    setFormLocked(false);
    setL1ReadingExamplesShown(3);
    setL2ReadingExamplesShown(3);
    setL3ReadingExamplesShown(3);
    setCurrentScreening(null);
    setScreeningReviewByTimepoint({});
    setScreeningReviewError('');
    setShowReturnForCorrection(false);
    setReturnForCorrectionNote('');
    setReturnForCorrectionScreenerKey('');
    setReviewCorrectionMode('return');
    setShowScreeningReviewEvidence(false);
    setScreeningReviewEvidence(null);
    setLoading(true);
    setLoadingMessage('Loading student data...');
    setError('');

    try {
      const passportResponse = await fetchWithTimeout(`${API_URL}/api/passport/${student.student_code}`);
      const passportResult = await passportResponse.json();
      
      if (passportResponse.ok && passportResult.passport) {
        setPassportData(passportResult.passport);
        setPassportSaved(true);
      } else {
        setPassportData({});
        setPassportSaved(false);
      }

      const screeningResponse = await fetchWithTimeout(`${API_URL}/api/screening/${student.student_code}`);
      const screeningResult = await screeningResponse.json();
      
      if (screeningResponse.ok) {
        setScreeningData(screeningResult.screenings || []);
      } else {
        setScreeningData([]);
      }

      // Load the review state first. This also performs the one-time repair for
      // the earlier correction-flow bug before we read completed sessions.
      try {
        const reviewFlowResponse = await fetchWithTimeout(`${API_URL}/api/screening-review/${student.student_code}`);
        const reviewFlowResult = await reviewFlowResponse.json();
        setScreeningReviewByTimepoint(reviewFlowResponse.ok ? (reviewFlowResult.timepoints || {}) : {});
      } catch (e) {
        setScreeningReviewByTimepoint({});
      }

      // Read completed sessions once, after any repair. Never overwrite a successful
      // load with a second empty response.
      try {
        const sessionsResponse = await fetchWithTimeout(`${API_URL}/api/sessions/${student.student_code}`);
        const sessionsResult = await sessionsResponse.json();
        if (!sessionsResponse.ok) throw new Error(sessionsResult.error || 'Could not load completed screening records');
        setSessionData(sessionsResult.sessions || []);
      } catch (e) {
        throw new Error('Could not load saved screening status. Please refresh and try again.');
      }

      try {
        const reviewResponse = await fetchWithTimeout(`${API_URL}/api/students/${student.student_code}/review-schedule`);
        const reviewResult = await reviewResponse.json();
        setReviewSchedule(reviewResponse.ok ? (reviewResult.schedule || {}) : {});
      } catch (e) {
        setReviewSchedule({});
      }
      setReviewScheduleStatus('');

      setView('student-profile');
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('The server took too long to respond. Please try again.');
      } else {
        setError('Failed to load student data');
      }
      setScreeningData([]);
      setPassportData({});
      setPassportSaved(false);
      setScreeningReviewByTimepoint({});
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleReviewDateChange = async (key, value, effectiveSchedule) => {
    if (!currentStudent?.student_code) return;
    const next = { ...effectiveSchedule, [key]: value };
    setReviewSchedule(next);
    setReviewScheduleSaving(true);
    setReviewScheduleStatus('');
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/students/${currentStudent.student_code}/review-schedule`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not save review dates');
      setReviewSchedule(data.schedule || next);
      setReviewScheduleStatus('Saved');
    } catch (err) {
      setReviewScheduleStatus(err.message || 'Could not save');
    } finally {
      setReviewScheduleSaving(false);
    }
  };

  const refreshScreeningReviewFlow = async () => {
    if (!currentStudent?.student_code) return;
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/screening-review/${currentStudent.student_code}`);
      const data = await response.json();
      if (response.ok) setScreeningReviewByTimepoint(data.timepoints || {});
    } catch (err) {
      // Non-blocking. Submission/approval calls still validate on the server.
    }
  };

  const handleAdministrationStatusChange = async (screenerKey, timepoint, status) => {
    if (!currentStudent?.student_code) return;
    setScreeningReviewBusy(true);
    setScreeningReviewError('');
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/screening-review/${currentStudent.student_code}/screener`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ screener_key: screenerKey, timepoint, status }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not update screener status');
      setScreeningReviewByTimepoint((prev) => ({ ...prev, [timepoint]: data }));
    } catch (err) {
      setScreeningReviewError(err.message || 'Could not update screener status');
    } finally {
      setScreeningReviewBusy(false);
    }
  };

  const handleReopenScreenerForCorrection = async (screenerKey, timepoint) => {
    if (!currentStudent?.student_code) return;
    setScreeningReviewBusy(true);
    setScreeningReviewError('');
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/screening-review/${currentStudent.student_code}/reopen-screener`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ screener_key: screenerKey, timepoint: timepoint || 'Baseline' }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not open this screener for correction');
      setScreeningReviewByTimepoint((prev) => ({ ...prev, [timepoint || 'Baseline']: data }));
      const sessionsResponse = await fetchWithTimeout(`${API_URL}/api/sessions/${currentStudent.student_code}`);
      const sessionsResult = await sessionsResponse.json();
      if (sessionsResponse.ok) setSessionData(sessionsResult.sessions || []);
    } catch (err) {
      setScreeningReviewError(err.message || 'Could not open this screener for correction');
    } finally {
      setScreeningReviewBusy(false);
    }
  };

  const handleSubmitScreeningReview = async () => {
    if (!currentStudent?.student_code) return;
    setScreeningReviewBusy(true);
    setScreeningReviewError('');
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/screening-review/${currentStudent.student_code}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timepoint: 'Baseline' }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not submit screening for review');
      setScreeningReviewByTimepoint((prev) => ({ ...prev, Baseline: data }));
      setShowReturnForCorrection(false);
      setReturnForCorrectionNote('');
      setReturnForCorrectionScreenerKey('');
      setReviewCorrectionMode('return');
      setShowScreeningReviewEvidence(false);
      setScreeningReviewEvidence(null);
      await refreshStage23AfterScreeningSave();
    } catch (err) {
      setScreeningReviewError(err.message || 'Could not submit screening for review');
    } finally {
      setScreeningReviewBusy(false);
    }
  };

  const handleScreeningReviewDecision = async (action) => {
    if (!currentStudent?.student_code) return;
    setScreeningReviewBusy(true);
    setScreeningReviewError('');
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/screening-review/${currentStudent.student_code}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timepoint: 'Baseline',
          action,
          note: action === 'return' || action === 'reopen' ? returnForCorrectionNote : '',
          screener_key: action === 'return' || action === 'reopen' ? returnForCorrectionScreenerKey : '',
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not update screening review');
      setScreeningReviewByTimepoint((prev) => ({ ...prev, Baseline: data }));
      setShowReturnForCorrection(false);
      setReturnForCorrectionNote('');
      setReturnForCorrectionScreenerKey('');
      setReviewCorrectionMode('return');
      setShowScreeningReviewEvidence(false);
      setScreeningReviewEvidence(null);
      await refreshStage23AfterScreeningSave();
    } catch (err) {
      setScreeningReviewError(err.message || 'Could not update screening review');
    } finally {
      setScreeningReviewBusy(false);
    }
  };

  const loadScreeningReviewEvidence = async () => {
    if (!currentStudent?.student_code) return;
    if (showScreeningReviewEvidence) {
      setShowScreeningReviewEvidence(false);
      return;
    }
    setScreeningReviewEvidenceLoading(true);
    setScreeningReviewError('');
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/screening-review/${currentStudent.student_code}/evidence?timepoint=Baseline`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not load screening evidence');
      setScreeningReviewEvidence(data);
      setShowScreeningReviewEvidence(true);
    } catch (err) {
      setScreeningReviewError(err.message || 'Could not load screening evidence');
    } finally {
      setScreeningReviewEvidenceLoading(false);
    }
  };

  const openLearningPlans = (student) => {
    setCurrentStudent(student);
    setView('learning-plans');
  };

  const refreshStage23AfterScreeningSave = async () => {
    if (!currentStudent?.student_code) return;
    const code = currentStudent.student_code;

    // Keep the student profile timeline current. This is intentionally independent
    // from plan generation so a downstream planning problem cannot hide a saved screen.
    fetchWithTimeout(`${API_URL}/api/sessions/${code}`)
      .then((r) => r.json())
      .then((d) => setSessionData(d.sessions || []))
      .catch(() => {});

    await refreshScreeningReviewFlow();

    try {
      const response = await fetchWithTimeout(`${API_URL}/api/learning-plans/readiness/${code}`);
      const readiness = await response.json();
      if (!response.ok) return;
      const patch = {
        learning_plans_ready: Boolean(readiness.learning_plans_ready),
        learning_plans_missing: readiness.learning_plans_missing || [],
        learning_plan_state: readiness.learning_plan_state || '',
        learning_plan_release_status: readiness.learning_plan_release_status || '',
        learning_plan_draft_revision: readiness.learning_plan_draft_revision || 0,
        learning_plans_visible: Boolean(readiness.learning_plans_visible),
        learning_plans_can_generate_draft: Boolean(readiness.learning_plans_can_generate_draft),
        learning_plans_can_regenerate_draft: Boolean(readiness.learning_plans_can_regenerate_draft),
        learning_plans_can_activate: Boolean(readiness.learning_plans_can_activate),
        reports_available: false,
      };
      setCurrentStudent((prev) => prev && prev.student_code === code ? { ...prev, ...patch } : prev);
      setStudents((prev) => prev.map((student) => student.student_code === code ? { ...student, ...patch } : student));
      // A report menu opened before the final screener may now be stale.
      setReportAvailability((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
    } catch (err) {
      // Non-blocking. A later dashboard refresh will obtain the same server state.
    }
  };

  const returnToStudentScreeningDashboard = async () => {
    if (!currentStudent?.student_code) {
      setView('student-profile');
      return;
    }
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/sessions/${currentStudent.student_code}`);
      const data = await response.json();
      if (response.ok) setSessionData(data.sessions || []);
    } catch (err) {
      // The dashboard can still open; a later refresh will retry.
    }
    await refreshScreeningReviewFlow();
    setView('student-profile');
  };

  const toggleReports = async (student) => {
    const code = student.student_code;
    if (reportMenuStudentCode === code) {
      setReportMenuStudentCode('');
      return;
    }
    setReportMenuStudentCode(code);
    if (reportAvailability[code]) return;

    setReportLoadingStudentCode(code);
    try {
      const response = await fetchWithTimeout(`${API_URL}/api/reports/${code}/availability`);
      const data = await response.json();
      if (response.ok) {
        setReportAvailability((prev) => ({ ...prev, [code]: data }));
      } else {
        setReportAvailability((prev) => ({ ...prev, [code]: { error: data.error || 'Could not load report options.' } }));
      }
    } catch (err) {
      setReportAvailability((prev) => ({ ...prev, [code]: { error: 'Connection error while loading report options.' } }));
    } finally {
      setReportLoadingStudentCode('');
    }
  };

  // Save passport
  const savePassport = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoadingMessage('Saving passport...');
    setError('');

    const passportFormData = {
      student_code: currentStudent.student_code,
      strengths: passportData.strengths || '',
      looks_forward: passportData.looks_forward || '',
      dislikes: passportData.dislikes || '',
      concerns: passportData.concerns || '',
      difficulties: passportData.difficulties || '',
      help_needed: passportData.help_needed || '',
      teacher_help_1: passportData.teacher_help_1 || '',
      teacher_help_2: passportData.teacher_help_2 || '',
      teacher_help_3: passportData.teacher_help_3 || ''
    };

    try {
      const response = await fetchWithTimeout(`${API_URL}/api/passport/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(passportFormData)
      });

      const result = await response.json();

      if (response.ok) {
        setFormLocked(true);
        setPassportSaved(true);
        const updatedStudents = students.map(s => 
          s.student_code === currentStudent.student_code 
            ? { ...s, has_passport: 'Yes' } 
            : s
        );
        setStudents(updatedStudents);
        await refreshStage23AfterScreeningSave();
      } else {
        setError(result.error || 'Failed to save passport');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('The server took too long to respond. Your data may have been saved - please go back and check before re-submitting.');
      } else {
        setError('Connection error. Please try again.');
      }
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Save screening
  const saveScreening = async () => {
    setLoading(true);
    setLoadingMessage('Saving screening data...');
    setError('');

    const screeningPayload = {
      student_code: currentStudent.student_code,
      facilitator_id: facilitatorId,
      l1ErrorPercentage: errorPercentageL1,
      l2ErrorPercentage: errorPercentageL2,
      l3ErrorPercentage: errorPercentageL3,
    ...formData
};

    try {
      const response = await fetchWithTimeout(`${API_URL}/api/screening/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(screeningPayload)
      });

      const result = await response.json();

      if (response.ok) {
        setFormLocked(true);
        
        // Update students list immediately
        const updatedStudents = students.map(s => {
          if (s.student_code === currentStudent.student_code) {
            if (formData.screeningType === 'Baseline') {
              return { ...s, has_pre_screen: 'Yes' };
            } else {
              return { ...s, has_post_screen: 'Yes' };
            }
          }
          return s;
        });
        setStudents(updatedStudents);
        
        // Create the new screening object with current date
        const newScreening = {
          ...formData,
          student_code: currentStudent.student_code,
          screening_date: new Date().toLocaleDateString('en-GB'),
          screening_type: formData.screeningType
        };
        
        // Update screeningData immediately
        setScreeningData(prev => [...prev, newScreening]);
        setCurrentScreening(newScreening);
        
        alert('Screening saved successfully!');
        
        // Go directly to results view
        setView('screening-results');
      } else {
        setError(result.error || 'Failed to save screening');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('The server took too long to respond. Your data may have been saved - please go back and check before re-submitting.');
      } else {
        setError('Connection error. Please try again.');
      }
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Form navigation
  const nextSection = () => {
    if (validateCurrentSection()) {
      setCurrentSection(prev => {
        // SDQ, Writing, Mathematics, Neurodiversity, Learning Style and
        // Confidence Levels now live on their own dashboard cards, so the
        // legacy Baseline/Post wizard steps past those sections instead of
        // collecting duplicate data.
        let next = prev + 1;
        if (next === 2 || next === 3) next = 4;
        if (next === 5 || next === 6) next = 7;
        if (next === 10 || next === 11) next = 12;
        return Math.min(next, 12);
      });
      setError('');
    }
  };

  const previousSection = () => {
    setCurrentSection(prev => {
      let back = prev - 1;
      if (back === 11 || back === 10) back = 9;
      if (back === 6 || back === 5) back = 4;
      if (back === 3 || back === 2) back = 1;
      return Math.max(back, 1);
    });
    setError('');
  };

  // Validation
  const validateCurrentSection = () => {
  const requiredFields = getRequiredFieldsForSection(currentSection);

  // Allow Passage 3 to be skipped when earlier data already gives enough information
  if (currentSection === 9 && !hasAnyL3Data && canSkipPassage3) {
    return true;
  }

  // ── SECTION 7 (Passage 1): encourage recording errors but no hard block ──
  // The facilitator is just starting the reading assessment. They'll see the
  // running total counter and on-screen guidance. No minimum enforced here
  // because they still have Passage 2 ahead.

  // ── SECTION 8 (Passage 2): enforce minimum errors when ceiling is reached ──
  // When the reading assessment will end after this passage (ceiling found),
  // we check the TOTAL errors across Passages 1 + 2 right here - rather than
  // waiting for Section 9, which the facilitator will skip.
  // We also require at least 1 error recorded from EACH passage read,
  // so the facilitator can't load all errors onto one passage.
  if (currentSection === 8 && canSkipPassage3) {
    const l1ErrorCount = countReadingErrorRowsForLevel(formData, 'l1');
    const l2ErrorCount = countReadingErrorRowsForLevel(formData, 'l2');
    const totalErrors = l1ErrorCount + l2ErrorCount;

    const noErrorsOverrideText = 'no errors observed';
    const anyNotes = [
      formData.proseReadingAdditionalNotes,
      formData.l2ProseReadingAdditionalNotes
    ].filter(Boolean).join(' ').toLowerCase();
    const hasNoErrorsOverride = totalErrors === 0 && anyNotes.includes(noErrorsOverrideText);

    if (!hasNoErrorsOverride) {
      if (l1ErrorCount < 1) {
        setError(
          'Please go back to Passage 1 and record at least 1 reading error example. Errors from every passage read are important.'
        );
        return false;
      }
      if (l2ErrorCount < 1) {
        setError(
          'Please record at least 1 reading error example from this passage. Errors from every passage read are important.'
        );
        return false;
      }
      if (totalErrors < 5) {
        setError(
          `Please record at least 5 reading errors across both passages (currently ${totalErrors}). You can add more errors to either Passage 1 (go back) or Passage 2 (below). Record as many as you can.`
        );
        return false;
      }
    }
  }

  // ── SECTION 9 (Passage 3): BLOCK if ceiling already found ──
  // If Passage 2 error rate >20%, the ceiling has been reached and the
  // facilitator should NOT complete Passage 3. Only allow Passage 3
  // when Passage 2 error rate is <20% (meaning the child coped well
  // and we need to test them at their expected level).
  if (currentSection === 9 && !canSkipPassage3) {
    // canSkipPassage3 is false only when both P1 and P2 are <20%,
    // meaning Passage 3 is genuinely required - this is correct.
  }
  if (currentSection === 9 && canSkipPassage3 && hasAnyL3Data) {
    // Facilitator entered L3 data even though ceiling was already found.
    // This shouldn't happen with the UI blocking, but just in case.
  }

  for (const field of requiredFields) {
    if (!formData[field] || formData[field] === '') {
      setError('Please fill in all required fields in this section');
      return false;
    }
  }

  // Reading errors quality check across passages administered
  if (currentSection === 9) {
    const totalReadingErrorsRecorded = countReadingErrorRows(formData);
    const noErrorsOverrideText = 'no errors observed';
    const anyNotes = [
      formData.proseReadingAdditionalNotes,
      formData.l2ProseReadingAdditionalNotes,
      formData.l3ProseReadingAdditionalNotes
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    const hasNoErrorsOverride =
      totalReadingErrorsRecorded === 0 && anyNotes.includes(noErrorsOverrideText);

    if (totalReadingErrorsRecorded < 5 && !hasNoErrorsOverride) {
      setError(
        'Please record at least 5 reading errors across passages administered. If there are genuinely no errors, write "No errors observed" in an Additional notes box.'
      );
      return false;
    }
  }

  return true;
}; 

  // Count reading error rows for a SPECIFIC level (l1, l2, or l3).
  // Used by Section 8 validation to ensure minimum errors are recorded
  // on the passage where the ceiling was reached.
  const countReadingErrorRowsForLevel = (data, level) => {
    const hasRow = (correct, readAs, errorType) => {
      const c = (data[correct] || '').toString().trim();
      const r = (data[readAs] || '').toString().trim();
      const t = (data[errorType] || '').toString().trim();
      return c !== '' && r !== '' && t !== '';
    };

    let count = 0;
    const prefix = level === 'l1' ? 'proseReadingExample' : `${level}ProseReadingExample`;
    for (let n = 1; n <= 5; n++) {
      if (hasRow(`${prefix}${n}Correct`, `${prefix}${n}ReadAs`, `${prefix}${n}ErrorType`)) count++;
    }
    return count;
  };

  // Count "useful" reading error rows across Levels 1-3.
  // A row counts as 1 when it has: Correct + Read as + Error type.
  const countReadingErrorRows = (data) => {
    const hasRow = (correct, readAs, errorType) => {
      const c = (data[correct] || '').toString().trim();
      const r = (data[readAs] || '').toString().trim();
      const t = (data[errorType] || '').toString().trim();
      return c !== '' && r !== '' && t !== '';
    };

    let count = 0;

    // Level 1
    for (let n = 1; n <= 5; n++) {
      if (hasRow(`proseReadingExample${n}Correct`, `proseReadingExample${n}ReadAs`, `proseReadingExample${n}ErrorType`)) count++;
    }

    // Level 2
    for (let n = 1; n <= 5; n++) {
      if (hasRow(`l2ProseReadingExample${n}Correct`, `l2ProseReadingExample${n}ReadAs`, `l2ProseReadingExample${n}ErrorType`)) count++;
    }

    // Level 3
    for (let n = 1; n <= 5; n++) {
      if (hasRow(`l3ProseReadingExample${n}Correct`, `l3ProseReadingExample${n}ReadAs`, `l3ProseReadingExample${n}ErrorType`)) count++;
    }

    return count;
  };

  const getRequiredFieldsForSection = (section) => {
    const fieldMap = {
      1: ['screeningType'],
      2: [], // SDQ moved to its own dashboard card
      3: [], // Writing Assessment moved to its own dashboard card
      4: [], // Single word spelling is now optional
      5: ['additionScore', 'subtractionScore', 'multiplicationScore', 'divisionScore'],
      6: [], // Neurodiversity Checklist moved to its own dashboard card
      7: ['readingLanguage', 'readingSpeed', 'totalWordsRead', 'readingErrors', 'readingComprehensionL1'],
      8: ['l2ReadingSpeed', 'l2TotalWordsRead', 'l2ReadingErrors', 'l2ReadingComprehensionL2'],
      9: ['l3ReadingSpeed', 'l3TotalWordsRead', 'l3ReadingErrors', 'l3ReadingComprehensionL3'],
      10: [], // Learning Style moved to its own dashboard card
      11: [], // Confidence Levels moved to its own dashboard card, section kept empty (see nextSection/previousSection, which already skip past it)
      12: []
    };
    
    return fieldMap[section] || [];
  };

  // Get badge style
  const getBadgeStyle = (student) => {
    if (!student.has_passport || student.has_passport === 'No') {
      return { color: '#DC2626', text: 'Need Passport', bg: '#FEE2E2' };
    } else if (student.has_passport === 'Yes' && (!student.has_pre_screen || student.has_pre_screen === 'No')) {
      return { color: '#EA580C', text: 'Ready for Pre-Screen', bg: '#FFEDD5' };
    } else if (student.has_pre_screen === 'Yes' && (!student.has_post_screen || student.has_post_screen === 'No')) {
      return { color: '#CA8A04', text: 'Ready for Post-Screen', bg: '#FEF3C7' };
    } else {
      return { color: '#16A34A', text: 'All Complete', bg: '#DCFCE7' };
    }
  };

  // Calculate age in years from DOB
  const calculateAge = (dobString, referenceDate = null) => {
  if (!dobString) return '';

  try {
    const parts = dobString.split('/');
    const dob = new Date(parts[2], parts[1] - 1, parts[0]);
    const ref = referenceDate ? new Date(referenceDate) : new Date();

    let years = ref.getFullYear() - dob.getFullYear();
    let months = ref.getMonth() - dob.getMonth();

    if (ref.getDate() < dob.getDate()) {
      months--;
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    return `${years}:${months}`;
  } catch {
    return '';
  }
};

  // Start new screening
  const startNewScreening = (type) => {
    // Start from a clean slate, but carry over what's already known about this
    // student from their own registration, so nobody has to type it twice.
    setFormData({
      ...INITIAL_FORM_DATA,
      screeningType: type,
      gender: currentStudent?.gender || '',
      attendance: currentStudent?.attendance_pct || '',
      nativeLanguage: currentStudent?.native_language || '',
      mediumOfInstruction: currentStudent?.medium_of_instruction || '',
      firstGenerationLearner: currentStudent?.first_gen_learner || '',
    });
    setCurrentSection(1);
    setFormLocked(false);
    setL1ReadingExamplesShown(3);
    setL2ReadingExamplesShown(3);
    setL3ReadingExamplesShown(3);
    setView('screening');
  };

  // Render screening sections
  const renderScreeningSection = () => {
    const sections = [
      {
        number: 1,
        title: 'Background Information',
        fields: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Screening Type */}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: COLORS.textDark, marginBottom: '8px' }}>Screening Type *</label>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                {['Baseline', '3 months', '6 months', '9 months', 'Other'].map(type => (
                  <label key={type} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input type="radio" name="screeningType" value={type} checked={formData.screeningType === type} onChange={(e) => setFormData({...formData, screeningType: e.target.value})} style={{ marginRight: '8px' }} disabled={formLocked} />
                    <span style={{ fontSize: '14px' }}>{type}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )
      },
      {
        number: 2,
        title: 'SDQ Assessment',
        fields: null // Migrated to standalone SDQ dashboard card; retained section number for legacy wizard compatibility.
      },
      { 
        number: 3,
        title: 'Writing Assessment',
        fields: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Top stats in 3-column grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              <div>
                <label style={styles.label}>Total words (10 mins) *</label>
                <input type="number" min="0" value={formData.totalWords} onChange={(e) => setFormData({...formData, totalWords: e.target.value})} style={styles.input} disabled={formLocked} />
              </div>
              <div>
                <label style={styles.label}>Legible words *</label>
                <input type="number" min="0" value={formData.legibleWords} onChange={(e) => setFormData({...formData, legibleWords: e.target.value})} style={styles.input} disabled={formLocked} />
              </div>
              <div>
                <label style={styles.label}>Illegible words *</label>
                <input type="number" min="0" value={formData.illegibleWords} onChange={(e) => setFormData({...formData, illegibleWords: e.target.value})} style={styles.input} disabled={formLocked} />
              </div>
              <div>
                <label style={styles.label}>Spelling errors *</label>
                <input type="number" min="0" value={formData.spellingErrors} onChange={(e) => setFormData({...formData, spellingErrors: e.target.value})} style={styles.input} disabled={formLocked} />
              </div>
              <div>
                <label style={styles.label}>Alphabet (letters/min) *</label>
                <input type="number" min="0" value={formData.alphabetWriting} onChange={(e) => setFormData({...formData, alphabetWriting: e.target.value})} style={styles.input} disabled={formLocked} />
              </div>
              <div>
                <label style={styles.label}>Writing legibility *</label>
                <select
                  value={formData.writingLegibility}
                  onChange={(e) => setFormData({ ...formData, writingLegibility: e.target.value })}
                  style={styles.select}
                  disabled={formLocked}
                >
                  <option value="">Select</option>
                  {WRITING_LEGIBILITY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Spelling Examples Section with subtle color */}
            <div style={{ 
              backgroundColor: '#F8FAFF', 
              padding: '20px', 
              borderRadius: '8px',
              border: '1px solid #E0E7FF'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <div style={{ fontSize: '16px', fontWeight: '600', color: COLORS.navyBlue }}>
                  Free Writing Spelling Examples (5)
                </div>
                <div style={{ fontSize: '13px', color: COLORS.textGray, marginTop: '4px' }}>
                  Enter the correct word and how the student spelt it
                </div>
                <div style={{ 
                  marginTop: '10px',
                  backgroundColor: '#FFFBEB',
                  border: '1px solid #FCD34D',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  fontSize: '12px',
                  color: COLORS.textDark,
                  lineHeight: 1.35
                }}>
                  <div style={{ fontWeight: 700, color: COLORS.navyBlue }}>
                    If the language is Kannada, please type words in English letters (Roman script).
                  </div>
                  <div style={{ marginTop: '4px', color: COLORS.textGray }}>
                    Example: <strong>Correct:</strong> mane; <strong>Read/Wrote as:</strong> mne; <strong>Error type:</strong> Omission
                  </div>
                </div>

              </div>

              {/* 3-column grid for spelling examples */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                {[1,2,3,4,5].map((n) => {
                  // Subtle alternating colors for visual separation
                  const bgColors = ['#FFFFFF', '#FFF8F0', '#FFFBF0', '#F0FFF4', '#F8FAFF'];
                  const borderColors = ['#E0E7FF', '#FFE4CC', '#FFECC7', '#CCF0DD', '#E0E7FF'];
                  
                  return (
                    <div 
                      key={n} 
                      style={{
                        backgroundColor: bgColors[(n-1) % 5],
                        padding: '14px',
                        borderRadius: '6px',
                        border: `1px solid ${borderColors[(n-1) % 5]}`
                      }}
                    >
                      <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textDark, marginBottom: '10px', textAlign: 'center' }}>
                        Example {n}
                      </div>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{...styles.smallLabel, fontSize: '11px'}}>Correct</label>
                        <input
                          type="text"
                          value={formData[`freeWritingExample${n}Correct`]}
                          onChange={(e) => setFormData({...formData, [`freeWritingExample${n}Correct`]: e.target.value.replace(/\d/g, '')})}
                          style={{...styles.input, fontSize: '13px', padding: '6px 10px'}}
                          placeholder="e.g., cat"
                          disabled={formLocked}
                          autoComplete="off"
                          name={`freeWritingExample${n}Correct-${Date.now()}`}
                          required
                        />
                      </div>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{...styles.smallLabel, fontSize: '11px'}}>Spelt as</label>
                        <input
                          type="text"
                          value={formData[`freeWritingExample${n}SpeltAs`]}
                          onChange={(e) => setFormData({...formData, [`freeWritingExample${n}SpeltAs`]: e.target.value.replace(/\d/g, '')})}
                          style={{...styles.input, fontSize: '13px', padding: '6px 10px'}}
                          placeholder="e.g., kat"
                          disabled={formLocked}
                          autoComplete="off"
                          name={`freeWritingExample${n}SpeltAs-${Date.now()}`}
                          required
                        />
                      </div>
                      
                      <div>
                        <label style={{...styles.smallLabel, fontSize: '11px'}}>Error type *</label>
                        <select
                          value={formData[`freeWritingExample${n}ErrorType`]}
                          onChange={(e) => setFormData({...formData, [`freeWritingExample${n}ErrorType`]: e.target.value})}
                          style={{...styles.select, fontSize: '12px', padding: '6px 10px'}}
                          disabled={formLocked}
                          autoComplete="off"
                        >
                          <option value="">Select type</option>
                          {(formData.mediumOfInstruction === 'Kannada' ? SPELLING_ERROR_TYPES_KANNADA : SPELLING_ERROR_TYPES_ENGLISH).map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
                
                {/* Additional notes spans full width at bottom */}
                <div style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
                  <label style={styles.label}>Additional notes</label>
                  <textarea 
                    value={formData.freeWritingAdditionalNotes} 
                    onChange={(e) => setFormData({...formData, freeWritingAdditionalNotes: e.target.value})} 
                    style={styles.textarea} 
                    rows="2" 
                    disabled={formLocked} 
                    placeholder="Any additional observations about spelling patterns..."
                  />
                </div>
              </div>
            </div>

            {/* Compact handwriting + writing behaviour capture */}
            <div style={{
              backgroundColor: '#FFFFFF',
              padding: '16px',
              borderRadius: '8px',
              border: `1px solid ${COLORS.borderGray}`
            }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: COLORS.navyBlue, marginBottom: '10px' }}>
                Free writing observations
              </div>

              {/* Physical & Behavioural Aspects */}
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: COLORS.textGray, marginBottom: '8px' }}>
                  Physical & behavioural aspects
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  <div>
                    <label style={styles.smallLabel}>Pencil grip</label>
                    <select
                      value={formData.pencilGrip}
                      onChange={(e) => setFormData({ ...formData, pencilGrip: e.target.value })}
                      style={styles.select}
                      disabled={formLocked}
                    >
                      <option value="">Select</option>
                      {[
                        'Functional tripod',
                        'Quadrupod',
                        'Thumb wrap/lateral',
                        'Immature four-grip',
                        'Fisted',
                        'Unstable/switching'
                      ].map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={styles.smallLabel}>Sitting posture</label>
                    <select
                      value={formData.sittingPosture}
                      onChange={(e) => setFormData({ ...formData, sittingPosture: e.target.value })}
                      style={styles.select}
                      disabled={formLocked}
                    >
                      <option value="">Select</option>
                      {[
                        'Upright and stable (feet flat and back supported)',
                        'Slightly slouched or leaning',
                        'Poor posture (significant leaning, unstable positioning)'
                      ].map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={styles.smallLabel}>Paper position</label>
                    <select
                      value={formData.paperPosition}
                      onChange={(e) => setFormData({ ...formData, paperPosition: e.target.value })}
                      style={styles.select}
                      disabled={formLocked}
                    >
                      <option value="">Select</option>
                      {['Slanted left', 'Slanted right', 'Upright'].map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Writing behaviour */}
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: COLORS.textGray, marginBottom: '8px' }}>
                  Writing behaviour
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  <div>
                    <label style={styles.smallLabel}>Hesitation / pausing</label>
                    <select
                      value={formData.writingHesitation}
                      onChange={(e) => setFormData({ ...formData, writingHesitation: e.target.value })}
                      style={styles.select}
                      disabled={formLocked}
                    >
                      <option value="">Select</option>
                      {['Never', 'Sometimes', 'Often'].map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={styles.smallLabel}>Words crossed out (number)</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.writingCrossedOut}
                      onChange={(e) => setFormData({ ...formData, writingCrossedOut: e.target.value })}
                      style={styles.input}
                      disabled={formLocked}
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <label style={styles.smallLabel}>Looking up/around</label>
                    <select
                      value={formData.writingLookingUp}
                      onChange={(e) => setFormData({ ...formData, writingLookingUp: e.target.value })}
                      style={styles.select}
                      disabled={formLocked}
                    >
                      <option value="">Select</option>
                      {['Never', 'Sometimes', 'Often'].map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={styles.smallLabel}>Hand fatigue</label>
                    <select
                      value={formData.writingHandFatigue}
                      onChange={(e) => setFormData({ ...formData, writingHandFatigue: e.target.value })}
                      style={styles.select}
                      disabled={formLocked}
                    >
                      <option value="">Select</option>
                      {['Never', 'Sometimes', 'Often'].map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={styles.smallLabel}>Frustration</label>
                    <select
                      value={formData.writingFrustration}
                      onChange={(e) => setFormData({ ...formData, writingFrustration: e.target.value })}
                      style={styles.select}
                      disabled={formLocked}
                    >
                      <option value="">Select</option>
                      {['Never', 'Sometimes', 'Often'].map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={styles.smallLabel}>Writing fluency</label>
                    <select
                      value={formData.writingFluency}
                      onChange={(e) => setFormData({ ...formData, writingFluency: e.target.value })}
                      style={styles.select}
                      disabled={formLocked}
                    >
                      <option value="">Select</option>
                      {[
                        'Fluent - writes continuously with natural flow',
                        'Moderate - some pauses but generally steady',
                        'Laboured - slow, effortful, frequent stops'
                      ].map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* After writing + language skills + mechanics (compact) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <div>
                  <label style={styles.smallLabel}>Ideas expressed clearly</label>
                  <select
                    value={formData.afterIdeasClearly}
                    onChange={(e) => setFormData({ ...formData, afterIdeasClearly: e.target.value })}
                    style={styles.select}
                    disabled={formLocked}
                  >
                    <option value="">Select</option>
                    {['Good', 'Below expected', 'Poor'].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.smallLabel}>Logical sequence</label>
                  <select
                    value={formData.afterLogicalSequence}
                    onChange={(e) => setFormData({ ...formData, afterLogicalSequence: e.target.value })}
                    style={styles.select}
                    disabled={formLocked}
                  >
                    <option value="">Select</option>
                    {['As expected', 'Weak'].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.smallLabel}>Vocabulary range</label>
                  <select
                    value={formData.afterVocabularyRange}
                    onChange={(e) => setFormData({ ...formData, afterVocabularyRange: e.target.value })}
                    style={styles.select}
                    disabled={formLocked}
                  >
                    <option value="">Select</option>
                    {['Excellent', 'Good', 'Average', 'Weak'].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.smallLabel}>Planning for writing</label>
                  <select
                    value={formData.languagePlanning}
                    onChange={(e) => setFormData({ ...formData, languagePlanning: e.target.value })}
                    style={styles.select}
                    disabled={formLocked}
                  >
                    <option value="">Select</option>
                    {['Strong', 'Good', 'Needs support'].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.smallLabel}>Grammar usage</label>
                  <select
                    value={formData.languageGrammar}
                    onChange={(e) => setFormData({ ...formData, languageGrammar: e.target.value })}
                    style={styles.select}
                    disabled={formLocked}
                  >
                    <option value="">Select</option>
                    {['Strong', 'Good', 'Needs support'].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.smallLabel}>Sentence structure</label>
                  <select
                    value={formData.languageSentenceStructure}
                    onChange={(e) => setFormData({ ...formData, languageSentenceStructure: e.target.value })}
                    style={styles.select}
                    disabled={formLocked}
                  >
                    <option value="">Select</option>
                    {['Strong', 'Good', 'Needs support'].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.smallLabel}>Capitalisation</label>
                  <select
                    value={formData.languageCapitalisation}
                    onChange={(e) => setFormData({ ...formData, languageCapitalisation: e.target.value })}
                    style={styles.select}
                    disabled={formLocked}
                  >
                    <option value="">Select</option>
                    {['Excellent', 'Good', 'Needs support'].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.smallLabel}>Letter formation</label>
                  <select
                    value={formData.mechanicsLetterFormation}
                    onChange={(e) => setFormData({ ...formData, mechanicsLetterFormation: e.target.value })}
                    style={styles.select}
                    disabled={formLocked}
                  >
                    <option value="">Select</option>
                    {['Strong', 'Developing', 'Needs support'].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.smallLabel}>Size consistency</label>
                  <select
                    value={formData.mechanicsSizeConsistency}
                    onChange={(e) => setFormData({ ...formData, mechanicsSizeConsistency: e.target.value })}
                    style={styles.select}
                    disabled={formLocked}
                  >
                    <option value="">Select</option>
                    {['Strong', 'Developing', 'Needs support'].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.smallLabel}>Spacing between words</label>
                  <select
                    value={formData.mechanicsSpacingBetweenWords}
                    onChange={(e) => setFormData({ ...formData, mechanicsSpacingBetweenWords: e.target.value })}
                    style={styles.select}
                    disabled={formLocked}
                  >
                    <option value="">Select</option>
                    {['Good', 'Inconsistent', 'Needs support'].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.smallLabel}>Line alignment</label>
                  <select
                    value={formData.mechanicsLineAlignment}
                    onChange={(e) => setFormData({ ...formData, mechanicsLineAlignment: e.target.value })}
                    style={styles.select}
                    disabled={formLocked}
                  >
                    <option value="">Select</option>
                    {['Good', 'Inconsistent', 'Needs support'].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )
      },
      { 
        number: 4,
        title: 'Single Word Spelling (Optional)',
        fields: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Score field - prominent at top */}
            <div style={{ maxWidth: '400px', margin: '0 auto', width: '100%' }}>
              <label style={{ display: 'block', fontSize: '15px', fontWeight: '600', color: COLORS.navyBlue, marginBottom: '6px', textAlign: 'center' }}>
                Spelling Test Score (optional)
              </label>
              <input 
                type="number" 
                min="0" 
                value={formData.spellingTestScore} 
                onChange={(e) => setFormData({...formData, spellingTestScore: e.target.value})} 
                style={{...styles.input, fontSize: '16px', textAlign: 'center', padding: '12px'}} 
                disabled={formLocked}
                placeholder="Enter score"
                autoComplete="off"
              />
            </div>

            {/* Spelling Examples Section with subtle color */}
            <div style={{ 
              backgroundColor: '#FFF8F0', 
              padding: '20px', 
              borderRadius: '8px',
              border: '1px solid #FFE4CC'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <div style={{ fontSize: '16px', fontWeight: '600', color: COLORS.navyBlue }}>
                  Single Word Spelling Examples (3)
                </div>
                <div style={{ fontSize: '13px', color: COLORS.textGray, marginTop: '4px' }}>
                  Enter the correct word and how the student spelt it
                </div>
                <div style={{ 
                  marginTop: '10px',
                  backgroundColor: '#FFFBEB',
                  border: '1px solid #FCD34D',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  fontSize: '12px',
                  color: COLORS.textDark,
                  lineHeight: 1.35
                }}>
                  <div style={{ fontWeight: 700, color: COLORS.navyBlue }}>
                    If the language is Kannada, please type words in English letters (Roman script).
                  </div>
                  <div style={{ marginTop: '4px', color: COLORS.textGray }}>
                    Example: <strong>Correct:</strong> mane; <strong>Read/Wrote as:</strong> mne; <strong>Error type:</strong> Omission
                  </div>
                </div>

              </div>

              {/* 3-column grid for spelling examples */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                {[1,2,3].map((n) => {
                  // Subtle alternating colors for visual separation
                  const bgColors = ['#FFFFFF', '#FFF8F0', '#FFFBF0'];
                  const borderColors = ['#E0E7FF', '#FFE4CC', '#FFECC7'];
                  
                  return (
                    <div 
                      key={n} 
                      style={{
                        backgroundColor: bgColors[(n-1) % 3],
                        padding: '14px',
                        borderRadius: '6px',
                        border: `1px solid ${borderColors[(n-1) % 3]}`
                      }}
                    >
                      <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textDark, marginBottom: '10px', textAlign: 'center' }}>
                        Example {n}
                      </div>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{...styles.smallLabel, fontSize: '11px'}}>Correct</label>
                        <input
                          type="text"
                          value={formData[`singleWordSpellingExample${n}Correct`]}
                          onChange={(e) => setFormData({...formData, [`singleWordSpellingExample${n}Correct`]: e.target.value.replace(/\d/g, '')})}
                          style={{...styles.input, fontSize: '13px', padding: '6px 10px'}}
                          placeholder="e.g., elephant"
                          disabled={formLocked}
                          autoComplete="off"
                          name={`singleWordSpellingExample${n}Correct-${Date.now()}`}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{...styles.smallLabel, fontSize: '11px'}}>Spelt as</label>
                        <input
                          type="text"
                          value={formData[`singleWordSpellingExample${n}SpeltAs`]}
                          onChange={(e) => setFormData({...formData, [`singleWordSpellingExample${n}SpeltAs`]: e.target.value.replace(/\d/g, '')})}
                          style={{...styles.input, fontSize: '13px', padding: '6px 10px'}}
                          placeholder="e.g., elefant"
                          disabled={formLocked}
                          autoComplete="off"
                          name={`singleWordSpellingExample${n}SpeltAs-${Date.now()}`}
                        />
                      </div>
                      
                      <div>
                        <label style={{...styles.smallLabel, fontSize: '11px'}}>Error type *</label>
                        <select
                          value={formData[`singleWordSpellingExample${n}ErrorType`]}
                          onChange={(e) => setFormData({...formData, [`singleWordSpellingExample${n}ErrorType`]: e.target.value})}
                          style={{...styles.select, fontSize: '12px', padding: '6px 10px'}}
                          disabled={formLocked}
                          autoComplete="off"
                        >
                          <option value="">Select type</option>
                          {(formData.mediumOfInstruction === 'Kannada' ? SPELLING_ERROR_TYPES_KANNADA : SPELLING_ERROR_TYPES_ENGLISH).map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
                
                {/* Additional notes spans full width at bottom */}
                <div style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
                  <label style={styles.label}>Additional notes</label>
                  <textarea 
                    value={formData.singleWordSpellingAdditionalNotes} 
                    onChange={(e) => setFormData({...formData, singleWordSpellingAdditionalNotes: e.target.value})} 
                    style={styles.textarea} 
                    rows="2" 
                    disabled={formLocked}
                    placeholder="Any additional observations about spelling patterns..."
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>
          </div>
        )
      },
      {
        number: 5,
        title: 'Mathematics',
        fields: (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '800px', margin: '0 auto' }}>
            {/* Addition */}
            <div style={{ 
              backgroundColor: '#F0F9FF', 
              padding: '20px', 
              borderRadius: '8px',
              border: '1px solid #BAE6FD'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ 
                  fontSize: '32px', 
                  fontWeight: 'bold', 
                  color: '#0284C7',
                  backgroundColor: '#E0F2FE',
                  width: '50px',
                  height: '50px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  +
                </div>
                <label style={{ fontSize: '15px', fontWeight: '600', color: COLORS.navyBlue }}>
                  Addition Score *
                </label>
              </div>
              <input 
                type="number" 
                min="0" 
                max="40"
                value={formData.additionScore} 
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (Number(value) >= 0 && Number(value) <= 40)) {
                    setFormData({...formData, additionScore: value});
                  }
                }} 
                style={{...styles.input, fontSize: '15px', padding: '10px'}} 
                disabled={formLocked}
                placeholder="Enter score"
                autoComplete="off"
                name={`math-addition-${Date.now()}`}
              />
            </div>

            {/* Subtraction */}
            <div style={{ 
              backgroundColor: '#FFF7ED', 
              padding: '20px', 
              borderRadius: '8px',
              border: '1px solid #FDBA74'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ 
                  fontSize: '32px', 
                  fontWeight: 'bold', 
                  color: '#EA580C',
                  backgroundColor: '#FFEDD5',
                  width: '50px',
                  height: '50px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  −
                </div>
                <label style={{ fontSize: '15px', fontWeight: '600', color: COLORS.navyBlue }}>
                  Subtraction Score *
                </label>
              </div>
              <input 
                type="number" 
                min="0" 
                max="40"
                value={formData.subtractionScore} 
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (Number(value) >= 0 && Number(value) <= 40)) {
                    setFormData({...formData, subtractionScore: value});
                  }
                }} 
                style={{...styles.input, fontSize: '15px', padding: '10px'}} 
                disabled={formLocked}
                placeholder="Enter score"
                autoComplete="off"
                name={`math-subtraction-${Date.now()}`}
              />
            </div>

            {/* Multiplication */}
            <div style={{ 
              backgroundColor: '#F0FDF4', 
              padding: '20px', 
              borderRadius: '8px',
              border: '1px solid #86EFAC'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ 
                  fontSize: '32px', 
                  fontWeight: 'bold', 
                  color: '#16A34A',
                  backgroundColor: '#DCFCE7',
                  width: '50px',
                  height: '50px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  ×
                </div>
                <label style={{ fontSize: '15px', fontWeight: '600', color: COLORS.navyBlue }}>
                  Multiplication Score *
                </label>
              </div>
              <input 
                type="number" 
                min="0" 
                max="40"
                value={formData.multiplicationScore} 
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (Number(value) >= 0 && Number(value) <= 40)) {
                    setFormData({...formData, multiplicationScore: value});
                  }
                }} 
                style={{...styles.input, fontSize: '15px', padding: '10px'}} 
                disabled={formLocked}
                placeholder="Enter score"
                autoComplete="off"
                name={`math-multiplication-${Date.now()}`}
              />
            </div>

            {/* Division */}
            <div style={{ 
              backgroundColor: '#FEF3C7', 
              padding: '20px', 
              borderRadius: '8px',
              border: '1px solid #FCD34D'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ 
                  fontSize: '32px', 
                  fontWeight: 'bold', 
                  color: '#CA8A04',
                  backgroundColor: '#FEF9C3',
                  width: '50px',
                  height: '50px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  ÷
                </div>
                <label style={{ fontSize: '15px', fontWeight: '600', color: COLORS.navyBlue }}>
                  Division Score *
                </label>
              </div>
              <input 
                type="number" 
                min="0" 
                max="40"
                value={formData.divisionScore} 
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (Number(value) >= 0 && Number(value) <= 40)) {
                    setFormData({...formData, divisionScore: value});
                  }
                }} 
                style={{...styles.input, fontSize: '15px', padding: '10px'}} 
                disabled={formLocked}
                placeholder="Enter score"
                autoComplete="off"
                name={`math-division-${Date.now()}`}
              />
            </div>
          </div>
        )
      },
      {
        number: 6,
        title: 'Neurodiversity Screening',
        fields: null // Migrated to standalone Neurodiversity dashboard card; retained section number for legacy wizard compatibility.
      },
      { 
        number: 7,
        title: 'Reading Assessment: Passage 1',
        fields: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* SIMPLE INSTRUCTIONS (Passage 1) */}
            <div
              style={{
                backgroundColor: '#EFF6FF',
                border: '1px solid #93C5FD',
                borderRadius: '10px',
                padding: '14px 16px',
                color: COLORS.textDark,
              }}
            >
              <div style={{ fontWeight: 800, color: COLORS.navyBlue, fontSize: '15px', marginBottom: '10px' }}>
                📖 Passage 1 - Starting Point (2 levels below expected)
              </div>
              <div style={{ fontSize: '14px', lineHeight: 1.7 }}>
                Ask the child to read the passage. Record the details below, then check the <strong>error rate</strong>.<br /><br />
                ✅ <strong>Less than 20% errors</strong> - the child coped well. On the next screen, they will read a <strong>harder</strong> passage.<br />
                ❌ <strong>More than 20% errors</strong> - the child found it difficult. On the next screen, they will read an <strong>easier</strong> passage.
              </div>
              <div style={{ marginTop: '10px', fontSize: '12px', color: COLORS.textGray, lineHeight: 1.5 }}>
                <div style={{ fontWeight: 700, color: COLORS.textDark }}>
                  Reading errors recorded: <span style={{ color: countReadingErrorRows(formData) >= 5 ? '#16A34A' : '#DC2626' }}>{countReadingErrorRows(formData)}</span> / 5 minimum across all passages.
                </div>
                <div style={{ marginTop: '4px' }}>If there are no errors, write <strong>‘No errors observed’</strong> in the Additional notes box.</div>
              </div>
            </div>

            {/* Top stats in 3-column grid */}
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
  <div>
    <label style={styles.label}>Language *</label>
    <select
      value={formData.readingLanguage}
      onChange={(e) => setFormData({ ...formData, readingLanguage: e.target.value })}
      style={styles.select}
      disabled={formLocked}
    >
      <option value="">Select Language</option>
      <option value="English">English</option>
      <option value="Kannada">Kannada</option>
      <option value="Hindi">Hindi</option>
      <option value="Tamil">Tamil</option>
      <option value="Urdu">Urdu</option>
      <option value="Malayalam">Malayalam</option>
    </select>
  </div>

  <div>
    <label style={styles.label}>Time (seconds) *</label>
    <input
      type="number"
      min="0"
      value={formData.readingSpeed}
      onChange={(e) => setFormData({ ...formData, readingSpeed: e.target.value })}
      style={styles.input}
      disabled={formLocked}
      autoComplete="off"
    />
  </div>

  <div>
    <label style={styles.label}>Words read *</label>
    <input
      type="number"
      min="0"
      value={formData.totalWordsRead}
      onChange={(e) => setFormData({ ...formData, totalWordsRead: e.target.value })}
      style={styles.input}
      disabled={formLocked}
      autoComplete="off"
    />
  </div>

  <div>
    <label style={styles.label}>Reading errors *</label>
    <input
      type="number"
      min="0"
      value={formData.readingErrors}
      onChange={(e) => setFormData({ ...formData, readingErrors: e.target.value })}
      style={styles.input}
      disabled={formLocked}
      autoComplete="off"
    />
    <div style={{ fontSize: '11px', color: COLORS.textGray, marginTop: '4px' }}>
      Missing words, lines, letter/sound omissions
      <div
        style={{
       fontSize: '14px',
       fontWeight: 'bold',
      marginTop: '6px',
      color:
        errorPercentageL1 === ''
          ? 'black'
          : errorPercentageL1 > 20
          ? 'red'
          : 'green'
      }}
    >
  </div>
    </div>
  </div>

  <div>
    <label style={styles.label}>Reading fluency *</label>
    <select
      value={formData.readingFluencyL1}
      onChange={(e) => setFormData({ ...formData, readingFluencyL1: e.target.value })}
      style={styles.select}
      disabled={formLocked}
    >
      <option value="">Select</option>
      {READING_FLUENCY_OPTIONS.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>

  <div>
    <label style={styles.label}>Reading Comprehension (0-3) *</label>
    <input
      type="number"
      min="0"
      max="3"
      value={formData.readingComprehensionL1}
      onChange={(e) => {
        const value = e.target.value;
        if (value === '' || (Number(value) >= 0 && Number(value) <= 3)) {
          setFormData({ ...formData, readingComprehensionL1: value });
        }
      }}
      style={styles.input}
      disabled={formLocked}
      autoComplete="off"
    />
  </div>

  <div>
    {/* Ceiling & Baseline auto-calculated in background - hidden from teacher view */}
    <div style={{ display: 'none' }}>
      <span>{autoCeiling}</span>
      <span>{autoBaseline}</span>
    </div>
  </div>

<div>
  <label style={styles.label}>Error rate</label>
  <div
    style={{
      boxSizing: 'border-box',
      justifyContent: 'center', 
      padding: '8px',
      borderRadius: '6px',
      fontSize: '15px',
      fontWeight: '700',
      backgroundColor:
        errorPercentageL1 === ''
          ? '#F3F4F6'
          : Number(errorPercentageL1) >= 20
          ? '#FEE2E2'
          : '#DCFCE7',
      color:
        errorPercentageL1 === ''
          ? COLORS.textGray
          : Number(errorPercentageL1) >= 20
          ? '#B91C1C'
          : '#15803D'
    }}
  >
    {errorPercentageL1 === ''
      ? '-'
      : Number(errorPercentageL1) >= 20
      ? `⚠ ${errorPercentageL1}%`
      : `✓ ${errorPercentageL1}%`}
  </div>
  </div>
</div>

            {/* CLEAR ACTION INSTRUCTION after error rate */}
            {errorPercentageL1 !== '' && (
              <div style={{
                padding: '14px 18px',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: 700,
                lineHeight: 1.6,
                backgroundColor: Number(errorPercentageL1) > 20 ? '#FFF7ED' : '#F0FDF4',
                border: `2px solid ${Number(errorPercentageL1) > 20 ? '#FB923C' : '#4ADE80'}`,
                color: Number(errorPercentageL1) > 20 ? '#9A3412' : '#166534',
              }}>
                {Number(errorPercentageL1) > 20
                  ? '⬇️ NEXT STEP: Press Next. The child will read an EASIER passage.'
                  : '⬆️ NEXT STEP: Press Next. The child will read a HARDER passage.'}
              </div>
            )}

            {/* Reading Examples Section with subtle color */}
            <div style={{ 
              backgroundColor: '#FFF1F2', 
              padding: '20px', 
              borderRadius: '8px',
              border: '1px solid #FECDD3'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <div style={{ fontSize: '16px', fontWeight: '600', color: COLORS.navyBlue }}>
                  Prose Reading: Word Examples (up to 5)
                </div>
                <div style={{ fontSize: '13px', color: COLORS.textGray, marginTop: '4px' }}>
                  Enter the correct word and what the student read it as (phonetic)
                </div>
                <div style={{ 
                  marginTop: '10px',
                  backgroundColor: '#FFFBEB',
                  border: '1px solid #FCD34D',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  fontSize: '12px',
                  color: COLORS.textDark,
                  lineHeight: 1.35
                }}>
                  <div style={{ fontWeight: 700, color: COLORS.navyBlue }}>
                    If the language is Kannada, please type words in English letters (Roman script).
                  </div>
                  <div style={{ marginTop: '4px', color: COLORS.textGray }}>
                    Example: <strong>Correct:</strong> mane; <strong>Read/Wrote as:</strong> mne; <strong>Error type:</strong> Omission
                  </div>
                </div>

                <div style={{ fontSize: '12px', fontWeight: 700, color: COLORS.textGray, marginTop: '8px' }}>
                  Reading errors recorded: <span style={{ color: countReadingErrorRows(formData) >= 5 ? '#16A34A' : '#DC2626' }}>{countReadingErrorRows(formData)}</span> / 5 minimum
                </div>

                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    disabled={formLocked || l1ReadingExamplesShown >= 5}
                    onClick={() => setL1ReadingExamplesShown(prev => Math.min(5, prev + 1))}
                    style={{
                      background: 'transparent',
                      border: '1px solid #CBD5E1',
                      color: COLORS.navyBlue,
                      borderRadius: '999px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: formLocked || l1ReadingExamplesShown >= 5 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    + Add another error (optional)
                  </button>

                  {l1ReadingExamplesShown > 3 && (
                    <button
                      type="button"
                      disabled={formLocked}
                      onClick={() => {
                        if (l1ReadingExamplesShown === 5) {
                          setFormData({
                            ...formData,
                            proseReadingExample5Correct: '',
                            proseReadingExample5ReadAs: '',
                            proseReadingExample5ErrorType: ''
                          });
                          setL1ReadingExamplesShown(4);
                        } else if (l1ReadingExamplesShown === 4) {
                          setFormData({
                            ...formData,
                            proseReadingExample4Correct: '',
                            proseReadingExample4ReadAs: '',
                            proseReadingExample4ErrorType: ''
                          });
                          setL1ReadingExamplesShown(3);
                        }
                      }}
                      style={{
                        background: 'transparent',
                        border: '1px solid #E5E7EB',
                        color: COLORS.textGray,
                        borderRadius: '999px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: formLocked ? 'not-allowed' : 'pointer'
                      }}
                    >
                      − Remove last
                    </button>
                  )}
                </div>
              </div>

              {/* 3-column grid for reading examples */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                {Array.from({ length: l1ReadingExamplesShown }, (_, i) => i + 1).map((n) => {
                  const bgColors = ['#FFFFFF', '#FFF1F2', '#FFEDD5'];
                  const borderColors = ['#E0E7FF', '#FECDD3', '#FECACA'];
                  
                  return (
                    <div 
                      key={n} 
                      style={{
                        backgroundColor: bgColors[(n-1) % 3],
                        padding: '14px',
                        borderRadius: '6px',
                        border: `1px solid ${borderColors[(n-1) % 3]}`
                      }}
                    >
                      <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textDark, marginBottom: '10px', textAlign: 'center' }}>
                        Example {n}
                      </div>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{...styles.smallLabel, fontSize: '11px'}}>Correct</label>
                        <input
                          type="text"
                          value={formData[`proseReadingExample${n}Correct`]}
                          onChange={(e) => setFormData({...formData, [`proseReadingExample${n}Correct`]: e.target.value.replace(/\d/g, '')})}
                          style={{...styles.input, fontSize: '13px', padding: '6px 10px'}}
                          placeholder="e.g., was"
                          disabled={formLocked}
                          autocomplete="off"
                        />
                      </div>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{...styles.smallLabel, fontSize: '11px'}}>Read as</label>
                        <input
                          type="text"
                          value={formData[`proseReadingExample${n}ReadAs`]}
                          onChange={(e) => setFormData({...formData, [`proseReadingExample${n}ReadAs`]: e.target.value.replace(/\d/g, '')})}
                          style={{...styles.input, fontSize: '13px', padding: '6px 10px'}}
                          placeholder="e.g., saw"
                          disabled={formLocked}
                          autocomplete="off"
                        />
                      </div>
                      
                      <div>
                        <label style={{...styles.smallLabel, fontSize: '11px'}}>Error type</label>
                        <select
                          value={formData[`proseReadingExample${n}ErrorType`]}
                          onChange={(e) => setFormData({...formData, [`proseReadingExample${n}ErrorType`]: e.target.value})}
                          style={{...styles.select, fontSize: '12px', padding: '6px 10px'}}
                          disabled={formLocked}
                          autoComplete="off"
                        >
                          <option value="">Select type</option>
                          {(formData.mediumOfInstruction === 'Kannada' ? READING_ERROR_TYPES_KANNADA : READING_ERROR_TYPES_ENGLISH).map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
                
                {/* Additional notes spans full width at bottom */}
                <div style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
                  <label style={styles.label}>Additional notes</label>
                  <textarea 
                    value={formData.proseReadingAdditionalNotes} 
                    onChange={(e) => setFormData({...formData, proseReadingAdditionalNotes: e.target.value})} 
                    style={styles.textarea} 
                    rows="2" 
                    disabled={formLocked}
                    autocomplete="off"
                    placeholder="Any additional observations about reading patterns..."
                  />
                </div>
              </div>
            </div>
          </div>
        )
      },
      {
        number: 8,
        title: errorPercentageL1 !== '' && Number(errorPercentageL1) > 20
          ? 'Reading Assessment: Passage 2 (Easier Level)'
          : 'Reading Assessment: Passage 2 (Harder Level)',
        fields: (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
    {/* Dynamic direction banner based on Passage 1 results */}
    {errorPercentageL1 !== '' && Number(errorPercentageL1) > 20 ? (
      <div style={{
        backgroundColor: '#FFF7ED',
        border: '1px solid #FDBA74',
        borderRadius: '10px',
        padding: '14px 16px',
      }}>
        <div style={{ fontWeight: 800, color: '#9A3412', fontSize: '15px', marginBottom: '6px' }}>
          ⬇️ Moving to an Easier Passage
        </div>
        <div style={{ fontSize: '14px', color: '#9A3412', lineHeight: 1.6 }}>
          The child had <strong>{errorPercentageL1}% errors</strong> in Passage 1, which was too difficult for them.
          Now ask them to read an <strong>easier passage (below the starting level)</strong> and record the results below.
        </div>
      </div>
    ) : errorPercentageL1 !== '' ? (
      <div style={{
        backgroundColor: '#F0FDF4',
        border: '1px solid #86EFAC',
        borderRadius: '10px',
        padding: '14px 16px',
      }}>
        <div style={{ fontWeight: 800, color: '#166534', fontSize: '15px', marginBottom: '6px' }}>
          ⬆️ Moving to a Harder Passage
        </div>
        <div style={{ fontSize: '14px', color: '#166534', lineHeight: 1.6 }}>
          The child had only <strong>{errorPercentageL1}% errors</strong> in Passage 1 - they coped well!
          Now ask them to read a <strong>harder passage (1 level below expected)</strong> and record the results below.
        </div>
      </div>
    ) : null}
    {/* Top stats in compact grid */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
      <div>
        <label style={styles.label}>Time (seconds) *</label>
        <input
          type="number"
          min="0"
          value={formData.l2ReadingSpeed}
          onChange={(e) => setFormData({ ...formData, l2ReadingSpeed: e.target.value })}
          style={styles.input}
          disabled={formLocked}
          autoComplete="off"
        />
      </div>

      <div>
        <label style={styles.label}>Words read *</label>
        <input
          type="number"
          min="0"
          value={formData.l2TotalWordsRead}
          onChange={(e) => setFormData({ ...formData, l2TotalWordsRead: e.target.value })}
          style={styles.input}
          disabled={formLocked}
          autoComplete="off"
        />
      </div>

      <div>
        <label style={styles.label}>Reading errors *</label>
        <input
          type="number"
          min="0"
          value={formData.l2ReadingErrors}
          onChange={(e) => setFormData({ ...formData, l2ReadingErrors: e.target.value })}
          style={styles.input}
          disabled={formLocked}
          autoComplete="off"
        />
        <div style={{ fontSize: '11px', color: COLORS.textGray, marginTop: '4px' }}>
          Missing words, lines, letter/sound omissions
          <div
      style={{
      fontSize: '14px',
      fontWeight: 'bold',
      marginTop: '6px',
      color:
        errorPercentageL2 === ''
         ? 'black'
         : errorPercentageL2 > 20
          ? 'red'
         : 'green'
    }}
  >
</div>
        </div>
      </div>

      <div>
        <label style={styles.label}>Reading fluency *</label>
        <select
          value={formData.l2ReadingFluency}
          onChange={(e) => setFormData({ ...formData, l2ReadingFluency: e.target.value })}
          style={styles.select}
          disabled={formLocked}
        >
          <option value="">Select</option>
          {READING_FLUENCY_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>

      <div>
        <label style={styles.label}>Reading Comprehension (0-3) *</label>
        <input
          type="number"
          min="0"
          max="3"
          value={formData.l2ReadingComprehensionL2}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || (Number(value) >= 0 && Number(value) <= 3)) {
              setFormData({ ...formData, l2ReadingComprehensionL2: value });
            }
          }}
          style={styles.input}
          disabled={formLocked}
          autoComplete="off"
        />
      </div>

      {/* Ceiling & Baseline auto-calculated in background - hidden from teacher view */}
      <div style={{ display: 'none' }}>
        <span>{autoCeiling}</span>
        <span>{autoBaseline}</span>
      </div>

    <div>
    <label style={styles.label}>Error rate</label>
    <div
    style={{
      boxSizing: 'border-box',
      justifyContent: 'center', 
      padding: '8px',
      borderRadius: '6px',
      fontSize: '15px',
      fontWeight: '700',
      backgroundColor:
        errorPercentageL2 === ''
          ? '#F3F4F6'
          : Number(errorPercentageL2) >= 20
          ? '#FEE2E2'
          : '#DCFCE7',
      color:
        errorPercentageL2 === ''
          ? COLORS.textGray
          : Number(errorPercentageL2) >= 20
          ? '#B91C1C'
          : '#15803D'
    }}
  >
    {errorPercentageL2 === ''
      ? '-'
      : Number(errorPercentageL2) >= 20
      ? `⚠ ${errorPercentageL2}%`
      : `✓ ${errorPercentageL2}%`}
  </div>
</div>
    </div>

            {/* CLEAR ACTION INSTRUCTION after Passage 2 error rate */}
            {errorPercentageL2 !== '' && errorPercentageL1 !== '' && (
              <div style={{
                padding: '14px 18px',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: 700,
                lineHeight: 1.6,
                backgroundColor:
                  (Number(errorPercentageL1) > 20 || Number(errorPercentageL2) > 20) ? '#EFF6FF' : '#FFF7ED',
                border: `2px solid ${
                  (Number(errorPercentageL1) > 20 || Number(errorPercentageL2) > 20) ? '#93C5FD' : '#FB923C'
                }`,
                color: (Number(errorPercentageL1) > 20 || Number(errorPercentageL2) > 20) ? '#1E40AF' : '#9A3412',
              }}>
                {Number(errorPercentageL1) > 20
                  ? '🛑 STOP: The reading assessment is complete. Press Next - you can skip Passage 3.'
                  : Number(errorPercentageL2) > 20
                  ? '🛑 STOP: The reading assessment is complete. Press Next - you can skip Passage 3.'
                  : '⬆️ CONTINUE: The child coped well with both passages. Press Next - Passage 3 is required to test them at their expected reading level.'}
              </div>
            )}

    {/* Reading Examples Section with blue theme */}
    <div
      style={{
        backgroundColor: '#EFF6FF',
        padding: '20px',
        borderRadius: '8px',
        border: '1px solid #BFDBFE'
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '16px', fontWeight: '600', color: COLORS.navyBlue }}>
          Prose Reading: Word Examples (up to 5)
        </div>
        <div style={{ fontSize: '13px', color: COLORS.textGray, marginTop: '4px' }}>
          Enter the correct word and what the student read it as (phonetic)
        </div>

        {/* Dynamic minimum-errors instruction based on direction */}
        {errorPercentageL1 !== '' && errorPercentageL2 !== '' && canSkipPassage3 && (
          <div style={{
            marginTop: '10px',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: 1.5,
            backgroundColor: '#FFFBEB',
            border: '1px solid #FCD34D',
            color: '#92400E',
          }}>
            📝 Record as many errors as you can. You need at least <strong>5 errors total</strong> across Passages 1 and 2. You can go back to add more to Passage 1 if needed.
          </div>
        )}

        <div
          style={{
            marginTop: '10px',
            backgroundColor: '#FFFBEB',
            border: '1px solid #FCD34D',
            borderRadius: '8px',
            padding: '8px 10px',
            fontSize: '12px',
            color: COLORS.textDark,
            lineHeight: 1.35
          }}
        >
          <div style={{ fontWeight: 700, color: COLORS.navyBlue }}>
            If the language is Kannada, please type words in English letters (Roman script).
          </div>
          <div style={{ marginTop: '4px', color: COLORS.textGray }}>
            Example: <strong>Correct:</strong> mane; <strong>Read/Wrote as:</strong> mne; <strong>Error type:</strong> Omission
          </div>
        </div>

        <div style={{ fontSize: '12px', fontWeight: 700, color: COLORS.textGray, marginTop: '8px' }}>
          Total reading errors recorded across all passages:{' '}
          <span style={{ color: countReadingErrorRows(formData) >= 5 ? '#16A34A' : '#DC2626' }}>
            {countReadingErrorRows(formData)}
          </span>{' '}
          / 5 minimum
          <span style={{ fontWeight: 400, marginLeft: '8px' }}>
            (P1: {countReadingErrorRowsForLevel(formData, 'l1')} | P2: {countReadingErrorRowsForLevel(formData, 'l2')})
          </span>
        </div>

        <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={formLocked || l2ReadingExamplesShown >= 5}
            onClick={() => setL2ReadingExamplesShown((prev) => Math.min(5, prev + 1))}
            style={{
              background: 'transparent',
              border: '1px solid #CBD5E1',
              color: COLORS.navyBlue,
              borderRadius: '999px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: formLocked || l2ReadingExamplesShown >= 5 ? 'not-allowed' : 'pointer'
            }}
          >
            + Add another error (optional)
          </button>

          {l2ReadingExamplesShown > 3 && (
            <button
              type="button"
              disabled={formLocked}
              onClick={() => {
                if (l2ReadingExamplesShown === 5) {
                  setFormData({
                    ...formData,
                    l2ProseReadingExample5Correct: '',
                    l2ProseReadingExample5ReadAs: '',
                    l2ProseReadingExample5ErrorType: ''
                  });
                  setL2ReadingExamplesShown(4);
                } else if (l2ReadingExamplesShown === 4) {
                  setFormData({
                    ...formData,
                    l2ProseReadingExample4Correct: '',
                    l2ProseReadingExample4ReadAs: '',
                    l2ProseReadingExample4ErrorType: ''
                  });
                  setL2ReadingExamplesShown(3);
                }
              }}
              style={{
                background: 'transparent',
                border: '1px solid #E5E7EB',
                color: COLORS.textGray,
                borderRadius: '999px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: formLocked ? 'not-allowed' : 'pointer'
              }}
            >
              − Remove last
            </button>
          )}
        </div>
      </div>

      {/* 3-column grid for reading examples */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
        {Array.from({ length: l2ReadingExamplesShown }, (_, i) => i + 1).map((n) => {
          const bgColors = ['#FFFFFF', '#EFF6FF', '#DBEAFE'];
          const borderColors = ['#E0E7FF', '#BFDBFE', '#93C5FD'];

          return (
            <div
              key={n}
              style={{
                backgroundColor: bgColors[(n - 1) % 3],
                padding: '14px',
                borderRadius: '6px',
                border: `1px solid ${borderColors[(n - 1) % 3]}`
              }}
            >
              <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textDark, marginBottom: '10px', textAlign: 'center' }}>
                Example {n}
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={{ ...styles.smallLabel, fontSize: '11px' }}>Correct</label>
                <input
                  type="text"
                  value={formData[`l2ProseReadingExample${n}Correct`]}
                  onChange={(e) => setFormData({ ...formData, [`l2ProseReadingExample${n}Correct`]: e.target.value.replace(/\d/g, '') })}
                  style={{ ...styles.input, fontSize: '13px', padding: '6px 10px' }}
                  placeholder="e.g., was"
                  disabled={formLocked}
                  autoComplete="off"
                />
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={{ ...styles.smallLabel, fontSize: '11px' }}>Read as</label>
                <input
                  type="text"
                  value={formData[`l2ProseReadingExample${n}ReadAs`]}
                  onChange={(e) => setFormData({ ...formData, [`l2ProseReadingExample${n}ReadAs`]: e.target.value.replace(/\d/g, '') })}
                  style={{ ...styles.input, fontSize: '13px', padding: '6px 10px' }}
                  placeholder="e.g., saw"
                  disabled={formLocked}
                  autoComplete="off"
                />
              </div>

              <div>
                <label style={{ ...styles.smallLabel, fontSize: '11px' }}>Error type</label>
                <select
                  value={formData[`l2ProseReadingExample${n}ErrorType`]}
                  onChange={(e) => setFormData({ ...formData, [`l2ProseReadingExample${n}ErrorType`]: e.target.value })}
                  style={{ ...styles.select, fontSize: '12px', padding: '6px 10px' }}
                  disabled={formLocked}
                  autoComplete="off"
                >
                  <option value="">Select type</option>
                  {(formData.mediumOfInstruction === 'Kannada' ? READING_ERROR_TYPES_KANNADA : READING_ERROR_TYPES_ENGLISH).map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}

        {/* Additional notes spans full width at bottom */}
        <div style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
          <label style={styles.label}>Additional notes</label>
          <textarea
            value={formData.l2ProseReadingAdditionalNotes}
            onChange={(e) => setFormData({ ...formData, l2ProseReadingAdditionalNotes: e.target.value })}
            style={styles.textarea}
            rows="2"
            disabled={formLocked}
            autoComplete="off"
            placeholder="Any additional observations about reading patterns..."
          />
        </div>
      </div>
    </div>
  </div>
)
},
{
  number: 9,
  title: canSkipPassage3 ? 'Reading Assessment: Passage 3 (Not Required)' : 'Reading Assessment: Passage 3 (Required)',
  fields: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* BLOCKED BANNER - shown when ceiling is already reached from Passages 1 & 2 */}
            {canSkipPassage3 && (
              <div style={{
                padding: '20px 18px',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: 700,
                lineHeight: 1.6,
                backgroundColor: '#EFF6FF',
                border: '2px solid #93C5FD',
                color: '#1E40AF',
                textAlign: 'center',
              }}>
                ✅ Passage 3 is NOT required - the ceiling has been reached.<br />
                <span style={{ fontWeight: 400, fontSize: '13px' }}>
                  The reading assessment is already complete from Passages 1 and 2. Press Next to continue to the next section.
                </span>
              </div>
            )}
            {/* REQUIRED BANNER - shown when child coped at both P1 and P2, no ceiling found */}
            {!canSkipPassage3 && errorPercentageL1 !== '' && errorPercentageL2 !== '' &&
             Number(errorPercentageL1) <= 20 && Number(errorPercentageL2) <= 20 && (
              <div style={{
                padding: '16px 18px',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: 700,
                lineHeight: 1.6,
                backgroundColor: '#FFF7ED',
                border: '2px solid #FB923C',
                color: '#9A3412',
                textAlign: 'center',
              }}>
                ⬆️ The child coped well with both earlier passages - now test them at their <strong>expected reading level</strong>.<br />
                <span style={{ fontWeight: 400, fontSize: '13px' }}>This section is required. Please ask the child to read this passage and record the results below.</span>
              </div>
            )}
            {/* Only show the Passage 3 form fields when ceiling has NOT been reached */}
            {!canSkipPassage3 && (
            <>
            {/* Top stats in compact grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              <div>
                <label style={styles.label}>Time (seconds) *</label>
                <input type="number" min="0" value={formData.l3ReadingSpeed} onChange={(e) => setFormData({...formData, l3ReadingSpeed: e.target.value})} style={styles.input} disabled={formLocked} autocomplete="off" />
              </div>
              <div>
                <label style={styles.label}>Words read *</label>
                <input type="number" min="0" value={formData.l3TotalWordsRead} onChange={(e) => setFormData({...formData, l3TotalWordsRead: e.target.value})} style={styles.input} disabled={formLocked} autocomplete="off" />
              </div>
              <div>
                <label style={styles.label}>Reading errors *</label>
                <input type="number" min="0" value={formData.l3ReadingErrors} onChange={(e) => setFormData({...formData, l3ReadingErrors: e.target.value})} style={styles.input} disabled={formLocked} autocomplete="off" />
                <div style={{ fontSize: '11px', color: COLORS.textGray, marginTop: '4px' }}>Missing words, lines, letter/sound omissions</div>
                <div
        style={{
        fontSize: '14px',
        fontWeight: 'bold',
        marginTop: '6px',
        color:
          errorPercentageL3 === ''
           ? 'black'
           : errorPercentageL3 > 20
           ? 'red'
           : 'green'
       }}
     >
    </div>
              </div>
              <div>
                <label style={styles.label}>Reading fluency *</label>
                <select
                  value={formData.l3ReadingFluency}
                  onChange={(e) => setFormData({ ...formData, l3ReadingFluency: e.target.value })}
                  style={styles.select}
                  disabled={formLocked}
                >
                  <option value="">Select</option>
                  {READING_FLUENCY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={styles.label}>Reading Comprehension (0-3) *</label>
                <input type="number" min="0" max="3" value={formData.l3ReadingComprehensionL3} onChange={(e) => {
                    const value = e.target.value;
                    // Allow blank while typing, otherwise strictly enforce 0-3
                    if (value === '' || (Number(value) >= 0 && Number(value) <= 3)) {
                      setFormData({ ...formData, l3ReadingComprehensionL3: value });
                    }
                  }} style={styles.input} disabled={formLocked} autocomplete="off" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginTop: '10px' }}>

  <div>
    {/* Ceiling & Baseline auto-calculated in background - hidden from teacher view */}
    <div style={{ display: 'none' }}>
      <span>{autoCeiling}</span>
      <span>{autoBaseline}</span>
    </div>
  </div>

<div>
  <label style={styles.label}>Error rate</label>
  <div
    style={{
      boxSizing: 'border-box',
      justifyContent: 'center', 
      padding: '8px',
      borderRadius: '6px',
      fontSize: '15px',
      fontWeight: '700',
      marginTop: '4px',
      backgroundColor:
        errorPercentageL3 === ''
          ? '#F3F4F6'
          : Number(errorPercentageL3) >= 20
          ? '#FEE2E2'
          : '#DCFCE7',
      color:
        errorPercentageL3 === ''
          ? COLORS.textGray
          : Number(errorPercentageL3) >= 20
          ? '#B91C1C'
          : '#15803D'
    }}
  >
    {errorPercentageL3 === ''
      ? '-'
      : Number(errorPercentageL3) >= 20
      ? `⚠ ${errorPercentageL3}%`
      : `✓ ${errorPercentageL3}%`}
  </div>
</div>

</div>      

            {/* Reading Examples Section with purple theme */}
            <div style={{ 
              backgroundColor: '#FAF5FF', 
              padding: '20px', 
              borderRadius: '8px',
              border: '1px solid #E9D5FF'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <div style={{ fontSize: '16px', fontWeight: '600', color: COLORS.navyBlue }}>
                  Prose Reading: Word Examples (up to 5)
                </div>
                <div style={{ fontSize: '13px', color: COLORS.textGray, marginTop: '4px' }}>
                  Enter the correct word and what the student read it as (phonetic)
                </div>
                <div style={{ 
                  marginTop: '10px',
                  backgroundColor: '#FFFBEB',
                  border: '1px solid #FCD34D',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  fontSize: '12px',
                  color: COLORS.textDark,
                  lineHeight: 1.35
                }}>
                  <div style={{ fontWeight: 700, color: COLORS.navyBlue }}>
                    If the language is Kannada, please type words in English letters (Roman script).
                  </div>
                  <div style={{ marginTop: '4px', color: COLORS.textGray }}>
                    Example: <strong>Correct:</strong> mane; <strong>Read/Wrote as:</strong> mne; <strong>Error type:</strong> Omission
                  </div>
                </div>

                <div style={{ fontSize: '12px', fontWeight: 700, color: COLORS.textGray, marginTop: '8px' }}>
                  Reading errors recorded: <span style={{ color: countReadingErrorRows(formData) >= 5 ? '#16A34A' : '#DC2626' }}>{countReadingErrorRows(formData)}</span> / 5 minimum
                </div>

                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    disabled={formLocked || l3ReadingExamplesShown >= 5}
                    onClick={() => setL3ReadingExamplesShown(prev => Math.min(5, prev + 1))}
                    style={{
                      background: 'transparent',
                      border: '1px solid #CBD5E1',
                      color: COLORS.navyBlue,
                      borderRadius: '999px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: formLocked || l3ReadingExamplesShown >= 5 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    + Add another error (optional)
                  </button>

                  {l3ReadingExamplesShown > 3 && (
                    <button
                      type="button"
                      disabled={formLocked}
                      onClick={() => {
                        if (l3ReadingExamplesShown === 5) {
                          setFormData({
                            ...formData,
                            l3ProseReadingExample5Correct: '',
                            l3ProseReadingExample5ReadAs: '',
                            l3ProseReadingExample5ErrorType: ''
                          });
                          setL3ReadingExamplesShown(4);
                        } else if (l3ReadingExamplesShown === 4) {
                          setFormData({
                            ...formData,
                            l3ProseReadingExample4Correct: '',
                            l3ProseReadingExample4ReadAs: '',
                            l3ProseReadingExample4ErrorType: ''
                          });
                          setL3ReadingExamplesShown(3);
                        }
                      }}
                      style={{
                        background: 'transparent',
                        border: '1px solid #E5E7EB',
                        color: COLORS.textGray,
                        borderRadius: '999px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: formLocked ? 'not-allowed' : 'pointer'
                      }}
                    >
                      − Remove last
                    </button>
                  )}
                </div>
              </div>

              {/* 3-column grid for reading examples */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                {Array.from({ length: l3ReadingExamplesShown }, (_, i) => i + 1).map((n) => {
                  const bgColors = ['#FFFFFF', '#FAF5FF', '#F3E8FF'];
                  const borderColors = ['#E0E7FF', '#E9D5FF', '#D8B4FE'];
                  
                  return (
                    <div 
                      key={n} 
                      style={{
                        backgroundColor: bgColors[(n-1) % 3],
                        padding: '14px',
                        borderRadius: '6px',
                        border: `1px solid ${borderColors[(n-1) % 3]}`
                      }}
                    >
                      <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textDark, marginBottom: '10px', textAlign: 'center' }}>
                        Example {n}
                      </div>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{...styles.smallLabel, fontSize: '11px'}}>Correct</label>
                        <input
                          type="text"
                          value={formData[`l3ProseReadingExample${n}Correct`]}
                          onChange={(e) => setFormData({...formData, [`l3ProseReadingExample${n}Correct`]: e.target.value.replace(/\d/g, '')})}
                          style={{...styles.input, fontSize: '13px', padding: '6px 10px'}}
                          placeholder="e.g., was"
                          disabled={formLocked}
                          autocomplete="off"
                        />
                      </div>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{...styles.smallLabel, fontSize: '11px'}}>Read as</label>
                        <input
                          type="text"
                          value={formData[`l3ProseReadingExample${n}ReadAs`]}
                          onChange={(e) => setFormData({...formData, [`l3ProseReadingExample${n}ReadAs`]: e.target.value.replace(/\d/g, '')})}
                          style={{...styles.input, fontSize: '13px', padding: '6px 10px'}}
                          placeholder="e.g., saw"
                          disabled={formLocked}
                          autocomplete="off"
                        />
                      </div>
                      
                      <div>
                        <label style={{...styles.smallLabel, fontSize: '11px'}}>Error type</label>
                        <select
                          value={formData[`l3ProseReadingExample${n}ErrorType`]}
                          onChange={(e) => setFormData({...formData, [`l3ProseReadingExample${n}ErrorType`]: e.target.value})}
                          style={{...styles.select, fontSize: '12px', padding: '6px 10px'}}
                          disabled={formLocked}
                          autoComplete="off"
                        >
                          <option value="">Select type</option>
                          {(formData.mediumOfInstruction === 'Kannada' ? READING_ERROR_TYPES_KANNADA : READING_ERROR_TYPES_ENGLISH).map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
                
                {/* Additional notes spans full width at bottom */}
                <div style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
                  <label style={styles.label}>Additional notes</label>
                  <textarea 
                    value={formData.l3ProseReadingAdditionalNotes} 
                    onChange={(e) => setFormData({...formData, l3ProseReadingAdditionalNotes: e.target.value})} 
                    style={styles.textarea} 
                    rows="2" 
                    disabled={formLocked}
                    autocomplete="off"
                    placeholder="Any additional observations about reading patterns..."
                  />
                </div>
              </div>
            </div>
          </>
          )}
          </div>
        )
      },
      {
        number: 12,
        title: 'Screening Complete',
        fields: (
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: '45% 55%',
            gap: '0',
            borderRadius: '12px',
            overflow: 'hidden',
            minHeight: '500px',
            maxHeight: '600px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            backgroundColor: 'white'
          }}>
            {/* LEFT SIDE - Image */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(0, 51, 102, 0.75) 0%, rgba(255, 107, 53, 0.70) 100%), url(/girl-student.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative'
            }}>
            </div>

            {/* RIGHT SIDE - Content */}
            <div style={{
              backgroundColor: '#F9FAFB',
              padding: '50px 40px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative'
            }}>
              {/* Logout Button - Top Right */}
              <button
                onClick={() => {
                  localStorage.removeItem('facilitatorEmail');
                  window.location.reload();
                }}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  backgroundColor: 'white',
                  color: COLORS.navyBlue,
                  border: `2px solid ${COLORS.navyBlue}`,
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.navyBlue;
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = COLORS.navyBlue;
                }}
              >
                LOGOUT
              </button>

              {/* Logo - Centered */}
              <div style={{ 
                marginBottom: '35px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <img 
                  src="/Clear-logo.png" 
                  alt="NeuroWise Logo" 
                  style={{ 
                    height: '70px', 
                    width: 'auto'
                  }} 
                />
              </div>

              {/* Main Heading */}
              <h2 style={{ 
                fontSize: '26px', 
                fontWeight: '700',
                lineHeight: '1.3',
                marginBottom: '16px',
                color: COLORS.navyBlue,
                textAlign: 'center'
              }}>
                Thank you for completing the Learning Support Screening Program
              </h2>

              {/* Mission Statement */}
              <p style={{ 
                fontSize: '17px', 
                lineHeight: '1.6',
                marginBottom: '40px',
                color: COLORS.textGray,
                fontStyle: 'italic',
                textAlign: 'center'
              }}>
                Identifying strengths and building pathways to help every student thrive
              </p>

              {/* Footer */}
              <div style={{ 
                borderTop: `2px solid ${COLORS.borderGray}`,
                paddingTop: '25px',
                textAlign: 'center'
              }}>
                <p style={{ 
                  fontSize: '16px', 
                  color: COLORS.navyBlue,
                  fontWeight: '700',
                  marginBottom: '6px',
                  letterSpacing: '0.3px'
                }}>
                  NeuroWise Academy Foundation
                </p>
                <p style={{ 
                  fontSize: '14px', 
                  color: COLORS.textGray,
                  fontStyle: 'italic',
                  marginBottom: '12px'
                }}>
                  Empowering Every Learner
                </p>
                <p style={{ 
                  fontSize: '12px', 
                  color: '#9CA3AF',
                  marginTop: '12px'
                }}>
                  © 2026 NeuroWise Academy Foundation. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        )
      }
    ];

    return sections.find(s => s.number === currentSection);
  };

  // LOGIN VIEW - UPDATED WITH LOGO AND COPYRIGHT
  if (view === 'login') {
    return (
      <>
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      {loading && <LoadingOverlay message={loadingMessage} />}
      <div style={styles.loginContainer}>
        <div style={styles.loginBox}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <img 
              src="/Clear-logo.png" 
              alt="NeuroWise Logo" 
              style={{ 
                width: '80px', 
                height: '80px',
                marginBottom: '20px'
              }} 
            />
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: COLORS.navyBlue, 
              margin: '0 0 8px 0',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
            }}>
              NeuroWise Academy Foundation
            </h1>
            <p style={{ 
              color: COLORS.textGray,
              margin: 0,
              fontSize: '15px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
            }}>
              Learning Support Management
            </p>
          </div>

          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: COLORS.textDark, 
                marginBottom: '6px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
              }}>
                NWA Practitioner ID
              </label>
              <input 
                type="text" 
                value={facilitatorId} 
                onChange={(e) => setFacilitatorId(e.target.value)} 
                style={{
                  ...styles.input,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                }} 
                placeholder="Enter your NWA Practitioner ID" 
                required 
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: COLORS.textDark, 
                marginBottom: '6px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
              }}>
                PIN
              </label>
              <input 
                type="password" 
                value={pin} 
                onChange={(e) => setPin(e.target.value)} 
                style={{
                  ...styles.input,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                }} 
                placeholder="Enter your PIN" 
                required 
              />
            </div>

            {error && <div style={styles.errorBox}>{error}</div>}

            <button 
              type="submit" 
              disabled={loading} 
              style={{ 
                ...styles.button, 
                width: '100%', 
                opacity: loading ? 0.6 : 1,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
              }}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div style={{
            marginTop: '32px',
            paddingTop: '20px',
            borderTop: '1px solid #E5E7EB',
            textAlign: 'center',
            fontSize: '12px',
            color: '#9CA3AF',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
          }}>
            © 2026 NeuroWise Academy Foundation. All Rights Reserved.
          </div>
        </div>
      </div>
      </>
    );
  }

  // DASHBOARD VIEW
  if (view === 'dashboard') {
    const dashboardFirstName = String(facilitatorName || 'NWA Practitioner').trim().split(/\s+/)[0];
    const filteredStudents = students.filter((student) => {
      const schoolOk = schoolFilter === 'all' || String(student.school_id || '') === schoolFilter;
      const complete = student.programme_status === 'Programme Complete' || student.learning_plan_complete || student.learning_plan_state === 'Complete';
      const statusOk = statusFilter === 'all' || (statusFilter === 'complete' ? complete : !complete);
      return schoolOk && statusOk;
    });
    const schoolNameFor = (schoolId) => schoolIds.find((school) => school.school_id === schoolId)?.name || '';
    const schoolDisplayNameFor = (schoolId) => {
      const name = schoolNameFor(schoolId);
      if (!name) return schoolId || '';
      const parts = name.split(',').map((part) => part.trim()).filter(Boolean);
      if (parts.length === 2 && /^Government School$/i.test(parts[0])) {
        return `${parts[1]} ${parts[0]}`;
      }
      return name;
    };

    return (
      <>
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      {loading && <LoadingOverlay message={loadingMessage} />}
      <div style={{ ...styles.container, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <img src="/Clear-logo.png" alt="NeuroWise Logo" style={{ height: '48px', width: 'auto', filter: 'brightness(0) invert(1)' }} />
              <div style={{ textAlign: 'left' }}>
                <h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>NeuroWise Academy Foundation</h1>
                <p style={{ fontSize: '12px', color: '#D1D5DB' }}>Learning Support System</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {isOwnerRole(userRole) && (
                <button
                  onClick={openPinReset}
                  style={{ ...styles.logoutButton, backgroundColor: COLORS.navyBlue, border: '1px solid rgba(255,255,255,0.35)' }}
                >
                  Reset Team PIN
                </button>
              )}
              {isOwnerRole(userRole) && (
                <button
                  onClick={openDeactivateTeamMember}
                  style={{ ...styles.logoutButton, backgroundColor: '#7F1D1D', border: '1px solid rgba(255,255,255,0.35)' }}
                >
                  Deactivate Team Member
                </button>
              )}
              {schoolIds.length > 0 && (
                <button
                  onClick={openAddStudent}
                  style={{ ...styles.logoutButton, backgroundColor: COLORS.orange }}
                >
                  + Add Student
                </button>
              )}
              <button 
                onClick={refreshStudents} 
                disabled={loading}
                style={{ 
                  ...styles.logoutButton, 
                  backgroundColor: loading ? '#9CA3AF' : COLORS.mustard,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                <RefreshCw size={20} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
              <button onClick={handleLogout} style={styles.logoutButton}>
                <LogOut size={20} />
                Logout
              </button>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: '1280px', width: '100%', margin: '0 auto', padding: '24px', flex: 1 }}>
          <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '14px' }}>{dashboardFirstName} - NWA Students</h2>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', marginBottom: '22px' }}>
            <div style={{ minWidth: '250px' }}>
              <label style={{ ...styles.label, marginBottom: '5px' }}>School</label>
              <select style={styles.select} value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)}>
                <option value="all">All authorised schools</option>
                {schoolIds.map((school) => (
                  <option key={school.school_id} value={school.school_id}>{school.school_id} - {school.name}</option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: '210px' }}>
              <label style={{ ...styles.label, marginBottom: '5px' }}>Programme status</label>
              <select style={styles.select} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="active">Active</option>
                <option value="complete">Programme Complete</option>
                <option value="all">All</option>
              </select>
            </div>
            <div style={{ fontSize: '13px', color: COLORS.textGray, paddingBottom: '10px' }}>
              Showing {filteredStudents.length} of {students.length} students
            </div>
          </div>

          {filteredStudents.length === 0 ? (
            <div style={{ ...styles.card, cursor: 'default', textAlign: 'left', color: COLORS.textGray }}>
              No students match the selected filters.
            </div>
          ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
            {filteredStudents.map((student, index) => {
              const borderColors = [COLORS.orange, COLORS.mustard, COLORS.gold];
              const age = calculateAge(student.dob);
              
              return (
                <div key={student.student_code} style={{ ...styles.card, borderLeft: `6px solid ${borderColors[index % 3]}`, padding: '16px 20px 18px', display: 'flex', flexDirection: 'column', minHeight: '176px', cursor: 'default' }}>
                  {isOwnerRole(userRole) && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
                      <button
                        onClick={() => setDeleteStudent(student)}
                        disabled={studentActionBusy === student.student_code}
                        style={{ border: '1px solid #FCA5A5', backgroundColor: '#FFF7F7', color: '#B91C1C', borderRadius: '5px', padding: '4px 7px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        Delete Student
                      </button>
                    </div>
                  )}
                  <div style={{ marginBottom: '8px', textAlign: 'left' }}>
                    <h3 title={student.full_name} style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.navyBlue, margin: 0, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{student.full_name}</h3>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '14px', color: COLORS.textGray, textAlign: 'left' }}>
                    <p style={{ margin: 0 }}><strong>Code:</strong> {student.student_code}</p>
                    <p style={{ margin: 0 }}><strong>DOB:</strong> {student.dob}</p>
                    <p style={{ margin: 0 }}><strong>STD:</strong> {student.standard}, Age {age} yrs</p>
                    {student.school_id && <p style={{ margin: 0 }}><strong>School:</strong> {schoolDisplayNameFor(student.school_id)}</p>}
                    <p style={{ margin: 0 }}><strong>Status:</strong> {student.programme_status || 'Active'}{student.is_locked ? ' | Profile locked' : ''}</p>
                  </div>

                  {isOwnerRole(userRole) && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: COLORS.textGray, textAlign: 'left' }}>
                      <div style={{ marginBottom: '6px' }}><strong>Assigned:</strong> {practitionerName(student.assigned_user_id)}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px', width: '100%' }}>
                        <button
                          onClick={() => openAssignment(student)}
                          style={{ border: `1px solid ${COLORS.borderGray}`, backgroundColor: COLORS.white, color: COLORS.navyBlue, borderRadius: '5px', padding: '4px 7px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          {student.assigned_user_id ? 'Change Practitioner' : 'Assign Practitioner'}
                        </button>
                        <button
                          onClick={() => openEditStudent(student)}
                          disabled={studentActionBusy === student.student_code}
                          style={{ border: `1px solid ${COLORS.borderGray}`, backgroundColor: COLORS.white, color: COLORS.navyBlue, borderRadius: '5px', padding: '4px 7px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => toggleStudentLock(student)}
                          disabled={studentActionBusy === student.student_code}
                          style={{ border: `1px solid ${COLORS.borderGray}`, backgroundColor: COLORS.white, color: COLORS.navyBlue, borderRadius: '5px', padding: '4px 7px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          {student.is_locked ? 'Unlock Profile' : 'Lock Profile'}
                        </button>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: 'auto', paddingTop: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px', alignItems: 'center', width: '100%' }}>
                      <button
                        onClick={() => viewStudentProfile(student)}
                        style={{
                          border: `1.5px solid ${COLORS.navyBlue}`, backgroundColor: '#F8FAFC', color: COLORS.navyBlue,
                          borderRadius: '5px', padding: '5px 9px', fontSize: '11px', fontWeight: '700',
                          textAlign: 'center', cursor: 'pointer', whiteSpace: 'nowrap'
                        }}
                      >
                        Screening
                      </button>
                      {isOwnerRole(userRole) ? (
                        <button
                          disabled={!student.learning_plans_ready}
                          onClick={(e) => { e.stopPropagation(); if (student.learning_plans_ready) openLearningPlans(student); }}
                          title={student.learning_plans_ready ? 'Open Learning Plan Generator' : 'Learning Plans are not ready yet'}
                          style={{
                            border: `1.5px solid ${student.learning_plans_ready ? COLORS.navyBlue : '#D1D5DB'}`,
                            backgroundColor: student.learning_plans_ready ? '#F8FAFC' : '#F9FAFB',
                            color: student.learning_plans_ready ? COLORS.navyBlue : '#9CA3AF',
                            borderRadius: '5px', padding: '5px 9px', fontSize: '11px', fontWeight: '700',
                            textAlign: 'center', cursor: student.learning_plans_ready ? 'pointer' : 'default', whiteSpace: 'nowrap'
                          }}
                        >
                          Learning Plans
                        </button>
                      ) : student.learning_plans_ready && student.learning_plans_visible && student.learning_plan_state === 'Active' ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); openLearningPlans(student); }}
                          style={{
                            border: `1.5px solid ${COLORS.green}`, backgroundColor: '#F0FDF4', color: '#166534',
                            borderRadius: '5px', padding: '5px 9px', fontSize: '11px', fontWeight: '700', textAlign: 'center', cursor: 'pointer', whiteSpace: 'nowrap'
                          }}
                        >
                          Learning Plans
                        </button>
                      ) : (
                        <button
                          disabled
                          title="Learning Plans awaiting owner review"
                          style={{ border: '1px solid #D1D5DB', backgroundColor: '#F9FAFB', color: COLORS.textGray, borderRadius: '5px', padding: '5px 9px', fontSize: '11px', fontWeight: '700', textAlign: 'center', cursor: 'default', whiteSpace: 'nowrap' }}
                        >
                          Learning Plans
                        </button>
                      )}

                      <button
                        disabled={!student.reports_available}
                        onClick={(e) => { e.stopPropagation(); if (student.reports_available) toggleReports(student); }}
                        title={student.reports_available ? 'Open Reports' : 'Reports placeholder - content to be added later'}
                        style={{
                          border: `1.5px solid ${student.reports_available ? COLORS.navyBlue : '#D1D5DB'}`,
                          backgroundColor: '#F8FAFC', color: student.reports_available ? COLORS.navyBlue : COLORS.textGray,
                          borderRadius: '5px', padding: '5px 9px', fontSize: '11px', fontWeight: '700',
                          textAlign: 'center', cursor: student.reports_available ? 'pointer' : 'default', whiteSpace: 'nowrap'
                        }}
                      >
                        Reports
                      </button>
                    </div>


                      {reportMenuStudentCode === student.student_code && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            marginTop: '8px', border: '1px solid #E5E7EB', borderRadius: '7px',
                            backgroundColor: '#FFFFFF', padding: '8px 10px', fontSize: '11px'
                          }}
                        >
                          {reportLoadingStudentCode === student.student_code ? (
                            <div style={{ color: COLORS.textGray }}>Loading report options…</div>
                          ) : reportAvailability[student.student_code]?.error ? (
                            <div style={{ color: '#B91C1C' }}>{reportAvailability[student.student_code].error}</div>
                          ) : (reportAvailability[student.student_code]?.options || []).map((option) => (
                            <div
                              key={option.report_key}
                              style={{
                                display: 'flex', justifyContent: 'space-between', gap: '10px',
                                padding: '6px 0', borderBottom: '1px solid #F3F4F6', alignItems: 'flex-start'
                              }}
                            >
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: '700', color: COLORS.textDark }}>{option.report_type}</div>
                                <div style={{ color: COLORS.textGray, marginTop: '2px', lineHeight: 1.3 }}>{option.detail}</div>
                              </div>
                              <div style={{
                                flexShrink: 0, fontWeight: '700', fontSize: '10px',
                                color: option.available ? '#166534' : COLORS.textGray
                              }}>
                                {option.status}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>

        <div style={{
          backgroundColor: COLORS.navyBlue,
          padding: '14px 24px 12px',
          borderTop: `3px solid ${COLORS.mustard}`,
          textAlign: 'center',
          flexShrink: 0
        }}>
          <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: COLORS.white }}>
            © 2026 NeuroWise Academy Foundation. All rights reserved.
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '10px', color: '#D1D5DB' }}>
            Build: {BUILD_VERSION}
          </p>
        </div>

        {pinResetOpen && isOwnerRole(userRole) && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 51, 102, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10002, padding: '16px'
          }}>
            <div style={{ backgroundColor: COLORS.white, borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '460px', boxShadow: '0 20px 45px rgba(0,0,0,0.18)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '18px' }}>
                <div>
                  <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.navyBlue, margin: 0 }}>Reset Team PIN</h3>
                  <p style={{ margin: '6px 0 0', fontSize: '13px', color: COLORS.textGray, lineHeight: 1.4 }}>Set a new 4-digit PIN. The existing PIN cannot be viewed or recovered.</p>
                </div>
                <button onClick={closePinReset} disabled={pinResetSaving} style={{ background: 'none', border: 'none', cursor: pinResetSaving ? 'default' : 'pointer', fontSize: '20px', color: COLORS.textGray, padding: '0 2px' }}>×</button>
              </div>

              <label style={styles.label}>NWA Practitioner</label>
              <select style={styles.select} value={pinResetUserId} onChange={(e) => { setPinResetUserId(e.target.value); setPinResetError(''); setPinResetMessage(''); }}>
                <option value="">Select practitioner</option>
                {practitioners.map((practitioner) => (
                  <option key={practitioner.user_id} value={practitioner.user_id}>
                    {practitioner.full_name || practitioner.user_id} - {practitioner.user_id}
                  </option>
                ))}
              </select>

              <label style={{ ...styles.label, marginTop: '16px' }}>New 4-digit PIN</label>
              <input
                type="password" inputMode="numeric" maxLength={4} autoComplete="new-password"
                style={styles.input} value={pinResetValue}
                onChange={(e) => setPinResetValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
              />

              <label style={{ ...styles.label, marginTop: '16px' }}>Confirm new PIN</label>
              <input
                type="password" inputMode="numeric" maxLength={4} autoComplete="new-password"
                style={styles.input} value={pinResetConfirm}
                onChange={(e) => setPinResetConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
              />

              {pinResetError && <div style={{ marginTop: '12px', color: '#B91C1C', fontSize: '13px' }}>{pinResetError}</div>}
              {pinResetMessage && <div style={{ marginTop: '12px', color: '#166534', fontSize: '13px', lineHeight: 1.4 }}>{pinResetMessage}</div>}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '22px' }}>
                <button onClick={closePinReset} disabled={pinResetSaving} style={{ border: `1px solid ${COLORS.borderGray}`, backgroundColor: COLORS.white, color: COLORS.navyBlue, borderRadius: '6px', padding: '9px 14px', fontWeight: '700', cursor: pinResetSaving ? 'default' : 'pointer' }}>Cancel</button>
                <button onClick={submitPinReset} disabled={pinResetSaving || !pinResetUserId || pinResetValue.length !== 4 || pinResetConfirm.length !== 4} style={{ border: 'none', backgroundColor: COLORS.orange, color: COLORS.white, borderRadius: '6px', padding: '9px 14px', fontWeight: '700', cursor: pinResetSaving ? 'default' : 'pointer', opacity: pinResetSaving ? 0.7 : 1 }}>
                  {pinResetSaving ? 'Saving...' : 'Reset PIN'}
                </button>
              </div>
            </div>
          </div>
        )}

        {deactivateOpen && isOwnerRole(userRole) && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 51, 102, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10003, padding: '16px'
          }}>
            <div style={{ backgroundColor: COLORS.white, borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '500px', boxShadow: '0 20px 45px rgba(0,0,0,0.18)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '18px' }}>
                <div>
                  <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#7F1D1D', margin: 0 }}>Deactivate Team Member</h3>
                  <p style={{ margin: '6px 0 0', fontSize: '13px', color: COLORS.textGray, lineHeight: 1.45 }}>This immediately removes system and school access. Historical students, screenings, learning plans and audit records are kept.</p>
                </div>
                <button onClick={closeDeactivateTeamMember} disabled={deactivateSaving} style={{ background: 'none', border: 'none', cursor: deactivateSaving ? 'default' : 'pointer', fontSize: '20px', color: COLORS.textGray, padding: '0 2px' }}>×</button>
              </div>

              <label style={styles.label}>NWA Practitioner</label>
              <select style={styles.select} value={deactivateUserId} onChange={(e) => { setDeactivateUserId(e.target.value); setDeactivateConfirmed(false); setDeactivateError(''); setDeactivateMessage(''); }}>
                <option value="">Select practitioner</option>
                {practitioners.filter((practitioner) => practitioner.user_id !== facilitatorId).map((practitioner) => (
                  <option key={practitioner.user_id} value={practitioner.user_id}>
                    {practitioner.full_name || practitioner.user_id} - {practitioner.user_id}
                  </option>
                ))}
              </select>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', marginTop: '18px', fontSize: '13px', color: COLORS.textDark, lineHeight: 1.4, cursor: deactivateSaving ? 'default' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={deactivateConfirmed}
                  disabled={deactivateSaving || !deactivateUserId}
                  onChange={(e) => setDeactivateConfirmed(e.target.checked)}
                  style={{ marginTop: '2px' }}
                />
                <span>I understand that this team member will no longer be able to log in, but their historical work will remain in the system.</span>
              </label>

              <div style={{ marginTop: '14px', padding: '10px 12px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '6px', color: '#991B1B', fontSize: '12px', lineHeight: 1.45 }}>
                All legacy MJ-FAC accounts are also blocked from login automatically. Their historical student records remain preserved.
              </div>

              {deactivateError && <div style={{ marginTop: '12px', color: '#B91C1C', fontSize: '13px' }}>{deactivateError}</div>}
              {deactivateMessage && <div style={{ marginTop: '12px', color: '#166534', fontSize: '13px', lineHeight: 1.4 }}>{deactivateMessage}</div>}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '22px' }}>
                <button onClick={closeDeactivateTeamMember} disabled={deactivateSaving} style={{ border: `1px solid ${COLORS.borderGray}`, backgroundColor: COLORS.white, color: COLORS.navyBlue, borderRadius: '6px', padding: '9px 14px', fontWeight: '700', cursor: deactivateSaving ? 'default' : 'pointer' }}>Cancel</button>
                <button onClick={submitDeactivateTeamMember} disabled={deactivateSaving || !deactivateUserId || !deactivateConfirmed} style={{ border: 'none', backgroundColor: '#B91C1C', color: COLORS.white, borderRadius: '6px', padding: '9px 14px', fontWeight: '700', cursor: deactivateSaving ? 'default' : 'pointer', opacity: (deactivateSaving || !deactivateUserId || !deactivateConfirmed) ? 0.65 : 1 }}>
                  {deactivateSaving ? 'Deactivating...' : 'Deactivate Access'}
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteStudent && isOwnerRole(userRole) && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 51, 102, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10001, padding: '16px'
          }}>
            <div style={{ backgroundColor: COLORS.white, borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '480px', boxShadow: '0 20px 45px rgba(0,0,0,0.18)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.navyBlue, margin: 0 }}>Delete Student</h3>
                  <p style={{ margin: '6px 0 0', fontSize: '14px', color: COLORS.textGray }}>{deleteStudent.full_name}</p>
                </div>
                <button onClick={() => setDeleteStudent(null)} disabled={studentActionBusy === deleteStudent.student_code} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                  <X size={22} color={COLORS.textGray} />
                </button>
              </div>

              <div style={{ border: '1px solid #FECACA', backgroundColor: '#FFF7F7', borderRadius: '8px', padding: '14px', color: '#7F1D1D', fontSize: '14px', lineHeight: 1.5 }}>
                This will archive this student's data first, then remove the student and their operational data from the live system.
              </div>

              <p style={{ margin: '14px 0 0', fontSize: '13px', color: COLORS.textGray }}>Only use this for confirmed dummy or test students.</p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '20px' }}>
                <button
                  onClick={() => setDeleteStudent(null)}
                  disabled={studentActionBusy === deleteStudent.student_code}
                  style={{ border: `1px solid ${COLORS.borderGray}`, backgroundColor: COLORS.white, color: COLORS.navyBlue, borderRadius: '6px', padding: '11px 12px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteTestStudent(deleteStudent)}
                  disabled={studentActionBusy === deleteStudent.student_code}
                  style={{ border: '1px solid #B91C1C', backgroundColor: '#B91C1C', color: COLORS.white, borderRadius: '6px', padding: '11px 12px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', opacity: studentActionBusy === deleteStudent.student_code ? 0.7 : 1 }}
                >
                  {studentActionBusy === deleteStudent.student_code ? 'Archiving and deleting...' : 'Archive and Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {editStudent && isOwnerRole(userRole) && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 51, 102, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, padding: '16px'
          }}>
            <div style={{ backgroundColor: COLORS.white, borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '760px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                <div>
                  <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.navyBlue, margin: 0 }}>Edit Student</h3>
                  <p style={{ margin: '5px 0 0', fontSize: '13px', color: COLORS.textGray }}>{editStudent.student_code}</p>
                </div>
                <button onClick={() => setEditStudent(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={22} color={COLORS.textGray} />
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '14px' }}>
                <div><label style={styles.label}>First name</label><input style={styles.input} value={editStudentForm.first_name || ''} onChange={(e) => setEditStudentForm({ ...editStudentForm, first_name: e.target.value })} /></div>
                <div><label style={styles.label}>Surname</label><input style={styles.input} value={editStudentForm.surname || ''} onChange={(e) => setEditStudentForm({ ...editStudentForm, surname: e.target.value })} /></div>
                <div><label style={styles.label}>Date of birth</label><input type="date" style={styles.input} value={editStudentForm.dob || ''} onChange={(e) => setEditStudentForm({ ...editStudentForm, dob: e.target.value })} /></div>
                <div><label style={styles.label}>Standard</label><input style={styles.input} value={editStudentForm.standard || ''} onChange={(e) => setEditStudentForm({ ...editStudentForm, standard: e.target.value })} /></div>
                <div>
                  <label style={styles.label}>School</label>
                  <select style={styles.select} value={editStudentForm.school_id || ''} onChange={(e) => setEditStudentForm({ ...editStudentForm, school_id: e.target.value, programme_id: '' })}>
                    <option value="">Select a school</option>
                    {schoolIds.map((school) => <option key={school.school_id} value={school.school_id}>{school.school_id} - {school.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Programme</label>
                  <select style={styles.select} value={editStudentForm.programme_id || ''} onChange={(e) => setEditStudentForm({ ...editStudentForm, programme_id: e.target.value })}>
                    <option value="">Select a programme</option>
                    {programmes.filter((p) => !editStudentForm.school_id || p.school_id === editStudentForm.school_id).map((p) => <option key={p.programme_id} value={p.programme_id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Gender</label>
                  <select style={styles.select} value={editStudentForm.gender || ''} onChange={(e) => setEditStudentForm({ ...editStudentForm, gender: e.target.value })}>
                    <option value="">Not yet known</option><option value="Female">Female</option><option value="Male">Male</option><option value="Other">Other</option>
                  </select>
                </div>
                <div><label style={styles.label}>% Attendance</label><input type="number" min="0" max="100" style={styles.input} value={editStudentForm.attendance_pct || ''} onChange={(e) => setEditStudentForm({ ...editStudentForm, attendance_pct: e.target.value })} /></div>
                <div><label style={styles.label}>Native language</label><input style={styles.input} value={editStudentForm.native_language || ''} onChange={(e) => setEditStudentForm({ ...editStudentForm, native_language: e.target.value })} /></div>
                <div><label style={styles.label}>Medium of instruction</label><input style={styles.input} value={editStudentForm.medium_of_instruction || ''} onChange={(e) => setEditStudentForm({ ...editStudentForm, medium_of_instruction: e.target.value })} /></div>
                <div>
                  <label style={styles.label}>First generation learner</label>
                  <select style={styles.select} value={editStudentForm.first_gen_learner || ''} onChange={(e) => setEditStudentForm({ ...editStudentForm, first_gen_learner: e.target.value })}>
                    <option value="">Not yet known</option><option value="Yes">Yes</option><option value="No">No</option>
                  </select>
                </div>
              </div>

              {editStudentError && <div style={{ ...styles.errorBox, marginTop: '14px' }}>{editStudentError}</div>}
              <button onClick={saveStudentProfile} disabled={editStudentSaving} style={{ ...styles.button, width: '100%', marginTop: '18px', opacity: editStudentSaving ? 0.7 : 1 }}>
                {editStudentSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {assignmentStudent && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 51, 102, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '16px'
          }}>
            <div style={{ backgroundColor: COLORS.white, borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '480px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                <div>
                  <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.navyBlue, margin: 0 }}>Assign NWA Practitioner</h3>
                  <p style={{ margin: '5px 0 0', fontSize: '13px', color: COLORS.textGray }}>{assignmentStudent.full_name}</p>
                </div>
                <button onClick={() => setAssignmentStudent(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={22} color={COLORS.textGray} />
                </button>
              </div>

              <label style={styles.label}>Responsible NWA Practitioner</label>
              <select style={styles.select} value={assignmentUserId} onChange={(e) => setAssignmentUserId(e.target.value)}>
                <option value="">Select a practitioner</option>
                {practitioners
                  .filter((p) => !assignmentStudent.school_id || p.school_ids?.includes(assignmentStudent.school_id))
                  .map((p) => (
                    <option key={p.user_id} value={p.user_id}>{p.full_name} - {p.role}</option>
                  ))}
              </select>

              {assignmentError && <div style={{ ...styles.errorBox, marginTop: '12px' }}>{assignmentError}</div>}

              <button
                onClick={saveAssignment}
                disabled={assignmentSaving}
                style={{ ...styles.button, width: '100%', marginTop: '18px', opacity: assignmentSaving ? 0.7 : 1 }}
              >
                {assignmentSaving ? 'Saving...' : 'Save Assignment'}
              </button>
            </div>
          </div>
        )}

        {showAddStudent && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 51, 102, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9998, padding: '16px'
          }}>
            <div style={{ backgroundColor: COLORS.white, borderRadius: '12px', padding: '32px', width: '100%', maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.navyBlue, margin: 0 }}>Add Student</h3>
                <button onClick={() => setShowAddStudent(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={22} color={COLORS.textGray} />
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
                  <div>
                    <label style={styles.label}>First name</label>
                    <input
                      style={styles.input}
                      value={newStudentForm.first_name}
                      onChange={(e) => setNewStudentForm({ ...newStudentForm, first_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={styles.label}>Surname</label>
                    <input
                      style={styles.input}
                      value={newStudentForm.surname}
                      onChange={(e) => setNewStudentForm({ ...newStudentForm, surname: e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={styles.label}>Date of birth</label>
                    <input
                      type="date"
                      style={styles.input}
                      value={newStudentForm.dob}
                      onChange={(e) => setNewStudentForm({ ...newStudentForm, dob: e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={styles.label}>Standard</label>
                    <select
                      style={styles.select}
                      value={newStudentForm.standard}
                      onChange={(e) => setNewStudentForm({ ...newStudentForm, standard: e.target.value })}
                    >
                      <option value="">Select standard</option>
                      <option value="6">6</option>
                      <option value="7">7</option>
                      <option value="8">8</option>
                      <option value="9">9</option>
                      <option value="10">10</option>
                      <option value="11">11</option>
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>Gender</label>
                    <select
                      style={styles.select}
                      value={newStudentForm.gender}
                      onChange={(e) => setNewStudentForm({ ...newStudentForm, gender: e.target.value })}
                    >
                      <option value="">Not yet known</option>
                      <option value="Female">Female</option>
                      <option value="Male">Male</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>% Attendance</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      style={styles.input}
                      value={newStudentForm.attendance_pct}
                      onChange={(e) => setNewStudentForm({ ...newStudentForm, attendance_pct: e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={styles.label}>Native language</label>
                    <select
                      style={styles.select}
                      value={newStudentForm.native_language}
                      onChange={(e) => setNewStudentForm({ ...newStudentForm, native_language: e.target.value })}
                    >
                      <option value="">Select native language</option>
                      <option value="Hindi">Hindi</option>
                      <option value="Kannada">Kannada</option>
                      <option value="Tamil">Tamil</option>
                      <option value="Bhojpuri">Bhojpuri</option>
                      <option value="Assamese">Assamese</option>
                      <option value="Nepali">Nepali</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>Medium of instruction</label>
                    <select
                      style={styles.select}
                      value={newStudentForm.medium_of_instruction}
                      onChange={(e) => setNewStudentForm({ ...newStudentForm, medium_of_instruction: e.target.value })}
                    >
                      <option value="">Select medium</option>
                      <option value="English">English</option>
                      <option value="Kannada">Kannada</option>
                      <option value="Hindi">Hindi</option>
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>First generation learner</label>
                    <select
                      style={styles.select}
                      value={newStudentForm.first_gen_learner}
                      onChange={(e) => setNewStudentForm({ ...newStudentForm, first_gen_learner: e.target.value })}
                    >
                      <option value="">Not yet known</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                {schoolIds.length > 0 && (
                  <div>
                    <label style={styles.label}>School (required)</label>
                    <select
                      style={styles.select}
                      value={newStudentForm.school_id}
                      onChange={(e) => setNewStudentForm({ ...newStudentForm, school_id: e.target.value, programme_id: '', assigned_user_id: isOwnerRole(userRole) ? facilitatorId : '' })}
                    >
                      <option value="">Select a school</option>
                      {schoolIds.map((school) => (
                        <option key={school.school_id} value={school.school_id}>{school.school_id} - {school.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {isOwnerRole(userRole) && (
                  <div>
                    <label style={styles.label}>Assigned NWA Practitioner (required)</label>
                    <select
                      style={styles.select}
                      value={newStudentForm.assigned_user_id}
                      onChange={(e) => setNewStudentForm({ ...newStudentForm, assigned_user_id: e.target.value })}
                    >
                      <option value="">Select a practitioner</option>
                      {practitioners
                        .filter((p) => !newStudentForm.school_id || p.school_ids?.includes(newStudentForm.school_id))
                        .map((p) => (
                          <option key={p.user_id} value={p.user_id}>{p.full_name} - {p.role}</option>
                        ))}
                    </select>
                  </div>
                )}

                {programmes.filter((p) => !newStudentForm.school_id || p.school_id === newStudentForm.school_id).length > 0 && (
                  <div>
                    <label style={styles.label}>Programme (required)</label>
                    <select
                      style={styles.select}
                      value={newStudentForm.programme_id}
                      onChange={(e) => setNewStudentForm({ ...newStudentForm, programme_id: e.target.value })}
                    >
                      <option value="">Select a programme</option>
                      {programmes
                        .filter((p) => !newStudentForm.school_id || p.school_id === newStudentForm.school_id)
                        .map((p) => (
                          <option key={p.programme_id} value={p.programme_id}>{p.name}</option>
                        ))}
                    </select>
                  </div>
                )}

                {addStudentError && <div style={styles.errorBox}>{addStudentError}</div>}

                <button
                  onClick={handleAddStudent}
                  disabled={addStudentSaving}
                  style={{ ...styles.button, opacity: addStudentSaving ? 0.7 : 1, marginTop: '8px' }}
                >
                  {addStudentSaving ? 'Adding...' : 'Add Student'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </>
    );
  }

  // STUDENT PROFILE VIEW
  if (view === 'student-profile') {
    const baselineReviewStateRaw = screeningReviewByTimepoint?.Baseline || { workflow_status: 'Draft', screener_statuses: {}, can_edit: true };
    const legacyCorrectionText = String(baselineReviewStateRaw.return_note || '').toLowerCase();
    const inferredCorrectionKey = baselineReviewStateRaw.correction_screener_key || (
      legacyCorrectionText.includes('learning style') ? 'learning-style' :
      legacyCorrectionText.includes('student passport') ? 'student-passport' :
      (TIMELINE_SCREENERS.find((item) => legacyCorrectionText.includes(String(item.title || '').toLowerCase()))?.key || '')
    );
    const baselineReviewState = { ...baselineReviewStateRaw, correction_screener_key: inferredCorrectionKey };
    const baselineWorkflowStatus = baselineReviewState.workflow_status || 'Draft';
    const baselineSubmissionMissing = baselineReviewState.submission_missing || [];
    const baselineReadyToSubmit = Boolean(baselineReviewState.ready_to_submit);
    return (
      <div style={styles.container}>
        {loading && <LoadingOverlay message={loadingMessage} />}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <button onClick={() => setView('dashboard')} style={{ ...styles.logoutButton, backgroundColor: 'transparent', border: 'none', color: COLORS.white }}>
              <ArrowLeft size={20} />
              Back to Students
            </button>
            <button onClick={handleLogout} style={styles.logoutButton}>
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>

        <div style={{ maxWidth: '896px', margin: '0 auto', padding: '24px' }}>
          <div style={{ ...styles.card, marginBottom: '16px', padding: '14px 20px', cursor: 'default' }}>
            <h1 style={{ fontSize: '24px', lineHeight: 1.15, fontWeight: 'bold', color: COLORS.navyBlue, margin: '0 0 6px' }}>{currentStudent.full_name}</h1>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', color: COLORS.textGray, fontSize: '14px' }}>
              <p style={{ margin: 0 }}><strong>DOB:</strong> {currentStudent.dob}</p>
              <p style={{ margin: 0 }}><strong>CA:</strong> {calculateAge(currentStudent.dob, currentStudent.createdAt)} yrs</p>
              <p style={{ margin: 0 }}><strong>Standard:</strong> {currentStudent.standard}</p>
            </div>
          </div>

          {/* NWA assessment cards. Baseline remains the first timepoint on
              each individual screener; the old teacher Baseline/Post-Screening
              workflow is deliberately no longer shown on this profile. */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: '16px',
            marginBottom: '24px'
          }}>
            {/* NWA screener cards in the agreed student-profile order. Annual-only
                cards use the same four-row footprint for visual consistency; completing
                the annual administration covers all four displayed timeline rows. */}
            {TIMELINE_SCREENERS.map((s) => (
              <React.Fragment key={s.key}>
                {s.key === 'learning-style' && (
                  <PassportTimelineCard
                    passportData={passportData}
                    passportSaved={passportSaved}
                    correctionEditable={baselineWorkflowStatus === 'Returned for correction' && baselineReviewState.correction_screener_key === 'student-passport'}
                    onOpen={() => {
                      const passportCorrection = baselineWorkflowStatus === 'Returned for correction' && baselineReviewState.correction_screener_key === 'student-passport';
                      setFormLocked(passportSaved && !passportCorrection);
                      setView('passport');
                    }}
                  />
                )}
                <TimepointScreenerCard
                  emoji={s.emoji}
                  title={s.title}
                  screenerKey={s.key}
                  screenerName={s.screenerName}
                  sessionData={sessionData}
                  reviewSchedule={buildEffectiveReviewSchedule(sessionData, reviewSchedule)}
                  reviewStateByTimepoint={{ ...screeningReviewByTimepoint, Baseline: baselineReviewState }}
                  onReviewDateChange={handleReviewDateChange}
                  onAdministrationStatusChange={handleAdministrationStatusChange}
                  reviewBusy={screeningReviewBusy}
                  annualOnly={!!s.annualOnly}
                  onAdd={(tp) => startTimepointScreener(s.viewName, tp)}
                />
              </React.Fragment>
            ))}
          </div>

          <div style={{ ...styles.card, cursor: 'default', padding: '16px 18px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'left' }}>
                <h2 style={{ margin: 0, fontSize: '16px', color: COLORS.navyBlue }}>Screening review</h2>
                <div style={{ marginTop: '4px', fontSize: '12px', color: COLORS.textGray }}>
                  Status: <strong style={{ color: baselineWorkflowStatus === 'Approved' ? '#4F6B57' : baselineWorkflowStatus === 'Returned for correction' ? '#9A6B3F' : COLORS.textDark }}>{baselineWorkflowStatus}</strong>
                </div>
              </div>

              {(baselineWorkflowStatus === 'Draft' || baselineWorkflowStatus === 'Returned for correction') && (
                <button
                  onClick={handleSubmitScreeningReview}
                  disabled={screeningReviewBusy}
                  style={{ border: 'none', backgroundColor: COLORS.orange, color: COLORS.white, borderRadius: '6px', padding: '8px 12px', fontSize: '12px', fontWeight: '700', cursor: screeningReviewBusy ? 'default' : 'pointer', opacity: screeningReviewBusy ? 0.7 : 1, whiteSpace: 'nowrap' }}
                >
                  {baselineWorkflowStatus === 'Returned for correction' ? 'Resubmit for Review' : 'Submit for Review'}
                </button>
              )}

              {baselineWorkflowStatus === 'Submitted for Review' && isOwnerRole(userRole) && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    onClick={loadScreeningReviewEvidence}
                    disabled={screeningReviewEvidenceLoading}
                    style={{ border: `1px solid ${COLORS.borderGray}`, backgroundColor: COLORS.white, color: COLORS.navyBlue, borderRadius: '6px', padding: '7px 11px', fontSize: '12px', fontWeight: '700', cursor: screeningReviewEvidenceLoading ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {screeningReviewEvidenceLoading ? 'Loading...' : showScreeningReviewEvidence ? 'Hide Review' : 'Review Screening'}
                  </button>
                  <button
                    onClick={() => handleScreeningReviewDecision('approve')}
                    disabled={screeningReviewBusy}
                    style={{ border: '1px solid #B7CDBD', backgroundColor: '#EEF6F0', color: '#3F634A', borderRadius: '6px', padding: '7px 11px', fontSize: '12px', fontWeight: '700', cursor: screeningReviewBusy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => { setReviewCorrectionMode('return'); setReturnForCorrectionScreenerKey(''); setShowReturnForCorrection(true); }}
                    disabled={screeningReviewBusy}
                    style={{ border: `1px solid ${COLORS.borderGray}`, backgroundColor: COLORS.white, color: COLORS.navyBlue, borderRadius: '6px', padding: '7px 11px', fontSize: '12px', fontWeight: '700', cursor: screeningReviewBusy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Return for correction
                  </button>
                </div>
              )}
            </div>

            {baselineWorkflowStatus === 'Returned for correction' && baselineReviewState.return_note && (
              <div style={{ marginTop: '10px', padding: '9px 10px', backgroundColor: '#FFF8EF', border: '1px solid #F2D5AE', borderRadius: '6px', color: '#7A5735', fontSize: '12px', textAlign: 'left' }}>
                {baselineReviewState.correction_screener_key && <><strong>Correction:</strong> {baselineReviewState.correction_screener_name || (baselineReviewState.correction_screener_key === 'student-passport' ? 'Student Passport' : TIMELINE_SCREENERS.find((item) => item.key === baselineReviewState.correction_screener_key)?.title || baselineReviewState.correction_screener_key)}<br /></>}
                <strong>Correction note:</strong> {baselineReviewState.return_note}
              </div>
            )}

            {baselineWorkflowStatus === 'Submitted for Review' && !isOwnerRole(userRole) && (
              <div style={{ marginTop: '10px', fontSize: '12px', color: COLORS.textGray, textAlign: 'left' }}>Submitted for owner review. Screening is locked until it is approved or returned.</div>
            )}

            {baselineWorkflowStatus === 'Approved' && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ padding: '9px 10px', backgroundColor: '#EEF6F0', border: '1px solid #C8DCCF', borderRadius: '6px', color: '#3F634A', fontSize: '12px', textAlign: 'left' }}>
                  ✓ Approved - screening is locked. Learning Plans can now be generated by the System Owner.
                </div>
                {isOwnerRole(userRole) && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={loadScreeningReviewEvidence}
                      disabled={screeningReviewEvidenceLoading}
                      style={{ border: `1px solid ${COLORS.borderGray}`, backgroundColor: COLORS.white, color: COLORS.navyBlue, borderRadius: '6px', padding: '7px 11px', fontSize: '12px', fontWeight: '700', cursor: screeningReviewEvidenceLoading ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {screeningReviewEvidenceLoading ? 'Loading...' : showScreeningReviewEvidence ? 'Hide Review' : 'View Approved Screening'}
                    </button>
                    <button
                      onClick={() => { setReviewCorrectionMode('reopen'); setReturnForCorrectionScreenerKey(''); setShowReturnForCorrection(true); }}
                      disabled={screeningReviewBusy}
                      style={{ border: `1px solid ${COLORS.borderGray}`, backgroundColor: COLORS.white, color: '#7A5735', borderRadius: '6px', padding: '7px 11px', fontSize: '12px', fontWeight: '700', cursor: screeningReviewBusy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
                    >
                      Reopen for correction
                    </button>
                  </div>
                )}
              </div>
            )}

            {(baselineWorkflowStatus === 'Draft' || baselineWorkflowStatus === 'Returned for correction') && !baselineReadyToSubmit && baselineSubmissionMissing.length > 0 && (
              <div style={{ marginTop: '10px', fontSize: '11px', color: COLORS.textGray, textAlign: 'left' }}>
                Before submission, complete or mark Not administered: {baselineSubmissionMissing.join(', ')}
              </div>
            )}

            {showReturnForCorrection && isOwnerRole(userRole) && ((reviewCorrectionMode === 'return' && baselineWorkflowStatus === 'Submitted for Review') || (reviewCorrectionMode === 'reopen' && baselineWorkflowStatus === 'Approved')) && (
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <select
                  value={returnForCorrectionScreenerKey}
                  onChange={(e) => setReturnForCorrectionScreenerKey(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${COLORS.borderGray}`, borderRadius: '6px', padding: '8px 10px', fontFamily: 'inherit', fontSize: '12px', color: COLORS.textDark, backgroundColor: COLORS.white }}
                >
                  <option value="">Select the screener to correct</option>
                  <option value="student-passport">Student Passport</option>
                  {TIMELINE_SCREENERS.map((item) => <option key={item.key} value={item.key}>{item.title}</option>)}
                </select>
                <textarea
                  value={returnForCorrectionNote}
                  onChange={(e) => setReturnForCorrectionNote(e.target.value)}
                  placeholder={reviewCorrectionMode === 'reopen' ? 'Briefly say why the approved screening needs reopening' : 'Briefly say what needs correcting'}
                  rows={2}
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', border: `1px solid ${COLORS.borderGray}`, borderRadius: '6px', padding: '8px 10px', fontFamily: 'inherit', fontSize: '12px', color: COLORS.textDark }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <button
                    onClick={() => { setShowReturnForCorrection(false); setReturnForCorrectionNote(''); setReturnForCorrectionScreenerKey(''); }}
                    style={{ border: `1px solid ${COLORS.borderGray}`, backgroundColor: COLORS.white, color: COLORS.textGray, borderRadius: '6px', padding: '6px 10px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleScreeningReviewDecision(reviewCorrectionMode === 'reopen' ? 'reopen' : 'return')}
                    disabled={screeningReviewBusy || !returnForCorrectionScreenerKey || !returnForCorrectionNote.trim()}
                    style={{ border: 'none', backgroundColor: COLORS.orange, color: COLORS.white, borderRadius: '6px', padding: '6px 10px', fontSize: '11px', fontWeight: '700', cursor: screeningReviewBusy || !returnForCorrectionScreenerKey || !returnForCorrectionNote.trim() ? 'default' : 'pointer', opacity: screeningReviewBusy || !returnForCorrectionScreenerKey || !returnForCorrectionNote.trim() ? 0.6 : 1 }}
                  >
                    {reviewCorrectionMode === 'reopen' ? 'Reopen' : 'Return'}
                  </button>
                </div>
              </div>
            )}

            {showScreeningReviewEvidence && screeningReviewEvidence && isOwnerRole(userRole) && (
              <div style={{ marginTop: '12px', borderTop: `1px solid ${COLORS.borderGray}`, paddingTop: '12px', textAlign: 'left' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: COLORS.navyBlue, marginBottom: '8px' }}>Read-only screening evidence</div>

                {screeningReviewEvidence.passport && Object.keys(screeningReviewEvidence.passport).length > 0 && (
                  <details style={{ marginBottom: '8px', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '8px 10px', backgroundColor: '#FAFBFC' }}>
                    <summary style={{ cursor: 'pointer', fontSize: '12px', fontWeight: '700', color: COLORS.textDark }}>Student Passport - Completed</summary>
                    <div style={{ marginTop: '8px', display: 'grid', gap: '5px', fontSize: '11px', color: COLORS.textGray }}>
                      {Object.entries(screeningReviewEvidence.passport).map(([label, value]) => (
                        <div key={label}><strong style={{ color: COLORS.textDark }}>{label}:</strong> {String(value)}</div>
                      ))}
                    </div>
                  </details>
                )}

                {(screeningReviewEvidence.screeners || []).map((area) => (
                  <details key={area.key} style={{ marginBottom: '8px', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '8px 10px', backgroundColor: '#FAFBFC' }}>
                    <summary style={{ cursor: 'pointer', fontSize: '12px', fontWeight: '700', color: COLORS.textDark }}>
                      {area.name} - {area.status}
                    </summary>
                    {area.status === 'Not administered' ? (
                      <div style={{ marginTop: '7px', fontSize: '11px', color: COLORS.textGray }}>/ Not administered</div>
                    ) : area.administrations?.length ? (
                      <div style={{ marginTop: '8px', display: 'grid', gap: '8px' }}>
                        {area.administrations.map((admin, adminIndex) => (
                          <div key={`${area.key}-${adminIndex}`} style={{ padding: '8px', borderRadius: '5px', backgroundColor: COLORS.white, border: '1px solid #EEF0F2' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', fontSize: '11px', color: COLORS.textGray }}>
                              {admin.screening_date && <span><strong>Date:</strong> {admin.screening_date}</span>}
                              {admin.form && <span><strong>Area:</strong> {admin.form}</span>}
                              {String(admin.raw_score ?? '').trim() && <span><strong>Raw score:</strong> {String(admin.raw_score)}</span>}
                              {String(admin.standard_score ?? '').trim() && <span><strong>Standard score:</strong> {String(admin.standard_score)}</span>}
                              {String(admin.percentile ?? '').trim() && <span><strong>Percentile:</strong> {String(admin.percentile)}</span>}
                              {admin.band_descriptor && <span><strong>Band:</strong> {admin.band_descriptor}</span>}
                              {String(admin.error_count ?? '').trim() && <span><strong>Errors:</strong> {String(admin.error_count)}</span>}
                            </div>
                            {admin.notes && <div style={{ marginTop: '6px', fontSize: '11px', color: COLORS.textDark }}><strong>Notes:</strong> {admin.notes}</div>}
                            {admin.responses?.length > 0 && (
                              <details style={{ marginTop: '7px' }}>
                                <summary style={{ cursor: 'pointer', fontSize: '11px', fontWeight: '700', color: COLORS.navyBlue }}>View recorded responses ({admin.responses.length})</summary>
                                <div style={{ marginTop: '6px', maxHeight: '260px', overflowY: 'auto', borderTop: '1px solid #EEF0F2' }}>
                                  {admin.responses.map((response, responseIndex) => (
                                    <div key={responseIndex} style={{ padding: '6px 0', borderBottom: '1px solid #F3F4F6', fontSize: '10.5px', color: COLORS.textGray }}>
                                      <strong style={{ color: COLORS.textDark }}>{response.item_number || responseIndex + 1}{response.item ? ` - ${response.item}` : ''}</strong>
                                      {response.student_response && <span> | Response: {response.student_response}</span>}
                                      {response.rating !== '' && response.rating !== null && <span> | Rating: {String(response.rating)}</span>}
                                      {response.status && <span> | {response.status}</span>}
                                      {response.error_type && <span> | Error: {response.error_type}</span>}
                                      {response.notes && <span> | Notes: {response.notes}</span>}
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ marginTop: '7px', fontSize: '11px', color: COLORS.textGray }}>No completed evidence recorded.</div>
                    )}
                  </details>
                ))}
              </div>
            )}

            {screeningReviewError && (
              <div style={{ marginTop: '10px', color: '#B91C1C', fontSize: '12px', textAlign: 'left' }}>{screeningReviewError}</div>
            )}
          </div>
        </div>
      </div>

    );
  }

  if (view === 'learning-plans' && currentStudent) {
    return (
      <LearningPlansScreen
        student={currentStudent}
        apiUrl={API_URL}
        userRole={userRole}
        onBack={async () => { await refreshStudents(); setView('dashboard'); }}
      />
    );
  }

  // Resolve school/programme from the student's own registration, not from
  // the first school in the logged-in user's access list. The IDs are also
  // sent to the backend so Sessions and Screening Summary keep their keys.
  const currentSchoolId = currentStudent?.school_id || '';
  const currentSchoolName = schoolIds.find((school) => school.school_id === currentSchoolId)?.name
    || (schoolIds.length === 1 ? (schoolIds[0]?.name || '') : '');
  const currentProgrammeId = currentStudent?.programme_id || '';
  const currentProgrammeName = programmes.find((programme) => programme.programme_id === currentProgrammeId)?.name || '';

  // HAST-2 VIEW
  if (view === 'hast2') {
    return (
      <Hast2Screening
        student={currentStudent}
        apiUrl={API_URL}
        facilitatorId={facilitatorId}
        facilitatorName={facilitatorName}
        schoolId={currentSchoolId}
        schoolName={currentSchoolName}
        programmeId={currentProgrammeId}
        programmeName={currentProgrammeName}
        initialTimepoint={pendingTimepoint}
        onBack={returnToStudentScreeningDashboard}
        onSaved={() => {
          // Refresh this student's session history (Screening Sessions),
          // which is what the dashboard cards actually read, so a
          // just-completed timepoint shows as done straight away.
          fetchWithTimeout(`${API_URL}/api/sessions/${currentStudent.student_code}`)
            .then((r) => r.json())
            .then((d) => setSessionData(d.sessions || []))
            .catch(() => {});
        }}
      />
    );
  }

  // NUMERACY VIEW
  if (view === 'numeracy') {
    return (
      <NumeracyScreening
        student={currentStudent}
        apiUrl={API_URL}
        facilitatorId={facilitatorId}
        facilitatorName={facilitatorName}
        schoolId={currentSchoolId}
        schoolName={currentSchoolName}
        programmeId={currentProgrammeId}
        programmeName={currentProgrammeName}
        initialTimepoint={pendingTimepoint}
        onBack={returnToStudentScreeningDashboard}
        onSaved={() => {
          refreshStage23AfterScreeningSave();
        }}
      />
    );
  }

  // CONFIDENCE LEVELS VIEW
  if (view === 'confidence') {
    return (
      <ConfidenceScreening
        student={currentStudent}
        apiUrl={API_URL}
        facilitatorId={facilitatorId}
        facilitatorName={facilitatorName}
        schoolId={currentSchoolId}
        schoolName={currentSchoolName}
        programmeId={currentProgrammeId}
        programmeName={currentProgrammeName}
        initialTimepoint={pendingTimepoint}
        correctionMode={screeningReviewByTimepoint?.Baseline?.workflow_status === 'Returned for correction' && (screeningReviewByTimepoint?.Baseline?.correction_screener_key === 'confidence' || String(screeningReviewByTimepoint?.Baseline?.return_note || '').toLowerCase().includes('confidence'))}
        onBack={returnToStudentScreeningDashboard}
        onSaved={() => {
          refreshStage23AfterScreeningSave();
        }}
      />
    );
  }

  // WRITING ASSESSMENT VIEW
  if (view === 'writing') {
    return (
      <WritingScreening
        student={currentStudent}
        apiUrl={API_URL}
        facilitatorId={facilitatorId}
        facilitatorName={facilitatorName}
        schoolId={currentSchoolId}
        schoolName={currentSchoolName}
        programmeId={currentProgrammeId}
        programmeName={currentProgrammeName}
        initialTimepoint={pendingTimepoint}
        correctionMode={screeningReviewByTimepoint?.Baseline?.workflow_status === 'Returned for correction' && (screeningReviewByTimepoint?.Baseline?.correction_screener_key === 'writing' || String(screeningReviewByTimepoint?.Baseline?.return_note || '').toLowerCase().includes('writing'))}
        onBack={returnToStudentScreeningDashboard}
        onSaved={() => {
          refreshStage23AfterScreeningSave();
        }}
      />
    );
  }

  // LEARNING STYLE VIEW
  if (view === 'learning-style') {
    return (
      <LearningStyleScreening
        student={currentStudent}
        apiUrl={API_URL}
        facilitatorId={facilitatorId}
        facilitatorName={facilitatorName}
        schoolId={currentSchoolId}
        schoolName={currentSchoolName}
        programmeId={currentProgrammeId}
        programmeName={currentProgrammeName}
        initialTimepoint={pendingTimepoint}
        correctionMode={screeningReviewByTimepoint?.Baseline?.workflow_status === 'Returned for correction' && (screeningReviewByTimepoint?.Baseline?.correction_screener_key === 'learning-style' || String(screeningReviewByTimepoint?.Baseline?.return_note || '').toLowerCase().includes('learning style'))}
        onBack={returnToStudentScreeningDashboard}
        onSaved={() => {
          refreshStage23AfterScreeningSave();
        }}
      />
    );
  }


  // SDQ VIEW
  if (view === 'sdq') {
    return (
      <SDQScreening
        student={currentStudent}
        apiUrl={API_URL}
        facilitatorId={facilitatorId}
        facilitatorName={facilitatorName}
        schoolId={currentSchoolId}
        schoolName={currentSchoolName}
        programmeId={currentProgrammeId}
        programmeName={currentProgrammeName}
        initialTimepoint={pendingTimepoint}
        onBack={returnToStudentScreeningDashboard}
        onSaved={() => {
          refreshStage23AfterScreeningSave();
        }}
      />
    );
  }

  // NEURODIVERSITY CHECKLIST VIEW
  if (view === 'neurodiversity') {
    return (
      <NeurodiversityScreening
        student={currentStudent}
        apiUrl={API_URL}
        facilitatorId={facilitatorId}
        facilitatorName={facilitatorName}
        schoolId={currentSchoolId}
        schoolName={currentSchoolName}
        programmeId={currentProgrammeId}
        programmeName={currentProgrammeName}
        initialTimepoint={pendingTimepoint}
        onBack={returnToStudentScreeningDashboard}
        onSaved={() => {
          refreshStage23AfterScreeningSave();
        }}
      />
    );
  }

  // MOTIVATION / MALS VIEW
  if (view === 'mals') {
    return (
      <MALSScreening
        student={currentStudent}
        apiUrl={API_URL}
        facilitatorId={facilitatorId}
        facilitatorName={facilitatorName}
        schoolId={currentSchoolId}
        schoolName={currentSchoolName}
        programmeId={currentProgrammeId}
        programmeName={currentProgrammeName}
        initialTimepoint={pendingTimepoint}
        onBack={returnToStudentScreeningDashboard}
        onSaved={() => {
          refreshStage23AfterScreeningSave();
        }}
      />
    );
  }

  // WRAT4 SINGLE WORD READING VIEW
  if (view === 'wrat4-word') {
    return (
      <WRAT4Screening
        subtest="word_reading"
        student={currentStudent}
        apiUrl={API_URL}
        facilitatorId={facilitatorId}
        facilitatorName={facilitatorName}
        schoolId={currentSchoolId}
        schoolName={currentSchoolName}
        programmeId={currentProgrammeId}
        programmeName={currentProgrammeName}
        initialTimepoint={pendingTimepoint}
        onBack={returnToStudentScreeningDashboard}
        onSaved={() => {
          refreshStage23AfterScreeningSave();
        }}
      />
    );
  }

  // WRAT4 SENTENCE READING COMPREHENSION VIEW
  if (view === 'wrat4-sentence') {
    return (
      <WRAT4Screening
        subtest="sentence_comprehension"
        student={currentStudent}
        apiUrl={API_URL}
        facilitatorId={facilitatorId}
        facilitatorName={facilitatorName}
        schoolId={currentSchoolId}
        schoolName={currentSchoolName}
        programmeId={currentProgrammeId}
        programmeName={currentProgrammeName}
        initialTimepoint={pendingTimepoint}
        onBack={returnToStudentScreeningDashboard}
        onSaved={() => {
          refreshStage23AfterScreeningSave();
        }}
      />
    );
  }


  // TOWRE-2 VIEW
  if (view === 'towre2') {
    return (
      <TOWRE2Screening
        student={currentStudent}
        apiUrl={API_URL}
        facilitatorId={facilitatorId}
        facilitatorName={facilitatorName}
        schoolId={currentSchoolId}
        schoolName={currentSchoolName}
        programmeId={currentProgrammeId}
        programmeName={currentProgrammeName}
        initialTimepoint={pendingTimepoint}
        onBack={returnToStudentScreeningDashboard}
        onSaved={() => {
          refreshStage23AfterScreeningSave();
        }}
      />
    );
  }

  // PASSPORT VIEW
  if (view === 'passport') {
    return (
      <div style={styles.container}>
        {loading && <LoadingOverlay message={loadingMessage} />}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <button onClick={() => { if (!passportSaved) setPassportData({}); setView('student-profile'); }} style={{ ...styles.logoutButton, backgroundColor: 'transparent', border: 'none', color: COLORS.white }}>
              <ArrowLeft size={20} />
              Back to Profile
            </button>
            <button onClick={handleLogout} style={styles.logoutButton}>
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>

        <div style={{ maxWidth: '980px', margin: '0 auto', padding: '20px' }}>
          <div style={{ ...styles.card, padding: '24px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS.navyBlue, margin: '0 0 14px' }}>Student Passport</h1>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', padding: '10px 12px', backgroundColor: '#F8FAFC', borderRadius: '8px', marginBottom: '18px' }}>
              <div><span style={{ fontSize: '11px', color: COLORS.textGray }}>Name</span><div style={{ fontSize: '14px', fontWeight: '700', color: COLORS.textDark }}>{currentStudent.full_name}</div></div>
              <div><span style={{ fontSize: '11px', color: COLORS.textGray }}>Standard</span><div style={{ fontSize: '14px', fontWeight: '700', color: COLORS.textDark }}>{currentStudent.standard || '-'}</div></div>
              <div><span style={{ fontSize: '11px', color: COLORS.textGray }}>Age</span><div style={{ fontSize: '14px', fontWeight: '700', color: COLORS.textDark }}>{calculateAge(currentStudent.dob)} yrs</div></div>
              <div><span style={{ fontSize: '11px', color: COLORS.textGray }}>Date</span><div style={{ fontSize: '14px', fontWeight: '700', color: COLORS.textDark }}>{passportSaved && passportData.admin_date ? passportData.admin_date : new Date().toLocaleDateString('en-GB')}</div></div>
            </div>

            <form onSubmit={savePassport} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '14px' }}>
                {[
                  { label: 'What I am good at and love doing', field: 'strengths' },
                  { label: 'What I look forward to the most at school', field: 'looks_forward' },
                  { label: "What I don't enjoy doing", field: 'dislikes' },
                  { label: 'When I feel upset, I...', field: 'concerns' },
                  { label: 'I can find it difficult to...', field: 'difficulties' },
                  { label: 'I sometimes need help when I...', field: 'help_needed' }
                ].map(({ label, field }) => (
                  <div key={field}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: COLORS.navyBlue, marginBottom: '6px' }}>{label} *</label>
                    <textarea value={passportData[field] || ''} onChange={(e) => setPassportData({ ...passportData, [field]: e.target.value })} style={{ ...styles.textarea, minHeight: '72px' }} rows="2" required disabled={formLocked} />
                  </div>
                ))}
              </div>

              <div style={{ borderTop: `1px solid ${COLORS.borderGray}`, paddingTop: '14px' }}>
                <h2 style={{ fontSize: '16px', color: COLORS.navyBlue, margin: '0 0 10px' }}>Three things that teachers can do to help me learn are...</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px' }}>
                  {[1, 2, 3].map((n) => (
                    <div key={n}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: COLORS.textGray, marginBottom: '5px' }}>{n}.</label>
                      <textarea value={passportData[`teacher_help_${n}`] || ''} onChange={(e) => setPassportData({ ...passportData, [`teacher_help_${n}`]: e.target.value })} style={{ ...styles.textarea, minHeight: '68px' }} rows="2" required disabled={formLocked} />
                    </div>
                  ))}
                </div>
              </div>

              {error && <div style={styles.errorBox}>{error}</div>}

              {formLocked ? (
                <div style={styles.successBox}>
                  <CheckCircle size={20} />
                  Saved
                </div>
              ) : (
                <button type="submit" disabled={loading} style={{ ...styles.button, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: loading ? 0.6 : 1 }}>
                  <Save size={20} />
                  {loading ? 'Saving...' : 'Save Passport'}
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    );
  }

  // SCREENING FORM VIEW
  if (view === 'screening') {
    const currentSectionData = renderScreeningSection();
    const progress = Math.round((currentSection / 12) * 100);

    return (
      <div style={styles.container}>
        {loading && <LoadingOverlay message={loadingMessage} />}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <button onClick={() => { if (window.confirm('Are you sure? Unsaved data will be lost.')) { setView('student-profile'); setFormData(INITIAL_FORM_DATA); setCurrentSection(1); setFormLocked(false); } }} style={{ ...styles.logoutButton, backgroundColor: 'transparent', border: 'none', color: COLORS.white }}>
              <ArrowLeft size={20} />
              Cancel
            </button>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontWeight: '600', fontSize: '18px', marginBottom: '2px' }}>{currentStudent.full_name}</h2>
              <p style={{ fontSize: '13px', color: '#D1D5DB', fontWeight: '400' }}>{formData.screeningType} Screening</p>
            </div>
            <button onClick={handleLogout} style={styles.logoutButton}>
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>

        <div style={{ backgroundColor: COLORS.white, borderBottom: `1px solid ${COLORS.borderGray}` }}>
          <div style={{ maxWidth: '896px', margin: '0 auto', padding: '12px 16px' }}>
            <div style={{ marginBottom: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: COLORS.textGray, marginBottom: '6px' }}>
                <span>Section {currentSection} of 12: {currentSectionData.title}</span>
                <span>{progress}%</span>
              </div>
              <div style={{ width: '100%', backgroundColor: '#E5E7EB', borderRadius: '9999px', height: '6px' }}>
                <div style={{ backgroundColor: COLORS.orange, height: '6px', borderRadius: '9999px', transition: 'width 0.3s', width: `${progress}%` }} />
              </div>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: '896px', margin: '0 auto', padding: '24px' }}>
          <div style={{ ...styles.card, padding: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '24px' }}>
              {currentSectionData.title}
            </h2>

            {currentSectionData.fields}

            {error && <div style={{ ...styles.errorBox, marginTop: '16px' }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px', paddingTop: '24px', borderTop: `1px solid ${COLORS.borderGray}` }}>
              <button onClick={previousSection} disabled={currentSection === 1} style={{ ...styles.button, backgroundColor: '#D1D5DB', color: COLORS.textDark, display: 'flex', alignItems: 'center', gap: '8px', opacity: currentSection === 1 ? 0.5 : 1, cursor: currentSection === 1 ? 'not-allowed' : 'pointer' }}>
                <ArrowLeft size={20} />
                Previous
              </button>

              {currentSection === 12 ? (
                <button onClick={saveScreening} disabled={loading || formLocked} style={{ ...styles.button, backgroundColor: COLORS.green, display: 'flex', alignItems: 'center', gap: '8px', opacity: loading || formLocked ? 0.6 : 1 }}>
                  <Save size={20} />
                  {loading ? 'Saving...' : 'Complete & Save'}
                </button>
              ) : (
                <button onClick={nextSection} style={{ ...styles.button, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Next
                  <ArrowRight size={20} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // SCREENING RESULTS VIEW
  if (view === 'screening-results' && currentScreening) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <button onClick={() => setView('student-profile')} style={{ ...styles.logoutButton, backgroundColor: 'transparent', border: 'none', color: COLORS.white }}>
              <ArrowLeft size={20} />
              Back to Profile
            </button>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontWeight: '600' }}>{currentStudent.full_name}</h2>
              <p style={{ fontSize: '14px', color: '#D1D5DB' }}>{currentScreening.screening_type} Screening Results</p>
            </div>
            <button onClick={handleLogout} style={styles.logoutButton}>
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>

        <div style={{ maxWidth: '896px', margin: '0 auto', padding: '24px' }}>
          {/* Header Card */}
          <div style={{ ...styles.card, padding: '24px', marginBottom: '24px', backgroundColor: '#EFF6FF' }}>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '12px' }}>
              {currentScreening.screening_type} Screening
            </h1>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '14px' }}>
              <div>
                <p style={{ color: COLORS.textGray }}>Screening Date</p>
                <p style={{ fontWeight: '600' }}>{displayValue(currentScreening.screening_date)}</p>
              </div>
              <div>
                <p style={{ color: COLORS.textGray }}>Gender</p>
                <p style={{ fontWeight: '600' }}>{displayValue(currentScreening.gender)}</p>
              </div>
              <div>
                <p style={{ color: COLORS.textGray }}>Attendance</p>
                <p style={{ fontWeight: '600' }}>{displayPercent(currentScreening.attendance)}</p>
              </div>
            </div>
          </div>

                    {/* Two-column layout for Sections 1-9 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(520px, 1fr))', gap: '16px' }}>

{/* Section 1: Background Information */}
          <div style={{ ...styles.card, padding: '24px', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '16px', borderBottom: `2px solid ${COLORS.orange}`, paddingBottom: '8px', textAlign: 'center' }}>
              Section 1: Background Information
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Native Language:</strong> {displayValue(currentScreening.nativeLanguage)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Medium of Instruction:</strong> {displayValue(currentScreening.mediumOfInstruction)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Handedness:</strong> {displayValue(currentScreening.handedness)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>First generation learner:</strong> {displayValue(currentScreening.firstGenerationLearner)}</div>
            </div>
          </div>

          {/* Section 2: SDQ Assessment */}
          <div style={{ ...styles.card, padding: '24px', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '16px', borderBottom: `2px solid ${COLORS.mustard}`, paddingBottom: '8px', textAlign: 'center' }}>
              Section 2: SDQ Assessment
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Emotional Symptoms:</strong> {displayValue(currentScreening.sdqEmotional)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Conduct Problems:</strong> {displayValue(currentScreening.sdqConduct)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Hyperactivity:</strong> {displayValue(currentScreening.sdqHyperactivity)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Peer Problems:</strong> {displayValue(currentScreening.sdqPeer)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Prosocial Behavior:</strong> {displayValue(currentScreening.sdqProsocial)}</div>
            </div>
          </div>

          {/* Section 3: Writing Assessment */}
          <div style={{ ...styles.card, padding: '24px', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '16px', borderBottom: `2px solid ${COLORS.gold}`, paddingBottom: '8px', textAlign: 'center' }}>
              Section 3: Writing Assessment
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Total Words (10 mins):</strong> {displayValue(currentScreening.totalWords)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Legible Words:</strong> {displayValue(currentScreening.legibleWords)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Illegible Words:</strong> {displayValue(currentScreening.illegibleWords)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Alphabet Writing (letters/min):</strong> {displayValue(currentScreening.alphabetWriting)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Spelling errors (count):</strong> {displayValue(currentScreening.spellingErrors)}</div>
              {/* "Total: free writing spelling errors" maps to both spellingErrors and spellingErrorsDuringFreeWriting from backend */}
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Spelling errors during free-writing:</strong> {displayValue(currentScreening.spellingErrorsDuringFreeWriting || currentScreening.spellingErrors)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Writing Legibility:</strong> {displayWritingLegibility(currentScreening.writingLegibility)}</div>
            </div>
          </div>

          {/* Section 4: Single Word Spelling Assessment */}
          <div style={{ ...styles.card, padding: '24px', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '16px', borderBottom: `2px solid ${COLORS.orange}`, paddingBottom: '8px', textAlign: 'center' }}>
              Section 4: Single Word Spelling Assessment
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ gridColumn: '1 / -1', textAlign: 'left', padding: '8px 16px' }}>
                <strong>Single word spelling score:</strong>{' '}
                {displayValue(currentScreening.spellingTestScore)}
              </div>
            </div>
          </div>

          {/* {/* Section 5: Mathematics */}
          <div style={{ ...styles.card, padding: '24px', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '16px', borderBottom: `2px solid ${COLORS.mustard}`, paddingBottom: '8px', textAlign: 'center' }}>
              Section 5: Mathematics
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Addition Score:</strong> {displayValue(currentScreening.additionScore)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Subtraction Score:</strong> {displayValue(currentScreening.subtractionScore)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Multiplication Score:</strong> {displayValue(currentScreening.multiplicationScore)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Division Score:</strong> {displayValue(currentScreening.divisionScore)}</div>
            </div>
          </div>

          {/* Section 6: Neurodiversity Screening */}
          <div style={{ ...styles.card, padding: '24px', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '16px', borderBottom: `2px solid ${COLORS.gold}`, paddingBottom: '8px', textAlign: 'center' }}>
              Section 6: Neurodiversity Screening
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Dyslexia:</strong> {displayValue(currentScreening.dyslexiaScore)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Dyspraxia:</strong> {displayValue(currentScreening.dyspraxiaScore)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>ADHD:</strong> {displayValue(currentScreening.adhdScore)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>ASD:</strong> {displayValue(currentScreening.asdScore)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Dyscalculia:</strong> {displayValue(currentScreening.dyscalculiaScore)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>SLI:</strong> {displayValue(currentScreening.sliScore)}</div>
            </div>
          </div>

          {/* Section 7: Reading Assessment */}
          <div style={{ ...styles.card, padding: '24px', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '16px', borderBottom: `2px solid ${COLORS.orange}`, paddingBottom: '8px' }}>
              Section 7: Reading Assessment
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '12px' }}>
              {/* Level 1 */}
              <div style={{ border: `1px solid ${COLORS.borderGray}`, borderRadius: '12px', padding: '12px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: COLORS.navyBlue }}>Level 1</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', textAlign: 'left' }}>
                  <div><strong>Language:</strong> {displayValue(currentScreening.readingLanguage)}</div>
                  <div><strong>Time (sec):</strong> {displayValue(currentScreening.readingSpeed)}</div>
                  <div><strong>Total words:</strong> {displayValue(currentScreening.totalWordsRead)}</div>
                  <div><strong>Total errors:</strong> {displayValue(currentScreening.readingErrors)}</div>
                  <div><strong>Fluency:</strong> {displayValue(currentScreening.readingFluencyL1)}</div>
                  <div><strong>Comprehension (0-3):</strong> {displayValue(currentScreening.readingComprehensionL1)}</div>
                </div>
              </div>

              {/* Level 2 */}
              <div style={{ border: `1px solid ${COLORS.borderGray}`, borderRadius: '12px', padding: '12px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: COLORS.navyBlue }}>Level 2</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', textAlign: 'left' }}>
                  <div><strong>Language:</strong> {displayValue(currentScreening.l2ReadingLanguage)}</div>
                  <div><strong>Time (sec):</strong> {displayValue(currentScreening.l2ReadingSpeed)}</div>
                  <div><strong>Total words:</strong> {displayValue(currentScreening.l2TotalWordsRead)}</div>
                  <div><strong>Total errors:</strong> {displayValue(currentScreening.l2ReadingErrors)}</div>
                  <div><strong>Fluency:</strong> {displayValue(currentScreening.l2ReadingFluency)}</div>
                  <div><strong>Comprehension (0-3):</strong> {displayValue(currentScreening.l2ReadingComprehensionL2)}</div>
                </div>
              </div>

              {/* Level 3 */}
              <div style={{ border: `1px solid ${COLORS.borderGray}`, borderRadius: '12px', padding: '12px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: COLORS.navyBlue }}>Level 3</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', textAlign: 'left' }}>
                  <div><strong>Language:</strong> {displayValue(currentScreening.l3ReadingLanguage)}</div>
                  <div><strong>Time (sec):</strong> {displayValue(currentScreening.l3ReadingSpeed)}</div>
                  <div><strong>Total words:</strong> {displayValue(currentScreening.l3TotalWordsRead)}</div>
                  <div><strong>Total errors:</strong> {displayValue(currentScreening.l3ReadingErrors)}</div>
                  <div><strong>Fluency:</strong> {displayValue(currentScreening.l3ReadingFluency)}</div>
                  <div><strong>Comprehension (0-3):</strong> {displayValue(currentScreening.l3ReadingComprehensionL3)}</div>
                </div>
              </div>
            </div>
          </div>

{/* Section 8: Learning Styles */}
          <div style={{ ...styles.card, padding: '24px', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '16px', borderBottom: `2px solid ${COLORS.mustard}`, paddingBottom: '8px', textAlign: 'center' }}>
              Section 8: Learning Styles
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Visual Score:</strong> {displayValue(currentScreening.visualScore)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Auditory Score:</strong> {displayValue(currentScreening.auditoryScore)}</div>
              <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Kinesthetic Score:</strong> {displayValue(currentScreening.kinestheticScore)}</div>
            </div>
          </div>
                    </div>

{/* Section 10: Intervention Planning */}
          {(currentScreening.interventionStartDate || currentScreening.interventionEndDate) && (
            <div style={{ ...styles.card, padding: '24px', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.navyBlue, marginBottom: '16px', borderBottom: `2px solid ${COLORS.orange}`, paddingBottom: '8px', textAlign: 'center' }}>
                Section 10: Intervention Planning
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {currentScreening.interventionStartDate && <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>Start Date:</strong> {currentScreening.interventionStartDate}</div>}
                {currentScreening.interventionEndDate && <div style={{ textAlign: 'left', padding: '8px 16px' }}><strong>End Date:</strong> {currentScreening.interventionEndDate}</div>}
              </div>
            </div>
          )}

          <button onClick={() => setView('student-profile')} style={{ ...styles.button, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
            <ArrowLeft size={20} />
            Back to Student Profile
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default NeuroWiseScreening;
